import { Injectable } from "@angular/core";
import {
  Observable,
  Subject,
  tap,
  map,
  switchMap,
  of,
  take,
  throwError,
  catchError,
  forkJoin,
  from,
  concat,
  EMPTY,
  finalize,
  shareReplay,
} from "rxjs";
import { concatMap, toArray } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { DataService } from "./data.service";
import { EncounterClaimStatusSyncService } from "./encounter-claim-status-sync.service";
import { ConfirmationActivityNoteService } from "./confirmation-activity-note.service";
import { EngageReferral, EngageReferralLastActivity } from "./engage-activity.service";
import {
  AdmissionDisplayMode,
  AdmissionListPayload,
  Appointment,
  AppointmentCountResponse,
  DateRange,
  EncounterStatusCountsResponse,
  EncounterFollowUpsResponse,
  FollowUpInfoResponse,
  PaginatedAppointmentResponse,
  UniqueFilterDimensions,
  UpcomingEncountersResponse,
  WORKSHEET_REFERRAL_NOTIFICATION_FILTER_KEYS,
  WorksheetGridClientFilters,
  emptyWorksheetGridClientFilters,
  getAppointmentReferralId,
  ProcedureListTabId,
  ProcedureListFilterStatusId,
  ProcedureListSheetFilters,
  DEFAULT_PROCEDURE_LIST_SHEET_FILTERS,
  parseReferralsFullNameFiltersCsv,
  ProcedureListProcedurePayload,
  ProcedureListStatusCountsPayload,
  ProcedureListStatusCountsResponse,
  ScribeListClinicFilters,
  ScribeListClinicFacetField,
  ListClinicFacetResponse,
} from "../models/admissions.model";
import { EnumerationItem, SelectableEnumerationItem } from "../models/lookup.model";
import { getAdmissionsAuthStatusFilterOptions } from "../helpers/admissions-auth-status.util";
import {
  deriveAuthWorkflowStatusLabel,
  requiresAuthToAuthRequirementStatus,
  resolveEncounterRequiresAuthForListView,
} from "../helpers/encounter-auth-requirement.util";
import { getOverallPriorAuthStatus } from "../models/prior-auth.model";
import {
  getAdmissionsScheduleStatusFilterOptions,
  resolveAdmissionsScheduleStatusFromAppointment,
} from "../helpers/admissions-schedule-status.util";
import {
  getScribeListScheduleStatusFilterValues,
  isScribeScheduleStatusAllowed,
  resolveScribeScheduleStatusFromAppointment,
} from "../helpers/scribe-schedule-status.util";
import { resolveScribeScheduleResourceDisplay } from "../helpers/scribe-column-filter.util";
import { filterProcedureListDisplayRows } from "../helpers/procedure-list-display.util";
import { getWorksheetStatusRawFromAppointment } from "src/app/pages/worksheet/worksheet-patient-status-label.util";
import {
  normalizeEncounterPatientPhi,
  pickBetterEncounterPatientDisplayName,
  isUsableEncounterPatientDisplayName,
} from "../helpers/phi-encounter-patient.util";
import { CalendarFilter, EncounterStatus, Utils, ViewMode } from "./utils";
import { EncounterSchedule, Procedure } from "../models/procedure.model";
import { DateTime } from "luxon";
import { ApplicationContextService } from "./application-context.service";
import { DateRangeFilter, FilterService } from "./filter.service";
import { clearReferralsFiltersSession } from "../utils/referrals-filters-session.util";
import { ApplicationCacheService } from "./application-cache.service";
import { isValidUuid } from "../helpers/uuid.helper";
import {
  buildSubmitEncounterToBillingPatch,
  EncounterScheduleStatusSource,
  isEncounterScheduleSubmitted,
  resolveEncounterScheduleRowStatus,
  resolveEncounterScheduleStatus,
  resolveScheduleStatusAfterSubmit,
} from "../helpers/encounter-schedule-status.util";
import { isGaeProcedureJourneyLogEligible } from "../helpers/gae-procedure-journey-log.util";
import { EngageClinicEventType, EngageEventsService } from "./engage-events.service";
import {
  EngagePatientService,
  ReferralSearchResult,
  extractEngageReferralRowId,
} from "./engage-patient.service";
import {
  extractWorksheetChecklistOpenCountFromEncounter,
  patchAppointmentEngageCountsOnActivityDismiss,
  patchWorksheetEngageBadgeDismiss,
  type WorksheetEngageBadgeDismissChannel,
  worksheetRowEngageIconCountFromReferral,
  worksheetRowNotificationsChannelCountFromReferral,
  worksheetRowReminderIconCountFromReferral,
  worksheetRowUnreadMessageCountFromReferral,
} from "../helpers/worksheet-row-action-badges";
import { mergeWorksheetVisitTypeFacetRows, mapVisitTypeFacetFromApiLabel } from "src/app/pages/worksheet/worksheet-visit-type-facets.util";
import {
  WORKSHEET_DOB_BLANK_DIRECTORY_QUERY,
  WORKSHEET_DOS_BLANK_DIRECTORY_QUERY,
  WORKSHEET_SOURCE_BLANK_DIRECTORY_QUERY,
  isWorksheetDobBlankFilterValue,
  isWorksheetDosBlankFilterValue,
  isWorksheetReferralSourceBlankFilterValue,
  normalizeActiveReferralSourceFilterValues,
  rowMatchesReferralSourceFilter,
  worksheetBlankFacetDirectoryQueryValue,
} from "src/app/pages/worksheet/worksheet-snapshot-row-match.util";
import { WorksheetFilterSnapshot } from "src/app/pages/worksheet/worksheet-saved-views.model";
import { WORKSHEET_ALL_WORKFLOW_ID } from "src/app/pages/worksheet/worksheet-queue-api.service";
import type { WorksheetFacetFilterColumn } from "src/app/core/helpers/worksheet-facet-column-filter.util";
import { normalizeWorksheetDirectoryEligibility } from "src/app/core/helpers/worksheet-biller-columns.util";
import { EngageReferralsQueryService } from "./engage-referrals-query.service";
import {
  appendPatientDirectoryFiltersToUrl,
  extractPatientDirectoryFacetValues,
  mergeEngageReferralPageWithPatientDirectoryOverlay,
  parsePatientDirectoryOrLegacyReferralsPayload,
  parsePatientDirectoryGroupedByStagePayload,
  type EngagePipelineGroupedStageTotals,
  patientDirectoryAssignedToQueryValue,
  patientDirectoryEncounterStatusQueryValue,
  patientDirectoryLeadStatusQueryValue,
  patientDirectoryLocationQueryValue,
  patientDirectoryQueIdQueryValue,
  patientDirectoryReferralSourceQueryValue,
  patientDirectoryScheduleResourceQueryValue,
  patientDirectoryServiceQueryValue,
  patientDirectoryVisitTypeQueryValue,
  phiPatientDirectoryListGetUrl,
  type PatientDirectoryFilterValues,
} from "../utils/phi-patient-directory-list.util";
import {
  extractEngageActivityTimestampIso,
  sortAppointmentsByLatestActivityDesc,
} from "../helpers/engage-referral-activity.helper";
import { Patient } from "../models/patient.model";
import { pickEngageDisplayDob } from "../utils/dob-display.util";
import { SCHEDULE_SHEET_LIST_CLINIC_FILTER_STATUS } from "../helpers/schedule-available-slots.util";

/** Worklist toolbar tags after Dashboard tile → `/worklist` deep link. */
export interface WorklistDashboardFilterTag {
  id: "activity" | "dateRange" | "patientScope" | "prospectReferral";
  label: string;
}

/** Worklist row action → open Schedule `/referrals` with New Schedule sidebar. */
export interface ReferralsNewScheduleLaunchRequest {
  referralId: string;
  patientId: string;
}

/** Reuse list-clinic HTTP response when server-side filters unchanged (worksheet client-only filters may still change). */
interface WorklistListClinicResponseCache {
  key: string;
  response: PaginatedAppointmentResponse;
}

/** Cached worklist merge: list-clinic rows + PHI `GET …/patients/directory` referral pages. */
interface WorklistMergedRowsCache {
  sig: string;
  clinicAll: Appointment[];
  processedData: Appointment[];
  applyReferralClientFilter: boolean;
  activeRs: string[];
  clinicResponse: PaginatedAppointmentResponse;
  /** First response: plain `GET /referrals` with no query string (company-wide total + optional rows). */
  bootstrapReferrals: EngageReferral[];
  referralsByPage: Map<number, EngageReferral[]>;
  referralsApiTotal: number | null;
  referralsHasMore: boolean;
  combined: Appointment[];
}

/** Worklist merge: each `GET .../referrals` page size (server `limit`). */
const WORKLIST_REFERRALS_API_PAGE_LIMIT = 100;

/** Column “Filter by” sidebar: directory page size for facet lists (name, DOB, DOS, created, last activity). */
export const WORKLIST_NAME_FILTER_DIRECTORY_PAGE_LIMIT = 1000;
export const WORKLIST_COLUMN_FACET_DIRECTORY_PAGE_LIMIT =
  WORKLIST_NAME_FILTER_DIRECTORY_PAGE_LIMIT;

/** Service column “Filter by” sidebar: single directory page size. */
export const WORKLIST_SERVICE_FILTER_DIRECTORY_PAGE_LIMIT = 2000;

/** Optional `GET …/patients/directory` sort query params for column-filter pickers / applied filters. */
export interface WorklistDirectoryPageFetchOpts {
  /** `sort_name` on `GET …/patients/directory`. */
  sortName?: "ASC" | "DESC";
  /** `sort_dob` on `GET …/patients/directory`. */
  sortDob?: "ASC" | "DESC";
  /** `sort_dos` on `GET …/patients/directory`. */
  sortDos?: "ASC" | "DESC";
  /** `sort_created_at` on `GET …/patients/directory`. */
  sortCreatedAt?: "ASC" | "DESC";
  /** @deprecated Use {@link sortCreatedAt} */
  sortCreatedAtAsc?: boolean;
  /** Request `include_full_name=true` (name filter sidebar). */
  includeFullName?: boolean;
  /** Request `include_dos=true` (DOS filter sidebar). */
  includeDos?: boolean;
  /** Request `include_dob=true` (DOB filter sidebar). */
  includeDob?: boolean;
  /** Request `include_created=true` (Created filter sidebar). */
  includeCreated?: boolean;
  /** Request `include_last_activity=true` (Last Activity filter sidebar). */
  includeLastActivity?: boolean;
  /** Request `include_queue=true` (Queue filter sidebar). */
  includeQueue?: boolean;
  sortQueue?: "ASC" | "DESC";
  /** Stage Filter By: `GET …/patients/directory?include_stage=true`. */
  includeStage?: boolean;
  includeReferralSource?: boolean;
  sortReferralSource?: "ASC" | "DESC";
  /** Source Filter By: `GET …/patients/directory?include_source=true`. */
  includeSource?: boolean;
  includeLeadStatus?: boolean;
  sortLeadStatus?: "ASC" | "DESC";
  /** Patient Status Filter By: `GET …/patients/directory?include_patient_status=true`. */
  includePatientStatus?: boolean;
  /** Workflow Filter By: `GET …/patients/directory?include_workflow=true`. */
  includeWorkflow?: boolean;
  includeEncounterStatus?: boolean;
  sortEncounterStatus?: "ASC" | "DESC";
  includeVisitType?: boolean;
  sortVisitType?: "ASC" | "DESC";
  includeService?: boolean;
  /** Request `include_last_service=true` (Service filter sidebar). */
  includeLastService?: boolean;
  sortService?: "ASC" | "DESC";
  includeScheduleResource?: boolean;
  sortScheduleResource?: "ASC" | "DESC";
  includeLocation?: boolean;
  sortLocation?: "ASC" | "DESC";
  includeAssignedTo?: boolean;
  sortAssignedTo?: "ASC" | "DESC";
  includePrimaryPayerName?: boolean;
  includeEligibility?: boolean;
  includeAuthorizationStatus?: boolean;
  includeClaimStatus?: boolean;
  /** Override default page `limit` (e.g. 1000 for column facet sidebars). */
  pageLimit?: number;
  /** Pipeline: filter directory to a single workflow stage (`que_id`). */
  overrideQueId?: string;
  /** Pipeline: request `include_stage=true` grouped response (totals per stage). */
  includeStageGrouped?: boolean;
}

/** Optional overrides for {@link AdmissionsService.getAdmissionsList} (pipeline vs list). */
export interface GetAdmissionsListOptions {
  /** PHI directory `limit` for this request (pipeline board uses 2000; list stays at 100). */
  directoryPageLimit?: number;
  /** Force a fresh merge instead of returning {@link worklistMergedRowsCache}. */
  bypassMergedRowsCache?: boolean;
}

function worklistDirectoryFetchOptsRequiresFreshDirectoryPages(
  directoryOpts?: WorklistDirectoryPageFetchOpts
): boolean {
  return !!(
    directoryOpts?.sortName ||
    directoryOpts?.sortDob ||
    directoryOpts?.sortDos ||
    directoryOpts?.sortCreatedAt ||
    directoryOpts?.sortCreatedAtAsc ||
    directoryOpts?.includeFullName ||
    directoryOpts?.includeDos ||
    directoryOpts?.includeDob ||
    directoryOpts?.includeCreated ||
    directoryOpts?.includeLastActivity ||
    directoryOpts?.includeQueue ||
    directoryOpts?.sortQueue ||
    directoryOpts?.includeStage ||
    directoryOpts?.includeReferralSource ||
    directoryOpts?.sortReferralSource ||
    directoryOpts?.includeSource ||
    directoryOpts?.includeLeadStatus ||
    directoryOpts?.sortLeadStatus ||
    directoryOpts?.includePatientStatus ||
    directoryOpts?.includeWorkflow ||
    directoryOpts?.includeEncounterStatus ||
    directoryOpts?.sortEncounterStatus ||
    directoryOpts?.includeVisitType ||
    directoryOpts?.sortVisitType ||
    directoryOpts?.includeService ||
    directoryOpts?.includeLastService ||
    directoryOpts?.sortService ||
    directoryOpts?.includeScheduleResource ||
    directoryOpts?.sortScheduleResource ||
    directoryOpts?.includeLocation ||
    directoryOpts?.sortLocation ||
    directoryOpts?.includeAssignedTo ||
    directoryOpts?.sortAssignedTo ||
    directoryOpts?.includePrimaryPayerName ||
    directoryOpts?.includeEligibility ||
    directoryOpts?.includeAuthorizationStatus ||
    directoryOpts?.includeClaimStatus ||
    directoryOpts?.pageLimit
  );
}

function resolveWorklistDirectoryPageLimit(
  directoryOpts?: WorklistDirectoryPageFetchOpts
): number {
  const n = directoryOpts?.pageLimit;
  if (n != null && Number.isFinite(Number(n)) && Number(n) > 0) {
    return Math.floor(Number(n));
  }
  return WORKLIST_REFERRALS_API_PAGE_LIMIT;
}

@Injectable({
  providedIn: "root",
})
export class AdmissionsService {
  selectedDate: DateRange;
  /** Day view date to restore when switching back from week/month (start of week must not become the day). */
  calendarDayFocusDate: Date | null = null;
  /** Schedule calendar: list-clinic fetch in progress (day/week/month). */
  scheduleCalendarListLoading = false;
  /** Schedule calendar: FullCalendar events not yet applied for the current payload. */
  scheduleCalendarEventsApplying = false;
  /** Fired when {@link scheduleCalendarListLoading} or {@link scheduleCalendarEventsApplying} changes. */
  scheduleCalendarToolbarBusyChanged$ = new Subject<void>();
  displayMode: AdmissionDisplayMode;
  physicianFilter: SelectableEnumerationItem[] = [];
  resourcesFilter: SelectableEnumerationItem[] = [];
  encounerStatusList: SelectableEnumerationItem[] = [];
  authStatusFilterList: SelectableEnumerationItem[] = [];
  /** Admission list left panel: all, confirmed, or unconfirmed (`encounter.isConfirmed`). */
  confirmationStatusFilter: "all" | "confirmed" | "unconfirmed" = "all";
  locationFilter: SelectableEnumerationItem[] = [];
  visitTypeFilter: SelectableEnumerationItem[] = [];
  /** Worklist Service column / filters sheet — PHI directory `service` (account service id). */
  serviceFilter: SelectableEnumerationItem[] = [];
  searchQuery: string;
  /** Active status tab on `/procedure-list` (drives DOS range + encounter status filter). */
  procedureListActiveTabId: ProcedureListTabId = "today";
  /** Committed filters from the procedure list filter drawer (Provider / Status / Date). */
  procedureListSheetFiltersCommitted = false;
  procedureListSheetFilters: ProcedureListSheetFilters = {
    ...DEFAULT_PROCEDURE_LIST_SHEET_FILTERS,
    serviceIds: [],
    referralSourceIds: [],
  };
  /**
   * Worklist toolbar: referrals chosen from patient-directory-backed search typeahead (grid updates on select;
   * multiple picks accumulate). Cleared to restore the default worklist.
   */
  worklistToolbarEngageSelections: EngageReferral[] = [];
  /**
   * Worklist → prospect / schedule: keep grid filter state while the detail route is open
   * (`WorksheetComponent` is destroyed during navigation).
   */
  worklistNavigatingToPatientDetail = false;
  /** Worklist row → `/engage/prospect` seed (queue / workflow) until referral-details hydrates. */
  worklistProspectReferralSeed: EngageReferral | null = null;
  /** Pending worklist row → `/referrals?newSchedule=1` sidebar launch (consumed by AdmissionsComponent). */
  private referralsNewScheduleLaunchRequest: ReferralsNewScheduleLaunchRequest | null = null;
  /** Saved view rail key to restore when returning from patient detail to `/engage`. */
  worklistToolbarWorklistKeyBeforeDetail: "all" | string | null = null;
  /**
   * When set, list-clinic requests include this single referral source (dashboard scoped instance).
   * Schedule page uses `referralSourceFilterList` multi-select instead.
   */
  referralSourceFilter: string | null = null;
  /** Multi-select referral sources for /referrals (synced from AdmissionsComponent). */
  referralSourceFilterList: SelectableEnumerationItem[] = [];
  /** Worklist grid: client-side text/date filters (Name uses searchQuery on apply). */
  worksheetGridText: WorksheetGridClientFilters = emptyWorksheetGridClientFilters();
  /** Engage toolbar pipeline picker: {@link WORKSHEET_ALL_WORKFLOW_ID} = every pipeline. */
  worksheetSelectedPipelineId: string | null = WORKSHEET_ALL_WORKFLOW_ID;
  /** Stage row ids (`que_id`) for {@link worksheetSelectedPipelineId}; empty when all pipelines. */
  worksheetSelectedPipelineStageIds: string[] = [];
  /** Workflow `stageId` tokens in `queueContains` → display name for filter tags. */
  private readonly worksheetStageLabelByStageId = new Map<string, string>();
  /** Worklist Name column: `sort_name` on patients/directory (`ASC` | `DESC`). */
  worksheetNameDirectorySort: "ASC" | "DESC" | null = null;
  /** Worklist DOB column: `sort_dob` on patients/directory. */
  worksheetDobDirectorySort: "ASC" | "DESC" | null = null;
  /** Worklist DOS column: `sort_dos` on patients/directory. */
  worksheetDosDirectorySort: "ASC" | "DESC" | null = null;
  /** Worklist Created column: `sort_created_at` on patients/directory. */
  worksheetCreatedDirectorySort: "ASC" | "DESC" | null = null;
  worksheetQueueDirectorySort: "ASC" | "DESC" | null = null;
  worksheetReferralSourceDirectorySort: "ASC" | "DESC" | null = null;
  worksheetPatientStatusDirectorySort: "ASC" | "DESC" | null = null;
  worksheetEncounterStatusDirectorySort: "ASC" | "DESC" | null = null;
  worksheetVisitTypeDirectorySort: "ASC" | "DESC" | null = null;
  worksheetServiceDirectorySort: "ASC" | "DESC" | null = null;
  worksheetResourceDirectorySort: "ASC" | "DESC" | null = null;
  worksheetLocationDirectorySort: "ASC" | "DESC" | null = null;
  worksheetCoordinatorDirectorySort: "ASC" | "DESC" | null = null;
  worksheetCreatedOnIso: string | null = null;
  /** Inclusive end calendar day (`yyyy-MM-dd`) for Created filter; null means same day as {@link worksheetCreatedOnIso}. */
  worksheetCreatedOnEndIso: string | null = null;
  worksheetUpdatedOnIso: string | null = null;
  /** Worklist filter sheet: Activity date range start (`activity_date_start`), yyyy-MM-dd. */
  worksheetActivityDateStartIso: string | null = null;
  /** Inclusive Activity date range end (`activity_date_end`); null when same day as start. */
  worksheetActivityDateEndIso: string | null = null;
  /**
   * Worklist filter sheet: when false, list-clinic uses {@link getWorksheetFacetCountsDateRangeIso} for
   * `filterDate` until the user applies a committed DOS range from the sheet.
   */
  worksheetUserCommittedDosRange = false;
  /**
   * Worklist page only: after list-clinic loads, merge Engage referrals that are not already
   * represented on the schedule (same referral id), so prospects without DOS appear in the grid.
   */
  worklistMergeEngageProspects = false;
  /**
   * With {@link worklistMergeEngageProspects}: merge step uses filtered `GET .../referrals`
   * (same params as Engage) when set from dashboard notification deep link.
   */
  worklistMergeEngageFromReferralsApi = false;
  /**
   * Engage worklist grid: show PHI directory rows immediately; {@link enrichReferralMappedRowsWithLatestPhi$}
   * fetches `latest-by-patient-ids` in the background (payer / encounter id are not blocking columns).
   */
  worklistNonBlockingLatestPhiEnrichment = false;
  /** Emits after background latest-encounter enrichment patches live worklist rows. */
  worklistLatestPhiEnrichmentApplied$ = new Subject<void>();
  /** Dashboard Notifications → worklist: removable tags shown after the Filters button. */
  worklistDashboardFilterTags: WorklistDashboardFilterTag[] = [];
  worklistDashboardFilterTagsChanged$ = new Subject<void>();
  /**
   * Worklist (/worklist): list-clinic + Engage referrals are loaded per UI page (see {@link getAdmissionsList});
   * the grid must not slice again by currentPage (list-view uses this flag).
   */
  worklistServerPagedRows = false;
  /**
   * Worklist: header “select all” applies to every row matching current filters (all server pages), not only
   * {@link admissionList}. Cleared when filters/cache reset or when the user changes row selection.
   */
  worklistSelectEntireFilterMatch = false;
  DayWeekMonthViewChanged$: Subject<void> = new Subject();
  searchQueryChanged$: Subject<string> = new Subject();

  get scheduleCalendarDayWeekMonthDisabled(): boolean {
    return (
      this.scheduleCalendarListLoading || this.scheduleCalendarEventsApplying
    );
  }

  setScheduleCalendarListLoading(loading: boolean): void {
    if (this.scheduleCalendarListLoading === loading) {
      return;
    }
    this.scheduleCalendarListLoading = loading;
    this.scheduleCalendarToolbarBusyChanged$.next();
  }

  setScheduleCalendarEventsApplying(applying: boolean): void {
    if (this.scheduleCalendarEventsApplying === applying) {
      return;
    }
    this.scheduleCalendarEventsApplying = applying;
    this.scheduleCalendarToolbarBusyChanged$.next();
  }
  /** Emitted when worksheet pipeline selection is restored from persisted filters. */
  worksheetPipelineSelectionRestored$ = new Subject<void>();
  /** Toolbar workflow dropdown applied (user pick) — Engage list/pipeline should show reload skeletons. */
  worksheetToolbarPipelineReload$ = new Subject<void>();
  /** Emitted when workflow stage id → name labels are registered (toolbar filter tags). */
  worksheetStageLabelsChanged$ = new Subject<void>();
  providerSelectionChanged$: Subject<void> = new Subject();
  resourceSelectionChanged$: Subject<void> = new Subject();
  admissionList: Appointment[];
  /** Which screen last wrote {@link admissionList} — prevents schedule from reusing procedure rows. */
  admissionListOrigin: "schedule" | "procedure" | null = null;

  // Pagination properties
  currentPage: number = 1;
  pageSize: number = 25;
  totalRecords: number = 0;
  totalPages: number = 0;
  /**
   * Worklist: list-clinic snapshot + cached GET `/referrals` pages; merged rows reused across UI pages
   * until filters/search change.
   */
  private worklistMergedRowsCache: WorklistMergedRowsCache | null = null;
  /**
   * While the pipeline board loads directory rows, restore list grid + merge cache afterward so
   * toggling list ↔ pipeline does not refetch the full worklist.
   */
  private engageWorklistPipelineIsolation: {
    admissionList: Appointment[];
    mergedCache: WorklistMergedRowsCache | null;
    currentPage: number;
    totalRecords: number;
    totalPages: number;
    pageSize: number;
  } | null = null;
  /** Last successful `list-clinic` response for worklist merge (keyed by server filter fingerprint). */
  private worklistListClinicResponseCache: WorklistListClinicResponseCache | null = null;
  /** Session cache: Engage/detail `POST …/encounters/list-clinic` filtered to one patient id. */
  private engageListClinicByPatientId = new Map<string, Appointment[]>();
  private engageListClinicInflight = new Map<string, Observable<Appointment[]>>();
  /** Session cache: plain paginated PHI patient directory pages (key includes referral_source / lead_status / que_id). */
  private worklistPlainReferralPageCache = new Map<
    string,
    { referrals: EngageReferral[]; total: number | null }
  >();
  /** Session cache: Engage-query referral pages when merging from filtered referrals API. */
  private worklistEngageReferralPageCache = new Map<
    number,
    { referrals: EngageReferral[]; total: number | null }
  >();
  /** Session cache: `POST …/encounters/latest-by-patient-ids` by patient id (worklist scroll dedupe). */
  private worklistLatestEncounterByPatientId = new Map<
    string,
    { patientId: string; encounterId: string; payerSummary: string }
  >();
  /** Invalidates in-flight background latest-encounter enrichment when filters/cache reset. */
  private worklistLatestPhiBackgroundSeq = 0;
  /** One-shot: after worklist “New Patient”, pin that row to the top of the next merged list. */
  private worklistBoostPatientId: string | null = null;
  private worklistBoostActivityIso: string | null = null;

  constructor(
    private readonly _dataService: DataService,
    private readonly _appContextService: ApplicationContextService,
    private readonly _engageEventsService: EngageEventsService,
    private readonly _applicationCacheService: ApplicationCacheService,
    private readonly _engagePatientService: EngagePatientService,
    private readonly _engageReferralsQuery: EngageReferralsQueryService,
    private readonly _filterService: FilterService,
    private readonly _encounterClaimStatusSyncService: EncounterClaimStatusSyncService,
    private readonly _confirmationActivityNoteService: ConfirmationActivityNoteService
  ) {
    this.displayMode = {
      view: ViewMode.List,
      filter: CalendarFilter.Day,
    };
    this.selectedDate = {
      startDate: DateTime.now().startOf("day").toJSDate(),
      endDate: DateTime.now().endOf("day").toJSDate(),
    };
    this.encounerStatusList = getAdmissionsScheduleStatusFilterOptions();
    this.authStatusFilterList = getAdmissionsAuthStatusFilterOptions();
    // Don't initialize visitTypeFilter here - let component handle it from API
  }

  notifyWorklistDashboardFilterTagsChanged(): void {
    this.worklistDashboardFilterTagsChanged$.next();
  }

  /** Clears dashboard deep-link notification filters and toolbar tags (worklist). */
  clearWorklistDashboardDeepLinkFilters(): void {
    this.worklistDashboardFilterTags = [];
    const gt = {
      ...emptyWorksheetGridClientFilters(),
      ...(this.worksheetGridText ?? emptyWorksheetGridClientFilters()),
    };
    gt.referralNotificationFilters = [];
    this.worksheetGridText = gt;
    this.worksheetCreatedOnIso = null;
    this.worksheetCreatedOnEndIso = null;
    this.worksheetActivityDateStartIso = null;
    this.worksheetActivityDateEndIso = null;
    this._filterService.setWorklistEngagePatientIdFilter(null);
    this.notifyWorklistDashboardFilterTagsChanged();
  }

  /** Reset all referrals filters to default (e.g. when opening Schedule from sidebar). */
  resetReferralsFilters(): void {
    clearReferralsFiltersSession(
      this._appContextService.contextSnapshot?.currentlyLoggedInUser
    );
    this.selectedDate = {
      startDate: DateTime.now().startOf("day").toJSDate(),
      endDate: DateTime.now().endOf("day").toJSDate(),
    };
    this.encounerStatusList = getAdmissionsScheduleStatusFilterOptions();
    this.authStatusFilterList = getAdmissionsAuthStatusFilterOptions();
    this.physicianFilter.forEach((p) => (p.isSelected = false));
    this.locationFilter.forEach((l) => (l.isSelected = false));
    this.resourcesFilter.forEach((r) => (r.isSelected = false));
    this.visitTypeFilter.forEach((v) => (v.isSelected = false));
    this.serviceFilter.forEach((v) => (v.isSelected = false));
    this.searchQuery = "";
    this.worklistToolbarEngageSelections = [];
    this.referralSourceFilter = null;
    this.referralSourceFilterList.forEach((x) => (x.isSelected = false));
    this.worksheetGridText = emptyWorksheetGridClientFilters();
    this.worksheetSelectedPipelineId = WORKSHEET_ALL_WORKFLOW_ID;
    this.worksheetSelectedPipelineStageIds = [];
    this.worksheetNameDirectorySort = null;
    this.worksheetDobDirectorySort = null;
    this.worksheetDosDirectorySort = null;
    this.worksheetCreatedDirectorySort = null;
    this.worksheetQueueDirectorySort = null;
    this.worksheetReferralSourceDirectorySort = null;
    this.worksheetPatientStatusDirectorySort = null;
    this.worksheetEncounterStatusDirectorySort = null;
    this.worksheetVisitTypeDirectorySort = null;
    this.worksheetServiceDirectorySort = null;
    this.worksheetResourceDirectorySort = null;
    this.worksheetLocationDirectorySort = null;
    this.worksheetCoordinatorDirectorySort = null;
    this.worksheetCreatedOnIso = null;
    this.worksheetCreatedOnEndIso = null;
    this.worksheetUpdatedOnIso = null;
    this.worksheetActivityDateStartIso = null;
    this.worksheetActivityDateEndIso = null;
    this.worksheetUserCommittedDosRange = false;
    this.worklistDashboardFilterTags = [];
    this.currentPage = 1;
    this.worklistServerPagedRows = false;
    this.clearWorklistSelectEntireFilterMatch();
    this.clearWorklistCachesIncludingClinic();
  }

  /**
   * Schedule (`/referrals`) auto-selects enc. status tags; worklist (`/engage`) must not send
   * `encounter_status` / `sort_encounter_status` on directory GET after navigating from Schedule.
   */
  clearScheduleEncounterStatusCarryover(): void {
    if (this.encounerStatusList?.length) {
      for (const item of this.encounerStatusList) {
        item.isSelected = false;
      }
    }
    this.worksheetEncounterStatusDirectorySort = null;
  }

  /** Drop merged worklist rows only (safe when worksheet client filters change; list-clinic may still be reused). */
  clearWorklistPageResultCache(): void {
    this.clearWorklistSelectEntireFilterMatch();
    this.worklistMergedRowsCache = null;
    this.worklistPlainReferralPageCache.clear();
    this.worklistEngageReferralPageCache.clear();
    this.worklistLatestEncounterByPatientId.clear();
    this.worklistLatestPhiBackgroundSeq++;
  }

  /** Snapshot list grid state before a pipeline-only directory fetch. */
  beginEngagePipelineDirectoryFetchIsolation(): void {
    this.engageWorklistPipelineIsolation = {
      admissionList: [...(this.admissionList ?? [])],
      mergedCache: this.worklistMergedRowsCache,
      currentPage: this.currentPage,
      totalRecords: this.totalRecords,
      totalPages: this.totalPages,
      pageSize: this.pageSize,
    };
  }

  /** Restore list grid after pipeline directory fetch completes. */
  endEngagePipelineDirectoryFetchIsolation(): void {
    const snap = this.engageWorklistPipelineIsolation;
    if (!snap) {
      return;
    }
    this.admissionList = [...snap.admissionList];
    this.worklistMergedRowsCache = snap.mergedCache;
    this.currentPage = snap.currentPage;
    this.totalRecords = snap.totalRecords;
    this.totalPages = snap.totalPages;
    this.pageSize = snap.pageSize;
    this.engageWorklistPipelineIsolation = null;
  }

  engageWorklistGridReadyForListView(): boolean {
    return (this.admissionList?.length ?? 0) > 0;
  }

  clearWorklistSelectEntireFilterMatch(): void {
    this.worklistSelectEntireFilterMatch = false;
  }

  /**
   * After worklist “+” save: next {@link getAdmissionsList} merge moves this patient to row 1 with a fresh
   * `last_activity` (directory may lag behind `form_filled`).
   */
  rememberWorklistPatientActivityBoost(
    patientId: string,
    activityIso?: string
  ): void {
    const pid = (patientId || "").trim();
    if (!pid) {
      return;
    }
    this.worklistBoostPatientId = pid;
    this.worklistBoostActivityIso = (activityIso || new Date().toISOString()).trim();
  }

  /**
   * Loads every worklist page for the current filter/search (sequential), then restores {@link currentPage}.
   * Used when the user chose “select all” across server pages before Engage bulk coordinator/queue apply.
   */
  fetchAllWorklistPagesForBulkSelection$(searchQuery: string): Observable<Appointment[]> {
    if (!this.worklistServerPagedRows || !this.worklistMergeEngageProspects) {
      return of([...(this.admissionList ?? [])]);
    }
    const ps = this.pageSize;
    const total = Math.max(0, this.totalRecords);
    const pageCount = Math.max(1, Math.ceil(total / ps));
    const q = searchQuery ?? this.searchQuery ?? "";
    const restorePage = Math.max(1, this.currentPage);
    const pages = Array.from({ length: pageCount }, (_, i) => i + 1);
    const collected: Appointment[] = [];
    return from(pages).pipe(
      concatMap((p) =>
        this.getAdmissionsList(p, ps, q).pipe(
          tap((res) => {
            for (const row of res.data ?? []) {
              collected.push(row);
            }
          })
        )
      ),
      toArray(),
      switchMap(() =>
        this.getAdmissionsList(restorePage, ps, q).pipe(
          map(() => {
            const cap = total > 0 ? Math.min(collected.length, total) : collected.length;
            return collected.slice(0, cap);
          })
        )
      )
    );
  }

  /** Clears merged rows + last `list-clinic` HTTP response (company/context change or leaving worklist). */
  clearWorklistCachesIncludingClinic(): void {
    this.worklistMergedRowsCache = null;
    this.worklistListClinicResponseCache = null;
    this.worklistPlainReferralPageCache.clear();
    this.worklistEngageReferralPageCache.clear();
    this.worklistLatestEncounterByPatientId.clear();
  }

  setReferralsNewScheduleLaunchRequest(req: ReferralsNewScheduleLaunchRequest | null): void {
    this.referralsNewScheduleLaunchRequest = req;
  }

  consumeReferralsNewScheduleLaunchRequest(): ReferralsNewScheduleLaunchRequest | null {
    const req = this.referralsNewScheduleLaunchRequest;
    this.referralsNewScheduleLaunchRequest = null;
    return req;
  }

  clearWorklistToolbarEngageSelections(): void {
    this.worklistToolbarEngageSelections = [];
    this.invalidateWorklistToolbarEngageSelectionCaches();
  }

  removeWorklistToolbarEngageSelectionByReferralId(referralId: string): void {
    const id = String(referralId ?? "").trim();
    if (!id) {
      return;
    }
    const next = this.worklistToolbarEngageSelections.filter(
      (r) => String(r?.id ?? "").trim() !== id
    );
    if (next.length === this.worklistToolbarEngageSelections.length) {
      return;
    }
    this.worklistToolbarEngageSelections = next;
    this.invalidateWorklistToolbarEngageSelectionCaches();
  }

  /** Toolbar patient search: dismiss filter tag for one clinic patient (all encounter rows). */
  removeWorklistToolbarEngageSelectionsByPatientId(patientId: string): void {
    const pid = String(patientId ?? "").trim().toLowerCase();
    if (!pid) {
      return;
    }
    const next = this.worklistToolbarEngageSelections.filter((r) => {
      const rowPid = this.extractPatientIdFromEngageReferral(r).toLowerCase();
      const rowId = String(r?.id ?? "").trim().toLowerCase();
      return rowPid !== pid && rowId !== pid;
    });
    if (next.length === this.worklistToolbarEngageSelections.length) {
      return;
    }
    this.worklistToolbarEngageSelections = next;
    this.invalidateWorklistToolbarEngageSelectionCaches();
  }

  private invalidateWorklistToolbarEngageSelectionCaches(): void {
    this.worklistMergedRowsCache = null;
    this.worklistPlainReferralPageCache.clear();
    this.worklistEngageReferralPageCache.clear();
    this.worklistLatestEncounterByPatientId.clear();
  }

  addWorklistToolbarEngageSelectionFromSearchResult(hit: ReferralSearchResult): void {
    if (!hit?.id) {
      return;
    }
    const mapped = this.referralSearchHitsToEngageReferrals([hit])[0];
    const id = String(mapped?.id ?? "").trim();
    if (!id) {
      return;
    }
    const exists = this.worklistToolbarEngageSelections.some(
      (r) => String(r?.id ?? "").trim() === id
    );
    if (!exists) {
      this.worklistToolbarEngageSelections = [mapped, ...this.worklistToolbarEngageSelections];
    }
    this.invalidateWorklistToolbarEngageSelectionCaches();
  }

  /**
   * Preferred for toolbar selections: load the referral via PHI patient directory so the same projection as
   * the main directory list is used (`GET …/patients/directory` with `patientId=…`).
   */
  /**
   * Worklist grid name click — same PHI directory lookup as toolbar Engage search select
   * (`GET …/patients/directory?page=1&limit=30&patientId=…`).
   */
  addWorklistToolbarEngageSelectionFromAppointment$(
    appt: Appointment
  ): Observable<void> {
    const hit = this.appointmentToReferralSearchResult(appt);
    if (!hit) {
      return of(undefined);
    }
    return this.addWorklistToolbarEngageSelectionFromSearchResult$(hit);
  }

  private appointmentToReferralSearchResult(
    appt: Appointment
  ): ReferralSearchResult | null {
    const rid = getAppointmentReferralId(appt).trim();
    const patientId = (appt.patientId ?? appt.encountersPatient?.id ?? "")
      .toString()
      .trim();
    if (!rid && !patientId) {
      return null;
    }
    const name = (appt.encountersPatient?.name ?? "").trim();
    const parts = name.split(/\s+/).filter(Boolean);
    const phone = String(
      (appt.encountersPatient as { phone?: string } | undefined)?.phone ?? ""
    ).trim();
    return {
      id: rid || patientId,
      ...(patientId ? { patientId } : {}),
      firstName: parts[0] ?? "",
      lastName: parts.slice(1).join(" "),
      phone,
      email: "",
      createdAt: "",
    };
  }

  addWorklistToolbarEngageSelectionFromSearchResult$(
    hit: ReferralSearchResult
  ): Observable<void> {
    const rid = String(hit?.id ?? "").trim();
    const patientId = String(hit?.patientId ?? rid).trim();
    if (!patientId && !rid) {
      return of(undefined);
    }
    return this._engagePatientService.getReferralsFirstPageForPatientId(patientId).pipe(
      catchError(() => of([] as EngageReferral[])),
      switchMap((rows) => {
        if (rows?.length) {
          return of(rows);
        }
        if (rid && rid !== patientId) {
          return this._engagePatientService.getReferralsFirstPageWithAssignedToFilter(null, rid).pipe(
            catchError(() => of(null)),
            map((res) =>
              res ? this.parseDirectoryResponseRowsForToolbarSelection(res) : []
            )
          );
        }
        return of(this.referralSearchHitsToEngageReferrals([hit]) as EngageReferral[]);
      }),
      tap((rows) => {
        this.mergeWorklistToolbarEngageSelections(rows ?? []);
      }),
      map(() => undefined)
    );
  }

  private mergeWorklistToolbarEngageSelections(rows: EngageReferral[]): void {
    const existingIds = new Set(
      this.worklistToolbarEngageSelections
        .map((r) => String(r?.id ?? "").trim())
        .filter(Boolean)
    );
    const toAdd: EngageReferral[] = [];
    for (const row of rows ?? []) {
      const id = String(row?.id ?? "").trim();
      if (!id || existingIds.has(id)) {
        continue;
      }
      existingIds.add(id);
      toAdd.push(row);
    }
    if (!toAdd.length) {
      return;
    }
    this.worklistToolbarEngageSelections = [...toAdd, ...this.worklistToolbarEngageSelections];
    this.invalidateWorklistToolbarEngageSelectionCaches();
  }

  /** Same extraction/coercion as worklist directory pages — not {@link parseReferralsPayloadFromUnknown}. */
  private parseDirectoryResponseRowsForToolbarSelection(res: unknown): EngageReferral[] {
    return parsePatientDirectoryOrLegacyReferralsPayload(res, 1, 30).referrals ?? [];
  }

  getWorklistToolbarEngageSelectionsCacheKey(): string {
    return [...this.worklistToolbarEngageSelections]
      .map((r) => String(r?.id ?? "").trim())
      .filter(Boolean)
      .sort()
      .join(",");
  }

  /**
   * Fingerprint of everything that changes the `list-clinic` POST body (excludes worksheet-only client filters).
   * Used to reuse the last list-clinic response and skip redundant network calls.
   */
  private worklistListClinicRequestCacheKey(pc: AdmissionListPayload): string {
    const fd = pc.filterDate;
    const fdStr =
      fd?.startDate != null && fd?.endDate != null
        ? `${String(fd.startDate)}\u001f${String(fd.endDate)}`
        : "";
    const sort = (a: string[] | undefined) => [...(a ?? [])].map(String).sort().join(",");
    return [
      String(pc.searchQuery ?? "").trim(),
      sort(pc.providers),
      sort(pc.resources),
      sort(pc.filterStatus),
      sort(pc.authStatus),
      pc.isConfirmed === true ? "1" : pc.isConfirmed === false ? "0" : "",
      sort(pc.filterLocation),
      sort(pc.visitType),
      sort(pc.referralSources),
      String(pc.pageStart ?? ""),
      String(pc.pageLength ?? ""),
      String(pc.sortField ?? ""),
      String(pc.sortDirection ?? ""),
      fdStr,
      this.worklistMergeEngageFromReferralsApi ? "1" : "0",
    ].join("\u001f");
  }

  /** Best-effort encounter DOS from list-clinic / schedule payloads that use alternate property names. */
  private pickEncounterDateTimeFromRow(enc: Appointment): string | undefined {
    const top = (enc.encounterDateTime ?? "").toString().trim();
    if (top) {
      return top;
    }
    const o = enc as unknown as Record<string, unknown>;
    const keys = [
      "encounter_date_time",
      "encounterDate",
      "visitDateTime",
      "visit_date_time",
      "scheduledDateTime",
      "appointmentDateTime",
      "appointment_date_time",
      "dos",
      "dateOfService",
      "date_of_service",
      "serviceDate",
    ];
    for (const k of keys) {
      const v = o[k];
      if (v != null && String(v).trim() !== "") {
        return String(v).trim();
      }
    }
    const sched = enc.encounterSchedule as unknown as Record<string, unknown> | undefined;
    if (sched && typeof sched === "object") {
      const sk = [
        "startDateTime",
        "start_date_time",
        "start",
        "dateTime",
        "checkInDate",
        "appointmentDate",
      ];
      for (const k of sk) {
        const v = sched[k];
        if (v != null && String(v).trim() !== "") {
          return String(v).trim();
        }
      }
    }
    return undefined;
  }

  /** Recent DOS from list-clinic / directory payloads (API `last_dos`). */
  private pickLastDosDateTimeFromRow(enc: Appointment): string | undefined {
    const o = enc as unknown as Record<string, unknown>;
    const raw = this.directoryStringField(o, [
      "last_dos",
      "lastDos",
      "lastDosDateTime",
      "last_dos_date_time",
      "lastDosDate",
      "last_dos_date",
      "lastEncounterDateTime",
      "last_encounter_date_time",
      "lastEncounterDate",
      "last_encounter_date",
      "lastEncounterDos",
      "last_encounter_dos",
      "previousEncounterDateTime",
      "previous_encounter_date_time",
      "previousDos",
      "previous_dos",
    ]);
    return raw ? this.normalizeDirectoryDateTimeToEncounterIso(raw) : undefined;
  }

  /** Worklist Services column (API `last_service`). */
  private pickLastServiceFromRow(enc: Appointment | Record<string, unknown>): string | undefined {
    const o = enc as Record<string, unknown>;
    const raw = this.directoryStringField(o, [
      "last_service",
      "lastService",
    ]);
    return raw || undefined;
  }

  /** Worklist Encounter Status column (API `encounterStatus`). */
  private pickLastEncounterStatusFromRow(
    enc: Appointment | Record<string, unknown>
  ): string | undefined {
    const o = enc as Record<string, unknown>;
    const raw = this.directoryStringField(o, [
      "encounterStatus",
      "last_encounter_status",
      "lastEncounterStatus",
      "lastEncounterStatusName",
      "last_encounter_status_name",
    ]);
    return raw || undefined;
  }

  /** Worklist Coordinator column (API `assigned_to`). */
  private pickAssignedToFromRow(
    enc: Appointment | Record<string, unknown>
  ): string | undefined {
    const ref = enc as EngageReferral;
    const fromEngage = this.extractEngageReferralAssignedToId(ref);
    if (fromEngage) {
      return fromEngage;
    }
    const o = enc as Record<string, unknown>;
    const raw = this.directoryStringField(o, [
      "assigned_to",
      "assignedTo",
      "assign_to",
      "assignTo",
    ]);
    return raw || undefined;
  }

  private getWorklistListClinicPost$(url: string, pageConfig: AdmissionListPayload): Observable<PaginatedAppointmentResponse> {
    if (
      this.worklistMergeEngageProspects &&
      this.worklistListClinicResponseCache &&
      this.worklistListClinicResponseCache.key === this.worklistListClinicRequestCacheKey(pageConfig)
    ) {
      return of(this.worklistListClinicResponseCache.response);
    }
    return this._dataService.post(url, pageConfig).pipe(
      tap((response) => {
        if (this.worklistMergeEngageProspects) {
          this.worklistListClinicResponseCache = {
            key: this.worklistListClinicRequestCacheKey(pageConfig),
            response,
          };
        }
      })
    );
  }

  /**
   * Full merged clinic + Engage rows when the merge cache matches current filters — used by worklist grid
   * for client-side pagination against worksheet filters.
   */
  getWorklistMergedCombinedIfCached(): Appointment[] | null {
    if (!this.worklistMergeEngageProspects) {
      return null;
    }
    const hit = this.worklistMergedRowsCache;
    if (!hit) {
      return null;
    }
    const q = (this.searchQuery ?? "").trim();
    const sig = this.worklistPageCacheSignature(this.pageSize, q, this.currentPage);
    return hit.sig === sig ? hit.combined : null;
  }

  /** Decrement worklist Engage icon badge on merged-cache + current grid rows after activity dismiss. */
  patchWorklistEngageBadgeDismissInCaches(payload: {
    referralId?: string;
    patientId?: string;
    activityType?: string;
  }): boolean {
    const rid = (payload.referralId ?? "").trim().toLowerCase();
    const pid = (payload.patientId ?? "").trim().toLowerCase();
    if (!rid && !pid) {
      return false;
    }

    const rowMatches = (appt: Appointment): boolean => {
      const apptRid = getAppointmentReferralId(appt).trim().toLowerCase();
      const apptPid = (appt.patientId ?? appt.encountersPatient?.id ?? "")
        .toString()
        .trim()
        .toLowerCase();
      return (rid && apptRid === rid) || (pid && apptPid === pid);
    };

    const patchedRows = new Set<Appointment>();
    let changed = false;
    const lists: Appointment[][] = [];
    const seenLists = new Set<Appointment[]>();
    const queueList = (list: Appointment[] | null | undefined): void => {
      if (list?.length && !seenLists.has(list)) {
        seenLists.add(list);
        lists.push(list);
      }
    };
    queueList(this.admissionList);
    queueList(this.worklistMergedRowsCache?.combined ?? null);

    for (const list of lists) {
      for (const row of list) {
        if (!rowMatches(row) || patchedRows.has(row)) {
          continue;
        }
        if (patchAppointmentEngageCountsOnActivityDismiss(row, payload.activityType)) {
          patchedRows.add(row);
          changed = true;
        }
      }
    }

    if (changed) {
      const hit = this.worklistMergedRowsCache;
      if (hit?.combined?.length) {
        hit.combined = [...hit.combined];
      }
      if (this.admissionList?.length) {
        this.admissionList = [...this.admissionList];
      }
    }
    return changed;
  }

  /**
   * Keep toolbar search selections + merged grid caches in sync after Queue column PATCH.
   * Without this, adding another searched patient rebuilds the grid from stale `que_id` on
   * {@link worklistToolbarEngageSelections} and the previous queue name reappears.
   */
  patchWorklistEngageQueueInCaches(payload: {
    referralId: string;
    queueItemId: string;
    queueLabel?: string;
  }): void {
    const key = (payload.referralId ?? "").trim().toLowerCase();
    const qid = (payload.queueItemId ?? "").trim();
    const label = (payload.queueLabel ?? "").trim();
    if (!key || !qid) {
      return;
    }

    const refMatches = (ref: EngageReferral): boolean => {
      const refRid = extractEngageReferralRowId(ref).toLowerCase();
      const refPid = this.extractPatientIdFromEngageReferral(ref).toLowerCase();
      return (refRid && refRid === key) || (refPid && refPid === key);
    };

    this.worklistToolbarEngageSelections = this.worklistToolbarEngageSelections.map((ref) => {
      if (!refMatches(ref)) {
        return ref;
      }
      const next = { ...ref } as EngageReferral & Record<string, unknown>;
      next.que_id = qid;
      next.queId = qid;
      if (label) {
        next.referralQueueLabel = label;
        next.queue_name = label;
      }
      return next as EngageReferral;
    });

    const apptMatches = (appt: Appointment): boolean => {
      const apptRid = getAppointmentReferralId(appt).trim().toLowerCase();
      const apptPid = (appt.patientId ?? appt.encountersPatient?.id ?? "")
        .toString()
        .trim()
        .toLowerCase();
      return (apptRid && apptRid === key) || (apptPid && apptPid === key);
    };

    const patchAppt = (appt: Appointment): Appointment => {
      if (!apptMatches(appt)) {
        return appt;
      }
      return {
        ...appt,
        referralQueueId: qid,
        ...(label ? { referralQueueLabel: label } : {}),
      };
    };

    if (this.admissionList?.length) {
      this.admissionList = this.admissionList.map(patchAppt);
    }
    const hit = this.worklistMergedRowsCache;
    if (hit?.combined?.length) {
      hit.combined = hit.combined.map(patchAppt);
    }
  }

  /**
   * Worklist name-column filter: merged rows for **all** Engage `/referrals` pages (up to API `total`),
   * not the capped slice used for the grid page — plus toolbar-search merge and Engage-only modes.
   */
  loadWorklistNameFilterSourceAppointments$(): Observable<Appointment[]> {
    if (!this.worklistMergeEngageProspects) {
      return of([...(this.admissionList ?? [])]);
    }
    const hit = this.worklistMergedRowsCache;
    if (!hit) {
      return of([]);
    }
    const sig = this.worklistPageCacheSignature(
      this.pageSize,
      (this.searchQuery ?? "").trim(),
      this.currentPage
    );
    if (hit.sig !== sig) {
      return of([]);
    }
    const searchQ = (this.searchQuery ?? "").trim();
    const toolbarSel = this.worklistToolbarEngageSelections.length > 0;
    if (searchQ || toolbarSel) {
      return forkJoin({
        byPage: this.expandWorklistReferralPagesForHit$(hit),
        search: this.fetchEngageBootstrapForWorklistMerge(),
      }).pipe(
        switchMap(({ byPage, search }) => {
          const bootstrap = search ?? [];
          let combined = this.buildWorklistMergedCombinedFromParts(
            hit.clinicAll,
            hit.processedData,
            bootstrap,
            byPage,
            hit.applyReferralClientFilter,
            hit.activeRs
          );
          const refsForPhi = this.flattenWorklistReferralRows(bootstrap, byPage);
          return this.enrichReferralMappedRowsWithLatestPhi$(combined, refsForPhi);
        })
      );
    }
    if (!hit.clinicAll.length && !hit.processedData.length) {
      return this.expandWorklistReferralPagesForHit$(hit).pipe(
        switchMap((byPage) => {
          const refsFlat = this.flattenWorklistReferralRows([], byPage);
          let combined = refsFlat.map((r) => this.mapEngageReferralToWorklistAppointment(r));
          combined = this.enrichWorksheetRowsWithEngageReferralCounts(combined, refsFlat);
          combined = this.sortWorklistRowsByLatestActivityDesc(combined);
          return this.enrichReferralMappedRowsWithLatestPhi$(combined, refsFlat);
        })
      );
    }
    return this.expandWorklistReferralPagesForHit$(hit).pipe(
      switchMap((byPage) => {
        const combined = this.buildWorklistMergedCombinedFromParts(
          hit.clinicAll,
          hit.processedData,
          hit.bootstrapReferrals,
          byPage,
          hit.applyReferralClientFilter,
          hit.activeRs
        );
        const refsForPhi = this.flattenWorklistReferralRows(hit.bootstrapReferrals, byPage);
        return this.enrichReferralMappedRowsWithLatestPhi$(combined, refsForPhi);
      })
    );
  }

  /**
   * Cache key for worklist name-column filter options (filters/search/toolbar — not grid scroll page).
   */
  getWorklistNameFilterSourceCacheKey(): string {
    if (!this.worklistMergeEngageProspects) {
      return "__admission_list__";
    }
    return this.worklistPageCacheSignature(
      this.pageSize,
      (this.searchQuery ?? "").trim(),
      1
    );
  }

  /**
   * Cache key for the name “Filter by” facet list — same worklist filters as the grid except the active
   * name selection (so the sidebar always lists all names with checkboxes for the current picks).
   */
  getWorklistNameFilterFacetCacheKey(): string {
    if (!this.worklistMergeEngageProspects) {
      return "__admission_list__";
    }
    const search = (this.searchQuery ?? "").trim();
    return `facet-name\u001f${this.worklistDirectoryFiltersCacheSignatureExcludingName()}\u001f${search}`;
  }

  /** Cache key for DOB / DOS / Created / Last Activity facet sidebars (excludes that column’s active narrowing). */
  getWorklistFacetFilterFacetCacheKey(column: WorksheetFacetFilterColumn): string {
    if (!this.worklistMergeEngageProspects) {
      return "__admission_list__";
    }
    const search = (this.searchQuery ?? "").trim();
    return `facet-${column}\u001f${this.worklistDirectoryFiltersCacheSignatureForFacetColumn(column)}\u001f${search}`;
  }

  getWorklistDateFilterFacetCacheKey(
    column: "dob" | "dos" | "created" | "updated"
  ): string {
    if (!this.worklistMergeEngageProspects) {
      return "__admission_list__";
    }
    const search = (this.searchQuery ?? "").trim();
    return `facet-${column}\u001f${this.worklistDirectoryFiltersCacheSignatureForDateFacet(column)}\u001f${search}`;
  }

  getWorklistWorkflowFilterFacetCacheKey(): string {
    if (!this.worklistMergeEngageProspects) {
      return "__admission_list__";
    }
    const search = (this.searchQuery ?? "").trim();
    return `facet-workflow\u001f${this.worklistDirectoryFiltersCacheSignatureForWorkflowFacet()}\u001f${search}`;
  }

  /** Workflow Filter By cache: same as facet columns but excludes active `workFlowId`. */
  private worklistDirectoryFiltersCacheSignatureForWorkflowFacet(): string {
    const sig = this.worklistDirectoryFiltersCacheSignatureForFacetColumn("queue");
    const workFlowId = this.getWorklistWorkFlowIdQueryParam();
    return `${sig}\u001f${workFlowId}`;
  }

  /** {@link worklistPlainReferralsQueryCacheSignature} without name / `full_name` narrowing. */
  private worklistDirectoryFiltersCacheSignatureExcludingName(): string {
    const src = [...this.getActiveReferralSourceValues()]
      .map((s) => String(s ?? "").trim().toLowerCase())
      .filter(Boolean)
      .sort()
      .join("\u0002");
    const lead = [...this.getPatientDirectoryLeadStatusQueryParams()].sort().join("\u0002");
    const workFlowId = this.getWorklistWorkFlowIdQueryParam();
    const que = [...this.getWorklistReferralsQueIdQueryParams()].sort().join("\u0002");
    const notif = [...this.getWorklistReferralNotificationQueryParams()].join("\u0002");
    const createdKey = this.worklistCreatedFilterCacheKey();
    const activityKey = this.worklistActivityFilterCacheKey();
    const dosKey = this.worklistDosFilterCacheKey();
    const dobKey = this.worklistDobFilterCacheKey();
    const enc = this.encounterStatusCsvFromLiveFacets();
    const vt = this.visitTypeCsvFromLiveFacets();
    const svc = this.serviceCsvFromLiveFacets();
    const loc = this.locationCsvFromLiveFacets();
    const res = this.resourcesCsvFromLiveFacets();
    const phys = this.physicianFilter
      .filter((p) => p.value !== "All" && p.isSelected)
      .map((p) => String(p.value ?? "").trim())
      .filter(Boolean)
      .sort()
      .join("\u0002");
    const pid = (this._filterService.getWorklistEngagePatientIdFilter() ?? "").trim();
    const coord = (this.worksheetGridText?.coordinatorContains ?? "").trim().toLowerCase();
    const sortName = (this.worksheetNameDirectorySort ?? "").trim() || "";
    const sortDob = (this.worksheetDobDirectorySort ?? "").trim() || "";
    const sortDos = (this.worksheetDosDirectorySort ?? "").trim() || "";
    const sortCreated =
      (this.worksheetCreatedDirectorySort ?? "").trim() ||
      (this.isWorksheetCreatedDirectorySortActive() ? "ASC" : "");
    return `${src}\u001f${lead}\u001f${workFlowId}\u001f${que}\u001f${notif}\u001f${createdKey}\u001f${activityKey}\u001f${dosKey}\u001f${dobKey}\u001f${enc}\u001f${vt}\u001f${svc}\u001f${loc}\u001f${res}\u001f${phys}\u001f${pid}\u001f${coord}\u001f${sortName}\u001f${sortDob}\u001f${sortDos}\u001f${sortCreated}`;
  }

  /** Facet picker signature — includes all active directory filters (Filter By requests keep applied facets). */
  private worklistDirectoryFiltersCacheSignatureForFacetColumn(
    column: WorksheetFacetFilterColumn | "dob" | "dos" | "created" | "updated"
  ): string {
    const src = [...this.getActiveReferralSourceValues()]
      .map((s) => String(s ?? "").trim().toLowerCase())
      .filter(Boolean)
      .sort()
      .join("\u0002");
    const lead = [...this.getPatientDirectoryLeadStatusQueryParams()].sort().join("\u0002");
    const workFlowId = this.getWorklistWorkFlowIdQueryParam();
    const que = [...this.getWorklistReferralsQueIdQueryParams()].sort().join("\u0002");
    const notif = [...this.getWorklistReferralNotificationQueryParams()].join("\u0002");
    const createdKey =
      column === "created" ? "" : this.worklistCreatedFilterCacheKey();
    const activityKey =
      column === "updated" ? "" : this.worklistActivityFilterCacheKey();
    const dosKey = column === "dos" ? "__nodos__" : this.worklistDosFilterCacheKey();
    const enc = this.encounterStatusCsvFromLiveFacets();
    const vt = this.visitTypeCsvFromLiveFacets();
    const svc = this.serviceCsvFromLiveFacets();
    const loc = this.locationCsvFromLiveFacets();
    const res = this.resourcesCsvFromLiveFacets();
    const phys = this.physicianFilter
      .filter((p) => p.value !== "All" && p.isSelected)
      .map((p) => String(p.value ?? "").trim())
      .filter(Boolean)
      .sort()
      .join("\u0002");
    const pid = (this._filterService.getWorklistEngagePatientIdFilter() ?? "").trim();
    const coord = (this.worksheetGridText?.coordinatorContains ?? "").trim().toLowerCase();
    const sortName = (this.worksheetNameDirectorySort ?? "").trim() || "";
    const sortDob =
      column === "dob" ? "" : (this.worksheetDobDirectorySort ?? "").trim() || "";
    const sortDos =
      column === "dos" ? "" : (this.worksheetDosDirectorySort ?? "").trim() || "";
    const sortCreated =
      column === "created"
        ? ""
        : (this.worksheetCreatedDirectorySort ?? "").trim() ||
          (this.isWorksheetCreatedDirectorySortActive() ? "ASC" : "");
    const sortQueue =
      column === "queue" ? "" : (this.worksheetQueueDirectorySort ?? "").trim() || "";
    const sortRefSrc =
      column === "source"
        ? ""
        : (this.worksheetReferralSourceDirectorySort ?? "").trim() || "";
    const sortPtStatus =
      column === "patientStatus"
        ? ""
        : (this.worksheetPatientStatusDirectorySort ?? "").trim() || "";
    const sortEnc =
      column === "encStatus"
        ? ""
        : (this.worksheetEncounterStatusDirectorySort ?? "").trim() || "";
    const sortVt =
      column === "visitType"
        ? ""
        : (this.worksheetVisitTypeDirectorySort ?? "").trim() || "";
    const sortSvc =
      column === "service"
        ? ""
        : (this.worksheetServiceDirectorySort ?? "").trim() || "";
    const sortRes =
      column === "resource"
        ? ""
        : (this.worksheetResourceDirectorySort ?? "").trim() || "";
    const sortLoc =
      column === "location"
        ? ""
        : (this.worksheetLocationDirectorySort ?? "").trim() || "";
    const sortCoord =
      column === "coordinator"
        ? ""
        : (this.worksheetCoordinatorDirectorySort ?? "").trim() || "";
    const refSrc = [...this.getActiveReferralSourceValues()].sort().join("\u0002");
    return `${src}\u001f${lead}\u001f${workFlowId}\u001f${que}\u001f${notif}\u001f${createdKey}\u001f${activityKey}\u001f${dosKey}\u001f${enc}\u001f${vt}\u001f${svc}\u001f${loc}\u001f${res}\u001f${phys}\u001f${pid}\u001f${coord}\u001f${sortName}\u001f${sortDob}\u001f${sortDos}\u001f${sortCreated}\u001f${sortQueue}\u001f${sortRefSrc}\u001f${sortPtStatus}\u001f${sortEnc}\u001f${sortVt}\u001f${sortSvc}\u001f${sortRes}\u001f${sortLoc}\u001f${sortCoord}\u001f${refSrc}`;
  }

  private worklistDirectoryFiltersCacheSignatureForDateFacet(
    column: "dob" | "dos" | "created" | "updated"
  ): string {
    return this.worklistDirectoryFiltersCacheSignatureForFacetColumn(column);
  }

  /** True when every directory page for the current worklist filter is already in `referralsByPage`. */
  private worklistNameFilterDirectoryFullyCached(
    hit: WorklistMergedRowsCache,
    directoryOpts?: WorklistDirectoryPageFetchOpts
  ): boolean {
    if (worklistDirectoryFetchOptsRequiresFreshDirectoryPages(directoryOpts)) {
      return false;
    }
    const limit = resolveWorklistDirectoryPageLimit(directoryOpts);
    const totalRaw = hit.referralsApiTotal;
    const total =
      totalRaw != null && !Number.isNaN(Number(totalRaw)) && Number(totalRaw) > 0
        ? Number(totalRaw)
        : null;
    if (total != null) {
      const lastPage = Math.max(1, Math.ceil(total / limit));
      for (let p = 1; p <= lastPage; p++) {
        if (!(hit.referralsByPage.get(p)?.length ?? 0)) {
          return false;
        }
      }
      return true;
    }
    if (!hit.referralsByPage.size) {
      return false;
    }
    return !hit.referralsHasMore;
  }

  /**
   * Service-column filter: `GET …/patients/directory` with `include_last_service=true`.
   * API may return `{ facetValues: string[] }` or rows with `last_service`.
   */
  loadWorklistServiceFilterFacetValuesFromDirectory$(
    directoryOpts?: WorklistDirectoryPageFetchOpts
  ): Observable<string[]> {
    const opts: WorklistDirectoryPageFetchOpts = {
      includeLastService: true,
      pageLimit: WORKLIST_SERVICE_FILTER_DIRECTORY_PAGE_LIMIT,
      ...directoryOpts,
    };
    const pageLimit = resolveWorklistDirectoryPageLimit(opts);
    const base = phiPatientDirectoryListGetUrl(1, pageLimit);
    const filters = this.buildWorklistPlainPatientDirectoryFilters(opts);
    const url = appendPatientDirectoryFiltersToUrl(base, filters);
    return this._dataService.get(url).pipe(
      map((res: unknown) => {
        const facets = extractPatientDirectoryFacetValues(res);
        if (facets.length) {
          return facets;
        }
        const parsed = parsePatientDirectoryOrLegacyReferralsPayload(res, 1, pageLimit);
        const seen = new Set<string>();
        const out: string[] = [];
        const push = (raw: string | undefined): void => {
          const v = (raw ?? "").trim();
          if (!v) {
            return;
          }
          const key = v.toLowerCase();
          if (seen.has(key)) {
            return;
          }
          seen.add(key);
          out.push(v);
        };
        for (const row of parsed.referrals ?? []) {
          push(this.pickLastServiceFromRow(row));
          push(
            this.directoryStringField(row as Record<string, unknown>, [
              "service",
              "formProduct",
              "form_product",
            ])
          );
        }
        return out;
      }),
      catchError(() => of([]))
    );
  }

  /**
   * Encounter Status filter sidebar: `GET …/patients/directory` with `include_encounter_status=true`.
   * API may return `{ facetValues: string[] }` or rows with `encounter_status` / `last_encounter_status`.
   */
  loadWorklistEncounterStatusFilterFacetValuesFromDirectory$(
    directoryOpts?: WorklistDirectoryPageFetchOpts
  ): Observable<string[]> {
    const opts: WorklistDirectoryPageFetchOpts = {
      includeEncounterStatus: true,
      pageLimit: WORKLIST_SERVICE_FILTER_DIRECTORY_PAGE_LIMIT,
      ...directoryOpts,
    };
    const pageLimit = resolveWorklistDirectoryPageLimit(opts);
    const base = phiPatientDirectoryListGetUrl(1, pageLimit);
    const filters = this.buildWorklistPlainPatientDirectoryFilters(opts);
    const url = appendPatientDirectoryFiltersToUrl(base, filters);
    return this._dataService.get(url).pipe(
      map((res: unknown) => {
        const facets = extractPatientDirectoryFacetValues(res);
        if (facets.length) {
          return facets;
        }
        const parsed = parsePatientDirectoryOrLegacyReferralsPayload(res, 1, pageLimit);
        const seen = new Set<string>();
        const out: string[] = [];
        const push = (raw: string | undefined): void => {
          const v = (raw ?? "").trim();
          if (!v) {
            return;
          }
          const key = v.toLowerCase();
          if (seen.has(key)) {
            return;
          }
          seen.add(key);
          out.push(v);
        };
        for (const row of parsed.referrals ?? []) {
          push(this.pickLastEncounterStatusFromRow(row));
          push(
            this.directoryStringField(row as Record<string, unknown>, [
              "encounterStatus",
              "encounter_status",
            ])
          );
        }
        return out;
      }),
      catchError(() => of([]))
    );
  }

  /**
   * Coordinator filter sidebar: `GET …/patients/directory` with `include_assigned_to=true`.
   * API may return `{ facetValues: string[] }` or rows with `assigned_to` / `assign_to`.
   */
  loadWorklistCoordinatorFilterFacetValuesFromDirectory$(
    directoryOpts?: WorklistDirectoryPageFetchOpts
  ): Observable<string[]> {
    const opts: WorklistDirectoryPageFetchOpts = {
      includeAssignedTo: true,
      pageLimit: WORKLIST_SERVICE_FILTER_DIRECTORY_PAGE_LIMIT,
      ...directoryOpts,
    };
    const pageLimit = resolveWorklistDirectoryPageLimit(opts);
    const base = phiPatientDirectoryListGetUrl(1, pageLimit);
    const filters = this.buildWorklistPlainPatientDirectoryFilters(opts);
    const url = appendPatientDirectoryFiltersToUrl(base, filters);
    return this._dataService.get(url).pipe(
      map((res: unknown) => extractPatientDirectoryFacetValues(res)),
      catchError(() => of([]))
    );
  }

  /** Source Filter By: `GET …/patients/directory` with `include_source=true`. */
  loadWorklistSourceFilterFacetValuesFromDirectory$(
    directoryOpts?: WorklistDirectoryPageFetchOpts
  ): Observable<string[]> {
    const opts: WorklistDirectoryPageFetchOpts = {
      includeSource: true,
      pageLimit: WORKLIST_SERVICE_FILTER_DIRECTORY_PAGE_LIMIT,
      ...directoryOpts,
    };
    return this.loadWorklistDirectoryFacetValuesFromIncludeRequest$(opts);
  }

  /** Patient Status Filter By: `GET …/patients/directory` with `include_patient_status=true`. */
  loadWorklistPatientStatusFilterFacetValuesFromDirectory$(
    directoryOpts?: WorklistDirectoryPageFetchOpts
  ): Observable<string[]> {
    const opts: WorklistDirectoryPageFetchOpts = {
      includePatientStatus: true,
      pageLimit: WORKLIST_SERVICE_FILTER_DIRECTORY_PAGE_LIMIT,
      ...directoryOpts,
    };
    return this.loadWorklistDirectoryFacetValuesFromIncludeRequest$(opts);
  }

  /** Workflow Filter By: `GET …/patients/directory` with `include_workflow=true`. */
  loadWorklistWorkflowFilterFacetValuesFromDirectory$(
    directoryOpts?: WorklistDirectoryPageFetchOpts
  ): Observable<string[]> {
    const opts: WorklistDirectoryPageFetchOpts = {
      includeWorkflow: true,
      pageLimit: WORKLIST_SERVICE_FILTER_DIRECTORY_PAGE_LIMIT,
      ...directoryOpts,
    };
    return this.loadWorklistDirectoryFacetValuesFromIncludeRequest$(opts);
  }

  /** Stage Filter By: `GET …/patients/directory` with `include_stage=true`. */
  loadWorklistStageFilterFacetValuesFromDirectory$(
    directoryOpts?: WorklistDirectoryPageFetchOpts
  ): Observable<string[]> {
    const opts: WorklistDirectoryPageFetchOpts = {
      includeStage: true,
      pageLimit: WORKLIST_SERVICE_FILTER_DIRECTORY_PAGE_LIMIT,
      ...directoryOpts,
    };
    return this.loadWorklistDirectoryFacetValuesFromIncludeRequest$(opts);
  }

  private loadWorklistDirectoryFacetValuesFromIncludeRequest$(
    opts: WorklistDirectoryPageFetchOpts
  ): Observable<string[]> {
    const pageLimit = resolveWorklistDirectoryPageLimit(opts);
    const base = phiPatientDirectoryListGetUrl(1, pageLimit);
    const filters = this.buildWorklistPlainPatientDirectoryFilters(opts);
    const url = appendPatientDirectoryFiltersToUrl(base, filters);
    return this._dataService.get(url).pipe(
      map((res: unknown) => extractPatientDirectoryFacetValues(res)),
      catchError(() => of([]))
    );
  }

  /**
   * Name-column filter: load `GET …/patients/directory` page-by-page; emit accumulated rows after each page
   * (page 1 names appear immediately, then page 2, …). Re-open is instant when directory pages are cached.
   */
  loadWorklistNameFilterSourceAppointmentsProgressive$(
    directoryOpts?: WorklistDirectoryPageFetchOpts
  ): Observable<Appointment[]> {
    if (!this.worklistMergeEngageProspects) {
      return of([...(this.admissionList ?? [])]);
    }
    const hit = this.worklistMergedRowsCache;
    if (!hit) {
      return of([]);
    }
    const filterKey = this.getWorklistNameFilterSourceCacheKey();
    const gridSig = this.worklistPageCacheSignature(
      this.pageSize,
      (this.searchQuery ?? "").trim(),
      this.currentPage
    );
    if (hit.sig !== gridSig && hit.sig !== filterKey) {
      return of([]);
    }
    const searchQ = (this.searchQuery ?? "").trim();
    const toolbarSel = this.worklistToolbarEngageSelections.length > 0;
    const bootstrap$ =
      searchQ || toolbarSel
        ? this.fetchEngageBootstrapForWorklistMerge().pipe(
            map((bootstrap) => bootstrap ?? [])
          )
        : of([] as EngageReferral[]);
    return bootstrap$.pipe(
      switchMap((bootstrap) => {
        if (this.worklistNameFilterDirectoryFullyCached(hit, directoryOpts)) {
          return of(
            this.buildWorklistNameFilterAppointments(
              hit,
              new Map(hit.referralsByPage),
              bootstrap
            )
          );
        }
        return this.emitWorklistNameFilterAppointmentsAsDirectoryPages$(
          hit,
          bootstrap,
          directoryOpts
        );
      })
    );
  }

  private buildWorklistNameFilterAppointments(
    hit: WorklistMergedRowsCache,
    byPage: Map<number, EngageReferral[]>,
    bootstrapReferrals: EngageReferral[]
  ): Appointment[] {
    if (!hit.clinicAll.length && !hit.processedData.length) {
      const refsFlat = this.flattenWorklistReferralRows(bootstrapReferrals, byPage);
      let combined = refsFlat.map((r) => this.mapEngageReferralToWorklistAppointment(r));
      combined = this.enrichWorksheetRowsWithEngageReferralCounts(combined, refsFlat);
      return this.sortWorklistRowsByLatestActivityDesc(combined);
    }
    return this.buildWorklistMergedCombinedFromParts(
      hit.clinicAll,
      hit.processedData,
      bootstrapReferrals,
      byPage,
      hit.applyReferralClientFilter,
      hit.activeRs
    );
  }

  private ensureWorklistNameFilterDirectoryPage$(
    hit: WorklistMergedRowsCache,
    byPage: Map<number, EngageReferral[]>,
    page: number,
    directoryOpts?: WorklistDirectoryPageFetchOpts
  ): Observable<void> {
    if (byPage.has(page)) {
      return of(undefined);
    }
    return this.fetchWorklistReferralPagesBatch([page], directoryOpts).pipe(
      map(({ byPage: fetched }) => {
        for (const [k, v] of fetched) {
          byPage.set(k, v);
          hit.referralsByPage.set(k, v);
        }
      })
    );
  }

  private emitWorklistNameFilterAppointmentsAsDirectoryPages$(
    hit: WorklistMergedRowsCache,
    bootstrapReferrals: EngageReferral[],
    directoryOpts?: WorklistDirectoryPageFetchOpts
  ): Observable<Appointment[]> {
    const byPage = worklistDirectoryFetchOptsRequiresFreshDirectoryPages(directoryOpts)
      ? new Map<number, EngageReferral[]>()
      : new Map(hit.referralsByPage);
    if (this.worklistNameFilterDirectoryFullyCached(hit, directoryOpts)) {
      return of(
        this.buildWorklistNameFilterAppointments(hit, byPage, bootstrapReferrals)
      );
    }
    const limit = resolveWorklistDirectoryPageLimit(directoryOpts);
    const totalRaw = hit.referralsApiTotal;
    const total =
      totalRaw != null && !Number.isNaN(Number(totalRaw)) && Number(totalRaw) > 0
        ? Number(totalRaw)
        : null;

    if (total != null) {
      const lastPage = Math.max(1, Math.ceil(total / limit));
      const pages = Array.from({ length: lastPage }, (_, i) => i + 1);
      return from(pages).pipe(
        concatMap((page) =>
          this.ensureWorklistNameFilterDirectoryPage$(hit, byPage, page, directoryOpts).pipe(
            map(() => this.buildWorklistNameFilterAppointments(hit, byPage, bootstrapReferrals))
          )
        )
      );
    }

    return this.emitWorklistNameFilterDirectoryPagesSequential$(
      hit,
      byPage,
      bootstrapReferrals,
      1,
      limit,
      directoryOpts
    );
  }

  private emitWorklistNameFilterDirectoryPagesSequential$(
    hit: WorklistMergedRowsCache,
    byPage: Map<number, EngageReferral[]>,
    bootstrapReferrals: EngageReferral[],
    page: number,
    limit: number,
    directoryOpts?: WorklistDirectoryPageFetchOpts
  ): Observable<Appointment[]> {
    const maxGuard = 500;
    if (page > maxGuard) {
      return EMPTY;
    }
    return this.ensureWorklistNameFilterDirectoryPage$(hit, byPage, page, directoryOpts).pipe(
      switchMap(() => {
        const combined = this.buildWorklistNameFilterAppointments(
          hit,
          byPage,
          bootstrapReferrals
        );
        const count = (byPage.get(page) ?? []).length;
        if (!count || count < limit) {
          return of(combined);
        }
        return concat(
          of(combined),
          this.emitWorklistNameFilterDirectoryPagesSequential$(
            hit,
            byPage,
            bootstrapReferrals,
            page + 1,
            limit,
            directoryOpts
          )
        );
      })
    );
  }

  private expandWorklistReferralPagesForHit$(
    hit: WorklistMergedRowsCache
  ): Observable<Map<number, EngageReferral[]>> {
    const byPage = new Map(hit.referralsByPage);
    const limit = WORKLIST_REFERRALS_API_PAGE_LIMIT;
    const totalRaw = hit.referralsApiTotal;
    const total =
      totalRaw != null && !Number.isNaN(Number(totalRaw)) && Number(totalRaw) > 0
        ? Number(totalRaw)
        : null;

    if (total != null) {
      const lastPage = Math.max(1, Math.ceil(total / limit));
      const missing: number[] = [];
      for (let p = 1; p <= lastPage; p++) {
        if (!byPage.has(p)) {
          missing.push(p);
        }
      }
      if (!missing.length) {
        return of(byPage);
      }
      return this.fetchWorklistReferralPagesBatch(missing).pipe(
        map(({ byPage: fetched }) => {
          for (const [k, v] of fetched) {
            byPage.set(k, v);
          }
          return byPage;
        })
      );
    }

    if (!hit.referralsHasMore) {
      return of(byPage);
    }
    const maxLoaded = byPage.size ? Math.max(...byPage.keys()) : 0;
    return this.fetchWorklistReferralPagesSequentially$(byPage, maxLoaded + 1, limit);
  }

  private fetchWorklistReferralPagesSequentially$(
    byPage: Map<number, EngageReferral[]>,
    startPage: number,
    limit: number
  ): Observable<Map<number, EngageReferral[]>> {
    const maxGuard = 500;
    const step = (page: number): Observable<Map<number, EngageReferral[]>> => {
      if (page > maxGuard) {
        return of(byPage);
      }
      return this.fetchWorklistReferralPagesBatch([page]).pipe(
        switchMap(({ byPage: fetched }) => {
          const rows = fetched.get(page) ?? [];
          if (!rows.length) {
            return of(byPage);
          }
          for (const [k, v] of fetched) {
            byPage.set(k, v);
          }
          if (rows.length < limit) {
            return of(byPage);
          }
          return step(page + 1);
        })
      );
    };
    return step(startPage);
  }

  private worklistPageCacheSignature(
    pageSize: number,
    searchQuery: string,
    listPage: number
  ): string {
    const now = new Date();
    /** Worklist merge: avoid invalidating cache every calendar month — list-clinic window is explicit in payload. */
    const monthKey = this.worklistMergeEngageProspects
      ? "__wk__"
      : DateTime.fromJSDate(now).toFormat("yyyy-MM");
    const q = (searchQuery || this.searchQuery || "").trim();
    const toolbarSel = [...this.worklistToolbarEngageSelections]
      .map((r) => String(r?.id ?? "").trim())
      .filter(Boolean)
      .sort()
      .join(",");
    const prov = this.physicianFilter
      .filter((p) => p.value !== "All" && p.isSelected)
      .map((p) => String(p.value))
      .join(",");
    const res = this.resourcesFilter
      .filter((r) => r.value !== "All" && r.isSelected)
      .map((r) => String(r.value))
      .join(",");
    const loc = this.locationFilter
      .filter((l) => l.value !== "All" && l.isSelected)
      .map((l) => String(l.value))
      .join(",");
    const st = this.encounerStatusList
      .filter((p) => p.value !== "All" && p.isSelected)
      .map((p) => String(p.value))
      .join(",");
    const vt = this.visitTypeFilter
      .filter((v) => v.value !== "All" && v.isSelected)
      .map((v) => String(v.value))
      .join(",");
    const rs = this.getActiveReferralSourceValues().join(",");
    const gt = JSON.stringify(this.worksheetGridText ?? {});
    const c = this.worksheetCreatedOnIso ?? "";
    const cEnd = this.worksheetCreatedOnEndIso ?? "";
    const u = this.worksheetUpdatedOnIso ?? "";
    const a0 = this.worksheetActivityDateStartIso ?? "";
    const a1 = this.worksheetActivityDateEndIso ?? "";
    const dr = this.selectedDate;
    let dateSig =
      dr?.startDate && dr?.endDate
        ? `${dr.startDate.getTime()}-${dr.endDate.getTime()}`
        : "";
    if (this.worklistMergeEngageProspects && !this.worksheetUserCommittedDosRange) {
      dateSig = "__worklist_nodos__";
    }
    return [
      monthKey,
      dateSig,
      String(pageSize),
      q,
      toolbarSel,
      prov,
      res,
      loc,
      st,
      vt,
      rs,
      gt,
      c,
      cEnd,
      u,
      a0,
      a1,
      this.worklistMergeEngageFromReferralsApi ? "1" : "0",
      String(listPage),
    ].join("\u001f");
  }

  /**
   * Seeds resources (when empty) and rebuilds location / visit-type rows from referrals counts.
   * Preserves current isSelected flags. Mirrors Schedule `loadCounts` facet handling for Worksheet.
   */
  hydrateReferralsFacetFiltersFromCounts(
    response: AppointmentCountResponse,
    opts: {
      resources: Array<{ id?: string; name?: string }>;
      locations: EnumerationItem[];
    }
  ): void {
    if (this.resourcesFilter.length === 0) {
      this.resourcesFilter.push(
        ...(opts.resources || []).map((resource) => ({
          displayName: resource.name ?? "",
          value: resource.id ?? "",
          isSelected: false,
        }))
      );
    }

    const preservedLocationValues = new Set(
      (this.locationFilter ?? [])
        .filter((x) => x.isSelected)
        .map((x) => String(x.value ?? ""))
    );
    const preservedVisitTypeValues = new Set(
      (this.visitTypeFilter ?? [])
        .filter((x) => x.isSelected)
        .map((x) => String(x.value ?? "").toLowerCase())
    );

    const locationsFilter: SelectableEnumerationItem[] = [];
    if (response.locations && response.locations.length > 0) {
      locationsFilter.push(
        ...response.locations
          .filter((loc) => loc.label !== "All")
          .map((loc) => ({
            displayName: loc.label,
            value: loc.label,
            isSelected: preservedLocationValues.has(loc.label),
          }))
      );
    } else {
      locationsFilter.push(
        ...(opts.locations || []).map((loc) => ({
          displayName: loc.displayName,
          value: String(loc.parentValue ?? ""),
          isSelected: preservedLocationValues.has(String(loc.parentValue ?? "")),
        }))
      );
    }
    this.locationFilter = locationsFilter;

    const visitTypeFilter: SelectableEnumerationItem[] = [];
    if (response.visitTypes && response.visitTypes.length > 0) {
      visitTypeFilter.push(
        ...response.visitTypes
          .filter((vt) => vt.label !== "All")
          .map((vt) => {
            const row = mapVisitTypeFacetFromApiLabel(vt.label, false);
            return {
              ...row,
              isSelected: preservedVisitTypeValues.has(
                String(row.value ?? "").toLowerCase()
              ),
            };
          })
      );
    } else {
      visitTypeFilter.push(
        ...Utils.getEnumerationItemsFromVisitTypeEnum().map((vt) => ({
          ...vt,
          displayName: `${vt.displayName}`,
          isSelected: preservedVisitTypeValues.has(
            String(vt.value ?? "").toLowerCase()
          ),
        }))
      );
    }
    this.visitTypeFilter = mergeWorksheetVisitTypeFacetRows(visitTypeFilter);

    if (!this.encounerStatusList?.length) {
      this.encounerStatusList = getAdmissionsScheduleStatusFilterOptions();
    }
  }

  /** Referral sources for list-clinic: multi-select on Schedule, or single value from dashboard. */
  getActiveReferralSourceValues(): string[] {
    const normalizeReferralSourceId = (v: string): string =>
      v.trim().toLowerCase() === "meta" ? "online" : v.trim();
    const fromList = this.referralSourceFilterList
      .filter((x) => x.value !== "All" && x.isSelected)
      .map((x) => normalizeReferralSourceId(String(x.value ?? "")))
      .filter((v) => v.length > 0);
    const normalized = normalizeActiveReferralSourceFilterValues(fromList);
    if (normalized.length > 0) {
      return normalized;
    }
    if (this.referralSourceFilter) {
      return normalizeActiveReferralSourceFilterValues([
        normalizeReferralSourceId(String(this.referralSourceFilter)),
      ]);
    }
    return [];
  }

  /**
   * Worksheet **Enc. Status** facet → Engage `GET .../referrals` `lead_status` query (comma-separated).
   */
  private getActiveWorklistLeadStatusQueryValues(): string[] {
    return this.encounerStatusList
      .filter((p) => p.value !== "All" && p.isSelected)
      .map((p) => String(p.value ?? "").trim())
      .filter((v) => v.length > 0);
  }

  /** Enc. Status facet values + Ref. status grid text → legacy Engage `lead_status` merge semantics (see {@link getPatientDirectoryLeadStatusQueryParams} for PHI directory). */
  private getWorklistReferralsLeadStatusQueryParams(): string[] {
    const raw = [
      ...this.getActiveWorklistLeadStatusQueryValues(),
      (this.worksheetGridText?.refStatusContains ?? "").toString().trim(),
    ].filter(Boolean);
    const seen = new Set<string>();
    const out: string[] = [];
    for (const s of raw) {
      const k = s.toLowerCase();
      if (seen.has(k)) {
        continue;
      }
      seen.add(k);
      out.push(k);
    }
    return out;
  }

  /**
   * PHI `GET …/patients/directory` **`lead_status`** — worksheet **Ref. status** grid text only (Engage referral pipeline).
   * Clinical **Encounter Status** (Open, Scheduled, …) is sent as **`encounter_status`** via {@link mergeWorklistDirectoryEncounterFacetFiltersIntoPatientDirectoryFilters}, not here.
   */
  private getPatientDirectoryLeadStatusQueryParams(): string[] {
    const raw = [(this.worksheetGridText?.refStatusContains ?? "").toString().trim()].filter(Boolean);
    const seen = new Set<string>();
    const out: string[] = [];
    for (const s of raw) {
      for (const part of s.split(",")) {
        const trimmed = part.trim();
        if (!trimmed) {
          continue;
        }
        const mapped = patientDirectoryLeadStatusQueryValue(trimmed);
        const k = mapped.toLowerCase();
        if (seen.has(k)) {
          continue;
        }
        seen.add(k);
        out.push(k);
      }
    }
    return out;
  }

  /** True when directory can load (`all` or a workflow UUID). */
  hasReadyWorksheetWorkFlowId(): boolean {
    return !!this.getWorklistWorkFlowIdQueryParam();
  }

  /** Toolbar / directory is showing every pipeline (`workFlowId=all`). */
  isAllWorksheetWorkFlowSelected(): boolean {
    return this.isAllWorkFlowId(this.getWorklistWorkFlowIdQueryParam());
  }

  /** Stage column / filters sheet selection → PHI directory `que_id` tokens. */
  getWorklistStageQueFilterIds(): string[] {
    return [...this.getWorklistReferralsQueIdQueryParams()];
  }

  /** True when the worklist is narrowed to one or more workflow stages (`queueContains`). */
  hasWorklistStageQueFilter(): boolean {
    return this.getWorklistStageQueFilterIds().length > 0;
  }

  /**
   * Signature for Engage pipeline reload — must match list directory filters (incl. stage `que_id`).
   */
  getEngagePipelineLoadCacheKey(): string {
    const wf = (this.getWorklistWorkFlowIdQueryParam() ?? "").trim();
    const q = (this.searchQuery ?? "").trim();
    return `${wf}\u001f${q}\u001f${this.worklistPlainReferralsQueryCacheSignature()}`;
  }

  /** Toolbar workflow picker → PHI directory `workFlowId` (`all` or a UUID). */
  private getWorklistWorkFlowIdQueryParam(): string {
    const id = (this.worksheetSelectedPipelineId ?? "").trim();
    if (!id || this.isPlaceholderWorkFlowId(id) || this.isAllWorkFlowId(id)) {
      return WORKSHEET_ALL_WORKFLOW_ID;
    }
    return id;
  }

  private isAllWorkFlowId(id: string): boolean {
    return id.trim().toLowerCase() === WORKSHEET_ALL_WORKFLOW_ID;
  }

  private isPlaceholderWorkFlowId(id: string): boolean {
    const k = id.toLowerCase();
    return k === "default" || k === "default work flow" || k === "default workflow";
  }

  /**
   * Worksheet filter sheet **Queue** only → PHI directory `que_id`.
   * Toolbar workflow uses {@link getWorklistWorkFlowIdQueryParam} (`workFlowId`), not stage slugs.
   */
  private getWorklistReferralsQueIdQueryParams(): string[] {
    return this.parseWorklistQueIdCsv(
      (this.worksheetGridText?.queueContains ?? "").toString().trim()
    );
  }

  private parseWorklistQueIdCsv(raw: string): string[] {
    if (!raw) {
      return [];
    }
    const seen = new Set<string>();
    const out: string[] = [];
    for (const part of raw.split(",")) {
      const s = part.trim();
      if (!s) {
        continue;
      }
      const mapped = patientDirectoryQueIdQueryValue(s);
      if (!mapped || seen.has(mapped)) {
        continue;
      }
      seen.add(mapped);
      out.push(mapped);
    }
    return out;
  }

  private readonly worklistReferralNotificationAllowed = new Set<string>(
    WORKSHEET_REFERRAL_NOTIFICATION_FILTER_KEYS
  );

  /** Engage `GET .../referrals` notification flags from the worksheet filter sheet (each sent as `&key=true`). */
  /**
   * Engage `GET .../referrals` `full_name` values from worksheet name column (exact match, case-insensitive on server).
   */
  private getWorklistReferralsFullNameQueryParams(
    gridOverride?: WorksheetGridClientFilters | null
  ): string[] {
    const g = gridOverride ?? this.worksheetGridText;
    if (!g) {
      return [];
    }
    const fromStored = parseReferralsFullNameFiltersCsv(g.referralsFullNameFilters);
    if (fromStored.length > 0) {
      const seen = new Set<string>();
      const out: string[] = [];
      for (const x of fromStored) {
        const s = String(x ?? "").trim();
        if (!s || seen.has(s)) {
          continue;
        }
        seen.add(s);
        out.push(s);
      }
      return out;
    }
    const keys = (g.namePatientKeysContains ?? "").trim();
    if (!keys) {
      return [];
    }
    const fallback: string[] = [];
    const seenN = new Set<string>();
    for (const k of keys
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean)) {
      if (k.startsWith("n:")) {
        const n = k.slice(2).trim();
        if (n && !seenN.has(n)) {
          seenN.add(n);
          fallback.push(n);
        }
      }
    }
    return fallback;
  }

  private getWorklistReferralNotificationQueryParams(): string[] {
    const raw = this.worksheetGridText?.referralNotificationFilters;
    const list = Array.isArray(raw) ? raw : [];
    const seen = new Set<string>();
    const out: string[] = [];
    for (const x of list) {
      const k = String(x ?? "").trim();
      if (!k || seen.has(k) || !this.worklistReferralNotificationAllowed.has(k)) {
        continue;
      }
      seen.add(k);
      out.push(k);
    }
    return out.sort();
  }

  private worklistPlainReferralsQueryCacheSignature(): string {
    const src = [...this.getActiveReferralSourceValues()]
      .map((s) => String(s ?? "").trim().toLowerCase())
      .filter(Boolean)
      .sort()
      .join("\u0002");
    const lead = [...this.getPatientDirectoryLeadStatusQueryParams()].sort().join("\u0002");
    const workFlowId = this.getWorklistWorkFlowIdQueryParam();
    const que = [...this.getWorklistReferralsQueIdQueryParams()].sort().join("\u0002");
    const notif = [...this.getWorklistReferralNotificationQueryParams()].join("\u0002");
    const createdKey = this.worklistCreatedFilterCacheKey();
    const activityKey = this.worklistActivityFilterCacheKey();
    const fullNames = [...this.getWorklistReferralsFullNameQueryParams()].sort().join("\u0002");
    const engageSearch = (this.searchQuery ?? "").trim();
    const dosKey = this.worklistDosFilterCacheKey();
    const dobKey = this.worklistDobFilterCacheKey();
    const enc = this.encounterStatusCsvFromLiveFacets();
    const vt = this.visitTypeCsvFromLiveFacets();
    const svc = this.serviceCsvFromLiveFacets();
    const loc = this.locationCsvFromLiveFacets();
    const res = this.resourcesCsvFromLiveFacets();
    const phys = this.physicianFilter
      .filter((p) => p.value !== "All" && p.isSelected)
      .map((p) => String(p.value ?? "").trim())
      .filter(Boolean)
      .sort()
      .join("\u0002");
    const pid = (this._filterService.getWorklistEngagePatientIdFilter() ?? "").trim();
    const coord = (this.worksheetGridText?.coordinatorContains ?? "").trim().toLowerCase();
    const payer = (this.worksheetGridText?.payerContains ?? "").trim().toLowerCase();
    const eligibility = (this.worksheetGridText?.eligibilityContains ?? "").trim().toLowerCase();
    const auth = (this.worksheetGridText?.authStatusContains ?? "").trim().toLowerCase();
    const claim = (this.worksheetGridText?.claimStatusContains ?? "").trim().toLowerCase();
    const sortName = (this.worksheetNameDirectorySort ?? "").trim() || "";
    const sortDob = (this.worksheetDobDirectorySort ?? "").trim() || "";
    const sortDos = (this.worksheetDosDirectorySort ?? "").trim() || "";
    const sortCreated =
      (this.worksheetCreatedDirectorySort ?? "").trim() ||
      (this.isWorksheetCreatedDirectorySortActive() ? "ASC" : "");
    const nameFilter = this.isWorksheetNameFilterValuesActive() ? "1" : "";
    return `${src}\u001f${lead}\u001f${workFlowId}\u001f${que}\u001f${notif}\u001f${createdKey}\u001f${activityKey}\u001f${fullNames}\u001f${engageSearch}\u001f${dosKey}\u001f${dobKey}\u001f${enc}\u001f${vt}\u001f${svc}\u001f${loc}\u001f${res}\u001f${phys}\u001f${pid}\u001f${coord}\u001f${payer}\u001f${eligibility}\u001f${auth}\u001f${claim}\u001f${sortName}\u001f${sortDob}\u001f${sortDos}\u001f${sortCreated}\u001f${nameFilter}`;
  }

  private isWorksheetNameFilterValuesActive(
    gridOverride?: WorksheetGridClientFilters | null
  ): boolean {
    if (this.getWorklistReferralsFullNameQueryParams(gridOverride).length > 0) {
      return true;
    }
    const g = gridOverride ?? this.worksheetGridText;
    return !!(g?.namePatientKeysContains ?? "").trim();
  }

  /** Worksheet facet column sort: explicit header choice, else default ASC when that facet is narrowed. */
  private resolveWorksheetFacetDirectorySort(
    directoryOptSort: "ASC" | "DESC" | undefined,
    worksheetSort: "ASC" | "DESC" | null,
    facetFilterActive: boolean
  ): "ASC" | "DESC" | null {
    if (directoryOptSort === "ASC" || directoryOptSort === "DESC") {
      return directoryOptSort;
    }
    if (worksheetSort === "ASC" || worksheetSort === "DESC") {
      return worksheetSort;
    }
    return facetFilterActive ? "ASC" : null;
  }

  private resolveWorksheetNameSortForDirectory(
    directoryOpts?: WorklistDirectoryPageFetchOpts,
    gridOverride?: WorksheetGridClientFilters | null
  ): "ASC" | "DESC" | null {
    if (directoryOpts?.sortName === "ASC" || directoryOpts?.sortName === "DESC") {
      return directoryOpts.sortName;
    }
    if (
      this.worksheetNameDirectorySort === "ASC" ||
      this.worksheetNameDirectorySort === "DESC"
    ) {
      return this.worksheetNameDirectorySort;
    }
    if (this.isWorksheetNameFilterValuesActive(gridOverride)) {
      return "ASC";
    }
    return null;
  }

  private isWorksheetCreatedDirectorySortActive(
    gridOverride?: WorksheetGridClientFilters | null,
    createdOnIso?: string | null,
    createdOnEndIso?: string | null
  ): boolean {
    const g = gridOverride ?? this.worksheetGridText;
    if ((g?.createdDayKeysContains ?? "").trim()) {
      return true;
    }
    const iso =
      createdOnIso !== undefined ? createdOnIso : this.worksheetCreatedOnIso;
    const end =
      createdOnEndIso !== undefined
        ? createdOnEndIso
        : this.worksheetCreatedOnEndIso;
    return !!(iso ?? "").trim() || !!(end ?? "").trim();
  }

  private resolveWorksheetDobSortForDirectory(
    directoryOpts?: WorklistDirectoryPageFetchOpts,
    gridOverride?: WorksheetGridClientFilters | null
  ): "ASC" | "DESC" | null {
    if (directoryOpts?.sortDob === "ASC" || directoryOpts?.sortDob === "DESC") {
      return directoryOpts.sortDob;
    }
    const g = gridOverride ?? this.worksheetGridText;
    if (isWorksheetDobBlankFilterValue(g?.dobContains)) {
      return null;
    }
    if (
      this.worksheetDobDirectorySort === "ASC" ||
      this.worksheetDobDirectorySort === "DESC"
    ) {
      return this.worksheetDobDirectorySort;
    }
    if ((g?.dobDayKeysContains ?? "").trim()) {
      return "ASC";
    }
    return null;
  }

  private worklistDobFilterCacheKey(
    gridOverride?: WorksheetGridClientFilters | null
  ): string {
    const g = gridOverride ?? this.worksheetGridText;
    if (isWorksheetDobBlankFilterValue(g?.dobContains)) {
      return WORKSHEET_DOB_BLANK_DIRECTORY_QUERY;
    }
    const dobBounds = this.dayKeysCsvToYyyyMmDdBounds(g?.dobDayKeysContains);
    return dobBounds ? `${dobBounds.start}\u0003${dobBounds.end}` : "__nodob__";
  }

  private worklistDosFilterCacheKey(
    gridOverride?: WorksheetGridClientFilters | null
  ): string {
    const g = gridOverride ?? this.worksheetGridText;
    if (isWorksheetDosBlankFilterValue(g?.dosContains)) {
      return WORKSHEET_DOS_BLANK_DIRECTORY_QUERY;
    }
    if (this.worksheetUserCommittedDosRange) {
      const dr = this.selectedDate;
      if (dr?.startDate && dr?.endDate) {
        const s = this.dateToLocalYyyyMmDd(dr.startDate);
        const e = this.dateToLocalYyyyMmDd(dr.endDate);
        if (s && e) {
          const lo = s <= e ? s : e;
          const hi = s <= e ? e : s;
          return `${lo}\u0003${hi}`;
        }
      }
    }
    const dosBounds = this.dayKeysCsvToYyyyMmDdBounds(g?.dosDayKeysContains);
    return dosBounds ? `${dosBounds.start}\u0003${dosBounds.end}` : "__nodos__";
  }

  private resolveWorksheetDosSortForDirectory(
    directoryOpts?: WorklistDirectoryPageFetchOpts,
    gridOverride?: WorksheetGridClientFilters | null
  ): "ASC" | "DESC" | null {
    if (directoryOpts?.sortDos === "ASC" || directoryOpts?.sortDos === "DESC") {
      return directoryOpts.sortDos;
    }
    if (
      this.worksheetDosDirectorySort === "ASC" ||
      this.worksheetDosDirectorySort === "DESC"
    ) {
      return this.worksheetDosDirectorySort;
    }
    const g = gridOverride ?? this.worksheetGridText;
    if (isWorksheetDosBlankFilterValue(g?.dosContains)) {
      return null;
    }
    if ((g?.dosDayKeysContains ?? "").trim()) {
      return "ASC";
    }
    return null;
  }

  private resolveWorksheetCreatedSortForDirectory(
    directoryOpts?: WorklistDirectoryPageFetchOpts,
    gridOverride?: WorksheetGridClientFilters | null,
    createdOnIso?: string | null,
    createdOnEndIso?: string | null
  ): "ASC" | "DESC" | null {
    if (
      directoryOpts?.sortCreatedAt === "ASC" ||
      directoryOpts?.sortCreatedAt === "DESC"
    ) {
      return directoryOpts.sortCreatedAt;
    }
    if (directoryOpts?.sortCreatedAtAsc) {
      return "ASC";
    }
    if (
      this.worksheetCreatedDirectorySort === "ASC" ||
      this.worksheetCreatedDirectorySort === "DESC"
    ) {
      return this.worksheetCreatedDirectorySort;
    }
    if (
      this.isWorksheetCreatedDirectorySortActive(
        gridOverride,
        createdOnIso,
        createdOnEndIso
      )
    ) {
      return "ASC";
    }
    return null;
  }

  private worklistDirectorySortCacheTags(
    directoryOpts?: WorklistDirectoryPageFetchOpts
  ): string {
    const name =
      directoryOpts?.sortName ?? this.worksheetNameDirectorySort ?? null;
    const dob = directoryOpts?.sortDob ?? this.worksheetDobDirectorySort ?? null;
    const dos = directoryOpts?.sortDos ?? this.worksheetDosDirectorySort ?? null;
    let created: "ASC" | "DESC" | null =
      directoryOpts?.sortCreatedAt ??
      this.worksheetCreatedDirectorySort ??
      null;
    if (!created && directoryOpts?.sortCreatedAtAsc) {
      created = "ASC";
    }
    if (!created && this.isWorksheetCreatedDirectorySortActive()) {
      created = "ASC";
    }
    let t = "";
    if (name === "DESC") {
      t += "SND";
    } else if (name === "ASC") {
      t += "SNA";
    }
    if (dob === "DESC") {
      t += "SDOBD";
    } else if (dob === "ASC") {
      t += "SDOBA";
    }
    if (dos === "DESC") {
      t += "SDOSD";
    } else if (dos === "ASC") {
      t += "SDOSA";
    }
    if (created === "DESC") {
      t += "SCRD";
    } else if (created === "ASC") {
      t += "SCRA";
    }
    const queue = directoryOpts?.sortQueue ?? this.worksheetQueueDirectorySort ?? null;
    const refSrc =
      directoryOpts?.sortReferralSource ??
      this.worksheetReferralSourceDirectorySort ??
      null;
    const lead =
      directoryOpts?.sortLeadStatus ?? this.worksheetPatientStatusDirectorySort ?? null;
    const enc =
      directoryOpts?.sortEncounterStatus ??
      this.worksheetEncounterStatusDirectorySort ??
      null;
    const vt = directoryOpts?.sortVisitType ?? this.worksheetVisitTypeDirectorySort ?? null;
    const svc = directoryOpts?.sortService ?? this.worksheetServiceDirectorySort ?? null;
    const res =
      directoryOpts?.sortScheduleResource ?? this.worksheetResourceDirectorySort ?? null;
    const loc = directoryOpts?.sortLocation ?? this.worksheetLocationDirectorySort ?? null;
    const coord =
      directoryOpts?.sortAssignedTo ?? this.worksheetCoordinatorDirectorySort ?? null;
    if (queue === "DESC") {
      t += "SQD";
    } else if (queue === "ASC") {
      t += "SQA";
    }
    if (refSrc === "DESC") {
      t += "SRSD";
    } else if (refSrc === "ASC") {
      t += "SRSA";
    }
    if (lead === "DESC") {
      t += "SLSD";
    } else if (lead === "ASC") {
      t += "SLSA";
    }
    if (enc === "DESC") {
      t += "SESD";
    } else if (enc === "ASC") {
      t += "SESA";
    }
    if (vt === "DESC") {
      t += "SVTD";
    } else if (vt === "ASC") {
      t += "SVTA";
    }
    if (svc === "DESC") {
      t += "SSVD";
    } else if (svc === "ASC") {
      t += "SSVA";
    }
    if (res === "DESC") {
      t += "SRRD";
    } else if (res === "ASC") {
      t += "SRA";
    }
    if (loc === "DESC") {
      t += "SLCD";
    } else if (loc === "ASC") {
      t += "SLCA";
    }
    if (coord === "DESC") {
      t += "SCOD";
    } else if (coord === "ASC") {
      t += "SCOA";
    }
    return t;
  }

  setWorksheetFacetDirectorySort(
    column: WorksheetFacetFilterColumn,
    direction: "ASC" | "DESC" | null
  ): void {
    switch (column) {
      case "queue":
        this.worksheetQueueDirectorySort = direction;
        break;
      case "source":
        this.worksheetReferralSourceDirectorySort = direction;
        break;
      case "patientStatus":
        this.worksheetPatientStatusDirectorySort = direction;
        break;
      case "encStatus":
        this.worksheetEncounterStatusDirectorySort = direction;
        break;
      case "visitType":
        this.worksheetVisitTypeDirectorySort = direction;
        break;
      case "service":
        this.worksheetServiceDirectorySort = direction;
        break;
      case "resource":
        this.worksheetResourceDirectorySort = direction;
        break;
      case "location":
        this.worksheetLocationDirectorySort = direction;
        break;
      case "coordinator":
        this.worksheetCoordinatorDirectorySort = direction;
        break;
    }
  }

  private mergeWorklistDirectorySortQueryParams(
    filters: PatientDirectoryFilterValues,
    directoryOpts?: WorklistDirectoryPageFetchOpts,
    gridOverride?: WorksheetGridClientFilters | null,
    createdOnIso?: string | null,
    createdOnEndIso?: string | null
  ): void {
    const nameSort = this.resolveWorksheetNameSortForDirectory(
      directoryOpts,
      gridOverride
    );
    if (nameSort) {
      filters.sort_name = nameSort;
    }
    const dobSort = this.resolveWorksheetDobSortForDirectory(
      directoryOpts,
      gridOverride
    );
    if (dobSort) {
      filters.sort_dob = dobSort;
    }
    const dosSort = this.resolveWorksheetDosSortForDirectory(
      directoryOpts,
      gridOverride
    );
    if (dosSort) {
      filters.sort_dos = dosSort;
    }
    const createdSort = this.resolveWorksheetCreatedSortForDirectory(
      directoryOpts,
      gridOverride,
      createdOnIso,
      createdOnEndIso
    );
    if (createdSort) {
      filters.sort_created_at = createdSort;
    }
    /** Stage Filter By uses `include_stage` only; never send `sort_que_id`. */
    if (!directoryOpts?.includeStage) {
      const queueSort = this.resolveWorksheetFacetDirectorySort(
        directoryOpts?.sortQueue,
        this.worksheetQueueDirectorySort,
        false
      );
      if (queueSort) {
        filters.sort_que_id = queueSort;
      }
    }
    /** Source Filter By uses `include_source` only; never send `sort_source`. */
    if (!directoryOpts?.includeSource) {
      const refSort = this.resolveWorksheetFacetDirectorySort(
        directoryOpts?.sortReferralSource,
        this.worksheetReferralSourceDirectorySort,
        false
      );
      if (refSort) {
        filters.sort_source = refSort;
      }
    }
    /** Patient Status Filter By uses `include_patient_status` only; never send `sort_lead_status`. */
    if (!directoryOpts?.includePatientStatus) {
      const leadSort = this.resolveWorksheetFacetDirectorySort(
        directoryOpts?.sortLeadStatus,
        this.worksheetPatientStatusDirectorySort,
        false
      );
      if (leadSort) {
        filters.sort_lead_status = leadSort;
      }
    }
    /** Encounter status filter uses `encounter_status` only; do not default `sort_encounter_status=ASC` when narrowing. */
    const encSort = this.resolveWorksheetFacetDirectorySort(
      directoryOpts?.sortEncounterStatus,
      this.worksheetEncounterStatusDirectorySort,
      false
    );
    if (encSort) {
      filters.sort_encounter_status = encSort;
    }
    /** Visit type filter uses `visit_type` only; do not default `sort_visit_type=ASC` when narrowing. */
    const vtSort = this.resolveWorksheetFacetDirectorySort(
      directoryOpts?.sortVisitType,
      this.worksheetVisitTypeDirectorySort,
      false
    );
    if (vtSort) {
      filters.sort_visit_type = vtSort;
    }
    /** Service Filter By uses `include_last_service` only; never send `sort_service`. */
    if (!directoryOpts?.includeLastService) {
      const svcSort = this.resolveWorksheetFacetDirectorySort(
        directoryOpts?.sortService,
        this.worksheetServiceDirectorySort,
        false
      );
      if (svcSort) {
        filters.sort_service = svcSort;
      }
    }
    const resSort = this.resolveWorksheetFacetDirectorySort(
      directoryOpts?.sortScheduleResource,
      this.worksheetResourceDirectorySort,
      !!this.resourcesCsvFromLiveFacets()
    );
    if (resSort) {
      filters.sort_resource = resSort;
    }
    const locSort = this.resolveWorksheetFacetDirectorySort(
      directoryOpts?.sortLocation,
      this.worksheetLocationDirectorySort,
      !!this.locationCsvFromLiveFacets()
    );
    if (locSort) {
      filters.sort_location = locSort;
    }
    /** Coordinator Filter By uses `include_assigned_to` only; never send `sort_assigned_to`. */
    if (!directoryOpts?.includeAssignedTo) {
      const coordSort = this.resolveWorksheetFacetDirectorySort(
        directoryOpts?.sortAssignedTo,
        this.worksheetCoordinatorDirectorySort,
        false
      );
      if (coordSort) {
        filters.sort_assigned_to = coordSort;
      }
    }
    if (directoryOpts?.includeFullName) {
      filters.include_full_name = true;
    }
    if (directoryOpts?.includeDos) {
      filters.include_dos = true;
    }
    if (directoryOpts?.includeDob) {
      filters.include_dob = true;
    }
    if (directoryOpts?.includeCreated) {
      filters.include_created = true;
    }
    if (directoryOpts?.includeLastActivity) {
      filters.include_last_activity = true;
    }
    if (directoryOpts?.includeQueue) {
      filters.include_queue = true;
    }
    if (directoryOpts?.includeStage) {
      filters.include_stage = true;
    }
    if (directoryOpts?.includeReferralSource) {
      filters.include_referral_source = true;
    }
    if (directoryOpts?.includeSource) {
      filters.include_source = true;
    }
    if (directoryOpts?.includeLeadStatus) {
      filters.include_lead_status = true;
    }
    if (directoryOpts?.includePatientStatus) {
      filters.include_patient_status = true;
    }
    if (directoryOpts?.includeWorkflow) {
      filters.include_workflow = true;
    }
    if (directoryOpts?.includeEncounterStatus) {
      filters.include_encounter_status = true;
    }
    if (directoryOpts?.includeVisitType) {
      filters.include_visit_type = true;
    }
    if (directoryOpts?.includeService) {
      filters.include_service = true;
    }
    if (directoryOpts?.includeLastService) {
      filters.include_last_service = true;
    }
    if (directoryOpts?.includeScheduleResource) {
      filters.include_schedule_resource = true;
    }
    if (directoryOpts?.includeLocation) {
      filters.include_location = true;
    }
    if (directoryOpts?.includeAssignedTo) {
      filters.include_assigned_to = true;
    }
    if (directoryOpts?.includePrimaryPayerName) {
      filters.include_primary_payer_name = true;
    }
    if (directoryOpts?.includeEligibility) {
      filters.include_eligibility = true;
    }
    if (directoryOpts?.includeAuthorizationStatus) {
      filters.include_authorization_status = true;
    }
    if (directoryOpts?.includeClaimStatus) {
      filters.include_claim_status = true;
    }
  }

  /**
   * PHI directory biller facets on the patient's latest encounter (`primary_payer_name`,
   * `eligibility`, `authorization_status`, `claim_status`).
   */
  private mergeWorklistBillerFacetFiltersIntoPatientDirectoryFilters(
    filters: PatientDirectoryFilterValues,
    gridOverride?: WorksheetGridClientFilters | null,
    directoryOpts?: WorklistDirectoryPageFetchOpts
  ): void {
    const g = gridOverride ?? this.worksheetGridText;
    const payerCsv = (g?.payerContains ?? "").trim();
    if (payerCsv) {
      filters.primary_payer_name = payerCsv;
    }
    const eligibilityCsv = (g?.eligibilityContains ?? "").trim();
    if (eligibilityCsv) {
      filters.eligibility = eligibilityCsv;
    }
    const authCsv = (g?.authStatusContains ?? "").trim();
    if (authCsv) {
      filters.authorization_status = authCsv;
    }
    const claimCsv = (g?.claimStatusContains ?? "").trim();
    if (claimCsv) {
      filters.claim_status = claimCsv;
    }
  }

  private mergeWorklistPatientIdDirectoryFilter(filters: PatientDirectoryFilterValues): void {
    const patientId = (this._filterService.getWorklistEngagePatientIdFilter() ?? "").trim();
    if (patientId) {
      filters.patientId = patientId;
    }
  }

  private worklistPlainReferralsCacheKey(
    page: number,
    directoryOpts?: WorklistDirectoryPageFetchOpts
  ): string {
    const sortTag = this.worklistDirectorySortCacheTags(directoryOpts);
    const fullNameTag = directoryOpts?.includeFullName ? "IFN" : "";
    const dosTag = directoryOpts?.includeDos ? "IDOS" : "";
    const dobTag = directoryOpts?.includeDob ? "IDOB" : "";
    const createdTag = directoryOpts?.includeCreated ? "ICR" : "";
    const lastActTag = directoryOpts?.includeLastActivity ? "ILA" : "";
    const queueTag = directoryOpts?.includeQueue ? "IQU" : "";
    const stageTag = directoryOpts?.includeStage ? "ISTG" : "";
    const refTag = directoryOpts?.includeReferralSource ? "IRS" : "";
    const srcTag = directoryOpts?.includeSource ? "ISRC" : "";
    const leadTag = directoryOpts?.includeLeadStatus ? "ILS" : "";
    const ptTag = directoryOpts?.includePatientStatus ? "IPS" : "";
    const wfTag = directoryOpts?.includeWorkflow ? "IWF" : "";
    const encTag = directoryOpts?.includeEncounterStatus ? "IES" : "";
    const vtTag = directoryOpts?.includeVisitType ? "IVT" : "";
    const svcTag = directoryOpts?.includeService ? "ISV" : "";
    const lastSvcTag = directoryOpts?.includeLastService ? "ILS" : "";
    const resTag = directoryOpts?.includeScheduleResource ? "IRE" : "";
    const locTag = directoryOpts?.includeLocation ? "ILC" : "";
    const coordTag = directoryOpts?.includeAssignedTo ? "IAT" : "";
    const payerTag = directoryOpts?.includePrimaryPayerName ? "IPN" : "";
    const eligTag = directoryOpts?.includeEligibility ? "IEL" : "";
    const authTag = directoryOpts?.includeAuthorizationStatus ? "IAS" : "";
    const claimTag = directoryOpts?.includeClaimStatus ? "ICS" : "";
    const limitTag =
      directoryOpts?.pageLimit != null
        ? `L${resolveWorklistDirectoryPageLimit(directoryOpts)}`
        : "";
    const stageGroupedTag = directoryOpts?.includeStageGrouped ? "ISG" : "";
    const overrideQueTag = directoryOpts?.overrideQueId
      ? `OQ${String(directoryOpts.overrideQueId).trim()}`
      : "";
    return `${page}\u001f${this.worklistPlainReferralsQueryCacheSignature()}\u001f${sortTag}${fullNameTag}${dosTag}${dobTag}${createdTag}${lastActTag}${queueTag}${stageTag}${refTag}${srcTag}${leadTag}${ptTag}${wfTag}${encTag}${vtTag}${svcTag}${lastSvcTag}${resTag}${locTag}${coordTag}${payerTag}${eligTag}${authTag}${claimTag}${limitTag}${stageGroupedTag}${overrideQueTag}`;
  }

  /**
   * PHI `GET api/v1/patients/directory` query filters — referral facets (`referral_source`, `lead_status`, `que_id`,
   * notifications, `full_names`, `engage_search`, Created `startDate`/`endDate`) plus encounter facets (`dos_start`/`dos_end`,
   * `encounter_status`, `visit_type`, `location`, `schedule_resource`, `providers` when live).
   */
  private mergeWorksheetCreatedUtcBoundsIntoPatientDirectoryFilters(
    filters: PatientDirectoryFilterValues,
    bounds: { startIso: string; endIso: string } | null
  ): void {
    if (!bounds) {
      return;
    }
    filters.startDate = bounds.startIso;
    filters.endDate = bounds.endIso;
  }

  /**
   * Maps worksheet facets → PHI `GET …/patients/directory` params (`dos_start` / `dos_end`, `encounter_status`,
   * `visit_type`, `location` (display names), `schedule_resource`, optional `providers`). Referral created-on stays on `startDate` / `endDate`.
   */
  private mergeWorklistDirectoryEncounterFacetFiltersIntoPatientDirectoryFilters(
    filters: PatientDirectoryFilterValues,
    snapshot: WorksheetFilterSnapshot | null | undefined,
    directoryOpts?: WorklistDirectoryPageFetchOpts
  ): void {
    const gridText = snapshot?.gridText ?? this.worksheetGridText;
    if (isWorksheetDosBlankFilterValue(gridText?.dosContains)) {
      if (!directoryOpts?.includeDos) {
        filters.last_dos = WORKSHEET_DOS_BLANK_DIRECTORY_QUERY;
      }
    } else {
      const dosBounds = snapshot
        ? this.worksheetSnapshotDosYyyyMmDdBounds(snapshot)
        : this.liveWorksheetDosYyyyMmDdBounds();
      if (dosBounds && !directoryOpts?.includeDos) {
        filters.dos_start = dosBounds.start;
        filters.dos_end = dosBounds.end;
      }
    }

    const encCsv = snapshot
      ? this.encounterStatusCsvFromSnapshot(snapshot)
      : this.encounterStatusCsvFromLiveFacets();
    if (encCsv) {
      filters.encounter_status = encCsv;
    }

    const vtCsv = snapshot
      ? this.visitTypeCsvFromSnapshot(snapshot)
      : this.visitTypeCsvFromLiveFacets();
    if (vtCsv) {
      filters.visit_type = vtCsv;
    }

    const svcCsv = snapshot
      ? this.serviceCsvFromSnapshot(snapshot)
      : this.serviceCsvFromLiveFacets();
    if (svcCsv) {
      filters.service = svcCsv;
    }

    const locCsv = snapshot
      ? this.locationCsvFromSnapshot(snapshot)
      : this.locationCsvFromLiveFacets();
    if (locCsv) {
      filters.location = locCsv;
    }

    const resCsv = snapshot
      ? this.resourcesCsvFromSnapshot(snapshot)
      : this.resourcesCsvFromLiveFacets();
    if (resCsv) {
      filters.schedule_resource = resCsv;
    }

    if (!snapshot) {
      const provCsv = this.physicianFilter
        .filter((p) => p.value !== "All" && p.isSelected)
        .map((p) => String(p.value ?? "").trim())
        .filter(Boolean)
        .join(",");
      if (provCsv) {
        filters.providers = provCsv;
      }
    }
  }

  private worksheetSnapshotDosYyyyMmDdBounds(
    snapshot: WorksheetFilterSnapshot
  ): { start: string; end: string } | null {
    const start = this.isoOrYmdToYyyyMmDd(snapshot.filterDate);
    if (start) {
      const endRaw = this.isoOrYmdToYyyyMmDd(snapshot.filterEndDate ?? "");
      const end = endRaw && endRaw !== start ? endRaw : start;
      const lo = start <= end ? start : end;
      const hi = start <= end ? end : start;
      return { start: lo, end: hi };
    }
    return this.dayKeysCsvToYyyyMmDdBounds(snapshot.gridText?.dosDayKeysContains);
  }

  private liveWorksheetDosYyyyMmDdBounds(): { start: string; end: string } | null {
    if (isWorksheetDosBlankFilterValue(this.worksheetGridText?.dosContains)) {
      return null;
    }
    if (this.worksheetUserCommittedDosRange) {
      const dr = this.selectedDate;
      if (!dr?.startDate || !dr?.endDate) {
        return null;
      }
      const s = this.dateToLocalYyyyMmDd(dr.startDate);
      const e = this.dateToLocalYyyyMmDd(dr.endDate);
      if (!s || !e) {
        return null;
      }
      const lo = s <= e ? s : e;
      const hi = s <= e ? e : s;
      return { start: lo, end: hi };
    }
    return this.dayKeysCsvToYyyyMmDdBounds(this.worksheetGridText?.dosDayKeysContains);
  }

  /**
   * Column checkbox day picks (comma-separated `yyyy-MM-dd`) → inclusive min / max calendar day.
   * Used for DOS (`dos_start`/`dos_end`), Created (`startDate`/`endDate`), Last activity (`activity_date_*`).
   */
  private dayKeysCsvToYyyyMmDdBounds(
    raw: string | null | undefined
  ): { start: string; end: string } | null {
    const s = (raw ?? "").toString().trim();
    if (!s) {
      return null;
    }
    const parts = s
      .split(",")
      .map((x) => x.trim())
      .filter((x) => /^\d{4}-\d{2}-\d{2}$/.test(x));
    if (!parts.length) {
      return null;
    }
    const sorted = [...parts].sort();
    return { start: sorted[0], end: sorted[sorted.length - 1] };
  }

  /** Accepts ISO datetimes or bare yyyy-MM-dd (saved views / calendar). */
  private isoOrYmdToYyyyMmDd(raw: string | null | undefined): string {
    const t = (raw ?? "").trim();
    if (!t) {
      return "";
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(t)) {
      return t;
    }
    const d = new Date(t);
    if (Number.isNaN(d.getTime())) {
      return "";
    }
    return this.dateToLocalYyyyMmDd(d);
  }

  private dateToLocalYyyyMmDd(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }

  private encounterStatusCsvFromSnapshot(snapshot: WorksheetFilterSnapshot): string {
    const encNorm = this.normalizeEncounterStatusListFromSnapshot(snapshot.encounerStatusList ?? []);
    return encNorm
      .filter((p) => p.value !== "All" && p.isSelected)
      .map((p) =>
        patientDirectoryEncounterStatusQueryValue(String(p.value ?? "").trim())
      )
      .filter(Boolean)
      .join(",");
  }

  private encounterStatusCsvFromLiveFacets(): string {
    return this.encounerStatusList
      .filter((p) => p.value !== "All" && p.isSelected)
      .map((p) =>
        patientDirectoryEncounterStatusQueryValue(String(p.value ?? "").trim())
      )
      .filter(Boolean)
      .join(",");
  }

  private visitTypeCsvFromSnapshot(snapshot: WorksheetFilterSnapshot): string {
    return (snapshot.visitTypeFilter ?? [])
      .filter((v) => v.value !== "All" && v.isSelected)
      .map((v) => patientDirectoryVisitTypeQueryValue(String(v.value ?? "").trim()))
      .filter(Boolean)
      .join(",");
  }

  private visitTypeCsvFromLiveFacets(): string {
    return this.visitTypeFilter
      .filter((v) => v.value !== "All" && v.isSelected)
      .map((v) => patientDirectoryVisitTypeQueryValue(String(v.value ?? "").trim()))
      .filter(Boolean)
      .join(",");
  }

  private serviceCsvFromSnapshot(snapshot: WorksheetFilterSnapshot): string {
    return (snapshot.serviceFilter ?? [])
      .filter((v) => v.value !== "All" && v.isSelected)
      .map((v) => patientDirectoryServiceQueryValue(String(v.value ?? "").trim()))
      .filter(Boolean)
      .join(",");
  }

  private serviceCsvFromLiveFacets(): string {
    return this.serviceFilter
      .filter((v) => v.value !== "All" && v.isSelected)
      .map((v) => patientDirectoryServiceQueryValue(String(v.value ?? "").trim()))
      .filter(Boolean)
      .join(",");
  }

  /** PHI directory `location` query — human-readable room names (not ids). */
  private worksheetLocationDirectoryQueryName(
    row: SelectableEnumerationItem
  ): string {
    const value = String(row.value ?? "").trim();
    const mapped = patientDirectoryLocationQueryValue(value);
    if (mapped === "no_location") {
      return mapped;
    }
    const display = String(row.displayName ?? "").trim();
    return display || value;
  }

  private locationCsvFromSnapshot(snapshot: WorksheetFilterSnapshot): string {
    return (snapshot.locationFilter ?? [])
      .filter((l) => l.value !== "All" && l.isSelected)
      .map((l) => this.worksheetLocationDirectoryQueryName(l))
      .filter(Boolean)
      .join(",");
  }

  private locationCsvFromLiveFacets(): string {
    return this.locationFilter
      .filter((l) => l.value !== "All" && l.isSelected)
      .map((l) => this.worksheetLocationDirectoryQueryName(l))
      .filter(Boolean)
      .join(",");
  }

  private worksheetReferralSourceDirectoryQueryValue(id: string): string {
    const mapped = worksheetBlankFacetDirectoryQueryValue("source", id);
    if (mapped === WORKSHEET_SOURCE_BLANK_DIRECTORY_QUERY) {
      return mapped;
    }
    return patientDirectoryReferralSourceQueryValue(id) || String(id ?? "").trim().toLowerCase();
  }

  private resourcesCsvFromSnapshot(snapshot: WorksheetFilterSnapshot): string {
    return (snapshot.resourcesFilter ?? [])
      .filter((r) => r.value !== "All" && r.isSelected)
      .map((r) =>
        patientDirectoryScheduleResourceQueryValue(String(r.value ?? "").trim())
      )
      .filter(Boolean)
      .join(",");
  }

  private resourcesCsvFromLiveFacets(): string {
    return this.resourcesFilter
      .filter((r) => r.value !== "All" && r.isSelected)
      .map((r) =>
        patientDirectoryScheduleResourceQueryValue(String(r.value ?? "").trim())
      )
      .filter(Boolean)
      .join(",");
  }

  private buildWorklistPlainPatientDirectoryFilters(
    directoryOpts?: WorklistDirectoryPageFetchOpts
  ): PatientDirectoryFilterValues {
    const filters: PatientDirectoryFilterValues = {};
    const referralSourceIds = this.getActiveReferralSourceValues()
      .map((s) => this.worksheetReferralSourceDirectoryQueryValue(s))
      .filter((v) => v.length > 0);
    if (referralSourceIds.length > 0) {
      filters.referral_source = referralSourceIds.join(",");
    }
    const leadStatuses = this.getPatientDirectoryLeadStatusQueryParams();
    if (leadStatuses.length > 0) {
      filters.lead_status = leadStatuses.join(",");
    }
    const workFlowId = this.getWorklistWorkFlowIdQueryParam();
    if (workFlowId) {
      filters.workFlowId = workFlowId;
    }
    const queIds = directoryOpts?.overrideQueId
      ? [
          patientDirectoryQueIdQueryValue(directoryOpts.overrideQueId) ||
            String(directoryOpts.overrideQueId).trim(),
        ].filter(Boolean)
      : this.getWorklistReferralsQueIdQueryParams();
    if (queIds.length > 0 && !directoryOpts?.includeQueue) {
      filters.que_id = queIds.join(",");
    }
    for (const param of this.getWorklistReferralNotificationQueryParams()) {
      filters[param] = true;
    }
    if (!directoryOpts?.includeFullName) {
      const fullNames = this.getWorklistReferralsFullNameQueryParams();
      if (fullNames.length > 0) {
        filters.full_names = fullNames;
      }
    }
    const engageSearch = (this.searchQuery ?? "").trim();
    if (engageSearch) {
      filters.engage_search = engageSearch;
    }
    if (!directoryOpts?.includeCreated) {
      this.mergeWorksheetCreatedUtcBoundsIntoPatientDirectoryFilters(
        filters,
        this.getWorksheetCreatedOnUtcIsoBounds()
      );
    }
    if (!directoryOpts?.includeLastActivity) {
      this.mergeWorksheetActivityDateIntoPatientDirectoryFilters(filters);
    }
    if (!directoryOpts?.includeDob) {
      this.mergeWorksheetDobBoundsIntoPatientDirectoryFilters(filters);
    }
    this.mergeWorklistDirectoryEncounterFacetFiltersIntoPatientDirectoryFilters(
      filters,
      null,
      directoryOpts
    );
    this.mergeWorklistPatientIdDirectoryFilter(filters);
    const assignedTo = patientDirectoryAssignedToQueryValue(
      this.worksheetGridText?.coordinatorContains
    );
    if (assignedTo) {
      filters.assigned_to = assignedTo;
    }
    this.mergeWorklistBillerFacetFiltersIntoPatientDirectoryFilters(
      filters,
      null,
      directoryOpts
    );
    this.mergeWorklistDirectorySortQueryParams(filters, directoryOpts);
    if (directoryOpts?.includeStageGrouped) {
      filters.include_stage = true;
    }
    return filters;
  }

  shouldSkipWorklistFilteredDirectoryFollowUps(): boolean {
    return (
      this.worklistMergeEngageProspects &&
      Object.keys(this.buildWorklistPlainPatientDirectoryFilters()).length > 0
    );
  }

  /** UTC ISO bounds for worksheet Created-on filter as directory GET `startDate` / `endDate`. */
  private getWorksheetCreatedOnUtcIsoBounds(): { startIso: string; endIso: string } | null {
    const fromSheet = AdmissionsService.getWorksheetCreatedOnUtcIsoBoundsFromCalendarDays(
      this.worksheetCreatedOnIso,
      this.worksheetCreatedOnEndIso
    );
    if (fromSheet) {
      return fromSheet;
    }
    const fromCheckboxes = this.dayKeysCsvToYyyyMmDdBounds(
      this.worksheetGridText?.createdDayKeysContains
    );
    if (!fromCheckboxes) {
      return null;
    }
    return AdmissionsService.getWorksheetCreatedOnUtcIsoBoundsFromCalendarDays(
      fromCheckboxes.start,
      fromCheckboxes.end
    );
  }

  /** PHI directory `dob=no_dob` or `dob_start` / `dob_end` from the DOB column filter. */
  private mergeWorksheetDobBoundsIntoPatientDirectoryFilters(
    filters: PatientDirectoryFilterValues,
    gridOverride?: WorksheetGridClientFilters | null
  ): void {
    const g = gridOverride ?? this.worksheetGridText;
    if (isWorksheetDobBlankFilterValue(g?.dobContains)) {
      filters.dob = WORKSHEET_DOB_BLANK_DIRECTORY_QUERY;
      return;
    }
    const bounds = this.dayKeysCsvToYyyyMmDdBounds(g?.dobDayKeysContains);
    if (!bounds) {
      return;
    }
    filters.dob_start = bounds.start;
    filters.dob_end = bounds.end;
  }

  /** PHI directory `activity_date_start` / `activity_date_end` (yyyy-MM-dd). */
  private mergeWorksheetActivityDateIntoPatientDirectoryFilters(
    filters: PatientDirectoryFilterValues
  ): void {
    const bounds = this.getWorksheetActivityDateYyyyMmDdBoundsForLive();
    if (!bounds) {
      return;
    }
    filters.activity_date_start = bounds.start;
    filters.activity_date_end = bounds.end;
  }

  private getWorksheetActivityDateYyyyMmDdBoundsForLive(): {
    start: string;
    end: string;
  } | null {
    const fromSheet = AdmissionsService.getWorksheetActivityDateYyyyMmDdBounds(
      this.worksheetActivityDateStartIso,
      this.worksheetActivityDateEndIso
    );
    if (fromSheet) {
      return fromSheet;
    }
    return this.dayKeysCsvToYyyyMmDdBounds(this.worksheetGridText?.updatedDayKeysContains);
  }

  private worklistCreatedFilterCacheKey(): string {
    const cd0 = (this.worksheetCreatedOnIso ?? "").trim().slice(0, 10);
    const cd1 = (this.worksheetCreatedOnEndIso ?? "").trim().slice(0, 10);
    if (cd0) {
      return `${cd0}\u0003${cd1}`;
    }
    const b = this.dayKeysCsvToYyyyMmDdBounds(this.worksheetGridText?.createdDayKeysContains);
    return b ? `${b.start}\u0003${b.end}` : "\u0003";
  }

  private worklistActivityFilterCacheKey(): string {
    const ad0 = (this.worksheetActivityDateStartIso ?? "").trim().slice(0, 10);
    const ad1 = (this.worksheetActivityDateEndIso ?? "").trim().slice(0, 10);
    if (ad0) {
      return `${ad0}\u0003${ad1}`;
    }
    const b = this.dayKeysCsvToYyyyMmDdBounds(this.worksheetGridText?.updatedDayKeysContains);
    return b ? `${b.start}\u0003${b.end}` : "\u0003";
  }

  private static getWorksheetActivityDateYyyyMmDdBounds(
    startIso: string | null | undefined,
    endIso: string | null | undefined
  ): { start: string; end: string } | null {
    const startDay = (startIso ?? "").trim().slice(0, 10);
    if (!startDay || !/^\d{4}-\d{2}-\d{2}$/.test(startDay)) {
      return null;
    }
    const rawEnd = (endIso ?? "").trim().slice(0, 10);
    const endDay =
      rawEnd && /^\d{4}-\d{2}-\d{2}$/.test(rawEnd) ? rawEnd : startDay;
    const lo = startDay <= endDay ? startDay : endDay;
    const hi = startDay <= endDay ? endDay : startDay;
    return { start: lo, end: hi };
  }

  private static getWorksheetCreatedOnUtcIsoBoundsFromCalendarDays(
    createdOnIso: string | null | undefined,
    createdOnEndIso: string | null | undefined
  ): { startIso: string; endIso: string } | null {
    const startDay = (createdOnIso ?? "").trim().slice(0, 10);
    if (!startDay || !/^\d{4}-\d{2}-\d{2}$/.test(startDay)) {
      return null;
    }
    const rawEnd = (createdOnEndIso ?? "").trim().slice(0, 10);
    const endDay =
      rawEnd && /^\d{4}-\d{2}-\d{2}$/.test(rawEnd) ? rawEnd : startDay;
    const lo = startDay <= endDay ? startDay : endDay;
    const hi = startDay <= endDay ? endDay : startDay;
    const startIso = DateTime.fromISO(lo, { zone: "local" }).startOf("day").toUTC().toISO();
    const endIso = DateTime.fromISO(hi, { zone: "local" }).endOf("day").toUTC().toISO();
    if (!startIso || !endIso) {
      return null;
    }
    return { startIso, endIso };
  }

  /**
   * One PHI `GET …/patients/directory?page=1&limit=1` using **only** a saved view snapshot’s query filters
   * (same shape as {@link buildWorklistPlainPatientDirectoryFiltersFromSnapshot}).
   * Used for per–saved-view **total** badges without loading merged grid rows.
   */
  fetchEngageReferralsFilteredCountOnlyForWorksheetSnapshot(
    snapshot: WorksheetFilterSnapshot
  ): Observable<number> {
    const base = phiPatientDirectoryListGetUrl(1, 1);
    const filters = this.buildWorklistPlainPatientDirectoryFiltersFromSnapshot(snapshot);
    const url = appendPatientDirectoryFiltersToUrl(base, filters);
    return this._dataService.get(url).pipe(
      map((res: unknown) => {
        const parsed = parsePatientDirectoryOrLegacyReferralsPayload(res, 1, 1);
        const t = parsed.total;
        return typeof t === "number" && Number.isFinite(t) ? t : 0;
      }),
      catchError(() => of(0))
    );
  }

  private normalizeWorksheetSnapshotGridText(
    snapshot: WorksheetFilterSnapshot
  ): WorksheetGridClientFilters {
    const gt: WorksheetGridClientFilters = {
      ...emptyWorksheetGridClientFilters(),
      ...(snapshot.gridText ?? {}),
    };
    gt.dobContains = "";
    const gridOpt = (v: unknown): string | null => {
      const s = v == null ? "" : String(v).trim();
      return s === "" ? null : s;
    };
    gt.queueContains = gridOpt(gt.queueContains);
    gt.namePatientKeysContains = gridOpt(gt.namePatientKeysContains);
    gt.refStatusContains = gridOpt(gt.refStatusContains);
    gt.payerContains = gridOpt(gt.payerContains);
    gt.eligibilityContains = gridOpt(gt.eligibilityContains);
    gt.coordinatorContains = gridOpt(gt.coordinatorContains);
    gt.claimStatusContains = gridOpt(gt.claimStatusContains);
    return gt;
  }

  private normalizeEncounterStatusListFromSnapshot(
    encListIn: SelectableEnumerationItem[]
  ): SelectableEnumerationItem[] {
    const selEnc = encListIn.filter((s) => s.isSelected);
    return selEnc.length > 1
      ? encListIn.map((s) => ({
          ...s,
          isSelected: String(s.value ?? "") === String(selEnc[0].value ?? ""),
        }))
      : encListIn;
  }

  private referralSourceValuesFromSnapshot(snapshot: WorksheetFilterSnapshot): string[] {
    const normalizeReferralSourceId = (v: string): string =>
      v.trim().toLowerCase() === "meta" ? "online" : v.trim();
    const fromList = snapshot.referralSourceFilterList
      .filter((x) => x.value !== "All" && x.isSelected)
      .map((x) => normalizeReferralSourceId(String(x.value ?? "")))
      .filter((v) => v.length > 0);
    return normalizeActiveReferralSourceFilterValues(fromList);
  }

  private leadStatusQueryParamsFromSnapshot(snapshot: WorksheetFilterSnapshot): string[] {
    const gt = this.normalizeWorksheetSnapshotGridText(snapshot);
    const raw = [(gt.refStatusContains ?? "").toString().trim()].filter(Boolean);
    const seen = new Set<string>();
    const out: string[] = [];
    for (const s of raw) {
      for (const part of s.split(",")) {
        const trimmed = part.trim();
        if (!trimmed) {
          continue;
        }
        const mapped = patientDirectoryLeadStatusQueryValue(trimmed);
        const k = mapped.toLowerCase();
        if (seen.has(k)) {
          continue;
        }
        seen.add(k);
        out.push(k);
      }
    }
    return out;
  }

  private queIdQueryParamsFromSnapshot(snapshot: WorksheetFilterSnapshot): string[] {
    const gt = this.normalizeWorksheetSnapshotGridText(snapshot);
    const raw = (gt.queueContains ?? "").toString().trim();
    if (!raw) {
      return [];
    }
    const seen = new Set<string>();
    const out: string[] = [];
    for (const part of raw.split(",")) {
      const s = part.trim();
      if (!s) {
        continue;
      }
      const mapped = patientDirectoryQueIdQueryValue(s);
      if (!mapped || seen.has(mapped)) {
        continue;
      }
      seen.add(mapped);
      out.push(mapped);
    }
    return out;
  }

  private referralNotificationQueryParamsFromSnapshot(
    snapshot: WorksheetFilterSnapshot
  ): string[] {
    const gt = this.normalizeWorksheetSnapshotGridText(snapshot);
    const raw = gt.referralNotificationFilters;
    const list = Array.isArray(raw) ? raw : [];
    const seen = new Set<string>();
    const out: string[] = [];
    for (const x of list) {
      const k = String(x ?? "").trim();
      if (!k || seen.has(k) || !this.worklistReferralNotificationAllowed.has(k)) {
        continue;
      }
      seen.add(k);
      out.push(k);
    }
    return out.sort();
  }

  private buildWorklistPlainPatientDirectoryFiltersFromSnapshot(
    snapshot: WorksheetFilterSnapshot
  ): PatientDirectoryFilterValues {
    const filters: PatientDirectoryFilterValues = {};
    const referralSourceIds = this.referralSourceValuesFromSnapshot(snapshot)
      .map((s) => this.worksheetReferralSourceDirectoryQueryValue(s))
      .filter((v) => v.length > 0);
    if (referralSourceIds.length > 0) {
      filters.referral_source = referralSourceIds.join(",");
    }
    const leadStatuses = this.leadStatusQueryParamsFromSnapshot(snapshot);
    if (leadStatuses.length > 0) {
      filters.lead_status = leadStatuses.join(",");
    }
    const workFlowId = this.getWorklistWorkFlowIdQueryParam();
    if (workFlowId) {
      filters.workFlowId = workFlowId;
    }
    const queIds = this.queIdQueryParamsFromSnapshot(snapshot);
    if (queIds.length > 0) {
      filters.que_id = queIds.join(",");
    }
    for (const param of this.referralNotificationQueryParamsFromSnapshot(snapshot)) {
      filters[param] = true;
    }
    const gt = this.normalizeWorksheetSnapshotGridText(snapshot);
    let createdBounds = AdmissionsService.getWorksheetCreatedOnUtcIsoBoundsFromCalendarDays(
      snapshot.createdOnIso,
      snapshot.createdOnEndIso
    );
    if (!createdBounds) {
      const createdDays = this.dayKeysCsvToYyyyMmDdBounds(gt.createdDayKeysContains);
      if (createdDays) {
        createdBounds = AdmissionsService.getWorksheetCreatedOnUtcIsoBoundsFromCalendarDays(
          createdDays.start,
          createdDays.end
        );
      }
    }
    this.mergeWorksheetCreatedUtcBoundsIntoPatientDirectoryFilters(filters, createdBounds);
    let activityBounds = AdmissionsService.getWorksheetActivityDateYyyyMmDdBounds(
      snapshot.activityDateStartIso,
      snapshot.activityDateEndIso
    );
    if (!activityBounds) {
      activityBounds = this.dayKeysCsvToYyyyMmDdBounds(gt.updatedDayKeysContains);
    }
    if (activityBounds) {
      filters.activity_date_start = activityBounds.start;
      filters.activity_date_end = activityBounds.end;
    }
    this.mergeWorklistDirectoryEncounterFacetFiltersIntoPatientDirectoryFilters(filters, snapshot);
    this.mergeWorksheetDobBoundsIntoPatientDirectoryFilters(filters, gt);
    const assignedTo = patientDirectoryAssignedToQueryValue(gt.coordinatorContains);
    if (assignedTo) {
      filters.assigned_to = assignedTo;
    }
    this.mergeWorklistBillerFacetFiltersIntoPatientDirectoryFilters(filters, gt);
    this.mergeWorklistDirectorySortQueryParams(filters, undefined, gt, snapshot.createdOnIso, snapshot.createdOnEndIso);
    return filters;
  }

  /**
   * When worklist merges via {@link EngageReferralsQueryService}, pass worksheet Created bounds so
   * `startDate` / `endDate` match the plain patient directory GET query from {@link buildWorklistPlainPatientDirectoryFilters}.
   */
  private getWorksheetCreatedDateRangeFilterForEngageQuery(): DateRangeFilter | null {
    const startDay = (this.worksheetCreatedOnIso ?? "").trim().slice(0, 10);
    if (!startDay || !/^\d{4}-\d{2}-\d{2}$/.test(startDay)) {
      return null;
    }
    const rawEnd = (this.worksheetCreatedOnEndIso ?? "").trim().slice(0, 10);
    const endDay =
      rawEnd && /^\d{4}-\d{2}-\d{2}$/.test(rawEnd) ? rawEnd : startDay;
    const lo = startDay <= endDay ? startDay : endDay;
    const hi = startDay <= endDay ? endDay : startDay;
    return {
      startDate: new Date(`${lo}T12:00:00`),
      endDate: new Date(`${hi}T12:00:00`),
    };
  }

  /**
   * ISO bounds for list-clinic / counts when worklist DOS is not committed — month start through
   * end of today (local), aligned with facet hydrate in the filter sheet.
   */
  getWorksheetFacetCountsDateRangeIso(): { startIso: string; endIso: string } {
    const now = new Date();
    const startOfMonth = DateTime.fromJSDate(now).startOf("month").toJSDate();
    return {
      startIso: Utils.getStartOfDayISODate(startOfMonth),
      endIso: Utils.getEndOfDayISODate(now),
    };
  }

  /**
   * Worklist `list-clinic` merge: always request encounters from **2020-01-01** through **end of today**
   * (local) so the clinic payload is not truncated to a narrow month window.
   * Committed sheet DOS still narrows rows client-side via {@link worksheetUserCommittedDosRange}.
   */
  getWorklistListClinicDosDateRangeIso(): { startIso: string; endIso: string } {
    const start2020 = new Date(2020, 0, 1);
    const now = new Date();
    return {
      startIso: Utils.getStartOfDayISODate(start2020),
      endIso: Utils.getEndOfDayISODate(now),
    };
  }

  getAdmissionsCounts(
    startDate: string,
    endDate: string,
    referralSources?: string[],
    userTimeZone?: string,
    providers?: string[],
    locations?: string[]
  ): Observable<AppointmentCountResponse> {
    let url =
      this.apiBaseUrl +
      `api/v1/encounters/referrals/counts?start-date=${encodeURIComponent(startDate)}&end-date=${encodeURIComponent(endDate)}`;
    if (referralSources?.length) {
      url += `&referralSources=${encodeURIComponent(referralSources.join(","))}`;
    }
    if (userTimeZone && String(userTimeZone).trim() !== "") {
      url += `&timeZone=${encodeURIComponent(String(userTimeZone).trim())}`;
    }
    if (providers?.length) {
      url += `&providers=${encodeURIComponent(providers.join(","))}`;
    }
    if (locations?.length) {
      url += `&locations=${encodeURIComponent(locations.join(","))}`;
    }
    return this._dataService.get(url).pipe(
      tap({
        error: (err) => {
},
      })
    );
  }

  /** New Schedule sheet: list-clinic rows for a date range (optional resource/location). */
  getScheduleSheetAppointments(
    startDate: Date,
    endDate: Date,
    resourceId?: string,
    locationName?: string
  ): Observable<Appointment[]> {
    const range: DateRange = { startDate, endDate };
    const payload = new AdmissionListPayload(range);
    const resource = (resourceId || "").trim();
    const location = (locationName || "").trim();
    if (resource) {
      payload.resources = [resource];
    }
    if (location) {
      payload.filterLocation = [location];
    }
    payload.filterStatus = [...SCHEDULE_SHEET_LIST_CLINIC_FILTER_STATUS];
    payload.pageStart = 0;
    payload.pageLength = 500;
    payload.sortField = "encounterDateTime";
    payload.sortDirection = "ASC";
    const url = this.apiBaseUrl + "api/v1/encounters/list-clinic";
    return this._dataService.post(url, payload).pipe(
      map((response: PaginatedAppointmentResponse) => response?.data ?? [])
    );
  }

  /** POST `list-clinic` for scribe with optional server-side filters. */
  getScribeAdmissions(
    page: number = 0,
    pageSize: number = 25,
    filters: ScribeListClinicFilters = {}
  ): Observable<PaginatedAppointmentResponse> {
    const pageConfig: any = {
      providers: filters.providerIds?.length ? filters.providerIds : [],
      resources: filters.resourceIds?.length ? filters.resourceIds : [],
      filterStatus:
        filters.filterStatus?.length
          ? filters.filterStatus
          : getScribeListScheduleStatusFilterValues(),
      filterLocation: [],
      visitType: [],
      pageStart: page * pageSize,
      pageLength: pageSize,
      sortField: filters.sortField ?? "encounterDateTime",
      sortDirection: filters.sortDirection ?? "DESC",
      searchQuery: (filters.searchQuery ?? "").trim(),
    };

    if (filters.serviceIds?.length) {
      pageConfig.service = filters.serviceIds;
    }
    if (filters.claimStatuses?.length) {
      pageConfig.claimStatus = filters.claimStatuses;
    }

    if (filters.startDate || filters.endDate) {
      const startKey = filters.startDate || filters.endDate!;
      const endKey = filters.endDate || filters.startDate!;
      pageConfig.filterDate = {
        startDate: Utils.getStartOfDayISODate(
          this.scribeFilterDateKeyToLocalDate(startKey)
        ),
        endDate: Utils.getEndOfDayISODate(
          this.scribeFilterDateKeyToLocalDate(endKey)
        ),
      };
    }

    if (filters.facetField) {
      // Ask API for distinct column values only (DOB / DOS / Name / …).
      pageConfig.facetField = filters.facetField;
    }

    if (filters.dobs?.length) {
      pageConfig.dobs = [...filters.dobs];
    }
    if (filters.dos?.length) {
      pageConfig.dos = [...filters.dos];
    }
    if (filters.names?.length) {
      pageConfig.names = [...filters.names];
    }
    if (filters.scheduleStatuses?.length) {
      pageConfig.scheduleStatus = [...filters.scheduleStatuses];
    }
    if (filters.signReq?.length) {
      pageConfig.signReq = [...filters.signReq];
    }

    const url = this.apiBaseUrl + "api/v1/encounters/list-clinic";
    return this._dataService.post(url, pageConfig).pipe(
      map((response: PaginatedAppointmentResponse) => {
        const enforceAllowlist = filters.enforceScribeScheduleAllowlist !== false;
        const rawData = response?.data ?? [];
        // Facet-only responses may omit encounter rows — do not wipe the grid.
        if (filters.facetField) {
          return {
            ...response,
            data: rawData,
          };
        }
        const processedData = rawData
          .filter(
            (appt) =>
              this.passesListClinicClientStatusFilter(
                appt,
                pageConfig.filterStatus
              ) && (!enforceAllowlist || isScribeScheduleStatusAllowed(appt))
          )
          .map((encounter: Appointment) => {
            // Combine encounterDateTime and checkIn into a single DateTime
            const date = DateTime.fromISO(encounter.encounterDateTime);
            const checkInTime = encounter.encounterSchedule?.checkIn;

            let combinedDateTime = date;
            if (checkInTime) {
              const [time, period] = checkInTime.split(" ");
              const [hours, minutes] = time.split(":").map(Number);

              // Adjust hours for AM/PM
              const adjustedHours =
                period === "PM" && hours !== 12
                  ? hours + 12
                  : period === "AM" && hours === 12
                  ? 0
                  : hours;

              combinedDateTime = combinedDateTime.set({
                hour: adjustedHours,
                minute: minutes,
              });
            }

            return {
              ...encounter,
              combinedDateTime: combinedDateTime.toISO(), // Add combined DateTime for sorting
            };
          });

        // Update service state
        this.admissionList = processedData;
        // Scribe rows are clinic encounters (not procedure list); clear any
        // stale origin so the list-view getter shows these rows.
        this.admissionListOrigin = null;
        this.currentPage = response.currentPage;
        this.totalRecords = response.totalRowsCount;
        this.totalPages = response.totalPages;
        this.pageSize = response.pageLength;

        // Return the modified response
        return {
          ...response,
          data: processedData
        };
      }),
      tap({
        error: (err) => {
},
      })
    );
  }

  /**
   * list-clinic facet projection: request distinct values for one column.
   * POST body includes `facetField` with the same filters as list-clinic.
   * Does not mutate {@link admissionList}.
   *
   * API response: `{ "facetValues": ["…"] }`
   * - dob / dos / name / service / resource / scheduleStatus
   * Without facetField, list-clinic behavior is unchanged.
   */
  getScribeListClinicFacetOptions(
    facetField: ScribeListClinicFacetField,
    filters: ScribeListClinicFilters = {}
  ): Observable<{ label: string; value: string }[]> {
    const omitResourceNarrowing = facetField === "resource";
    const omitStatusNarrowing = facetField === "scheduleStatus";
    const pageConfig: Record<string, unknown> = {
      providers: omitResourceNarrowing
        ? []
        : filters.providerIds?.length
          ? filters.providerIds
          : [],
      resources: omitResourceNarrowing
        ? []
        : filters.resourceIds?.length
          ? filters.resourceIds
          : [],
      filterStatus: omitStatusNarrowing
        ? getScribeListScheduleStatusFilterValues()
        : filters.filterStatus?.length
          ? filters.filterStatus
          : getScribeListScheduleStatusFilterValues(),
      filterLocation: [],
      visitType: [],
      pageStart: 0,
      pageLength: 10000,
      sortField: filters.sortField ?? "encounterDateTime",
      sortDirection: filters.sortDirection ?? "DESC",
      searchQuery: (filters.searchQuery ?? "").trim(),
      facetField,
    };

    if (facetField !== "service" && filters.serviceIds?.length) {
      pageConfig.service = filters.serviceIds;
    }
    if (filters.claimStatuses?.length) {
      pageConfig.claimStatus = filters.claimStatuses;
    }
    if (filters.startDate || filters.endDate) {
      const startKey = filters.startDate || filters.endDate!;
      const endKey = filters.endDate || filters.startDate!;
      pageConfig.filterDate = {
        startDate: Utils.getStartOfDayISODate(
          this.scribeFilterDateKeyToLocalDate(startKey)
        ),
        endDate: Utils.getEndOfDayISODate(
          this.scribeFilterDateKeyToLocalDate(endKey)
        ),
      };
    }

    // When loading options for one column, omit that column's multi-select
    // so the picker still shows the full set of values.
    if (facetField !== "dob" && filters.dobs?.length) {
      pageConfig.dobs = [...filters.dobs];
    }
    if (facetField !== "dos" && filters.dos?.length) {
      pageConfig.dos = [...filters.dos];
    }
    if (facetField !== "name" && filters.names?.length) {
      pageConfig.names = [...filters.names];
    }
    if (facetField !== "scheduleStatus" && filters.scheduleStatuses?.length) {
      pageConfig.scheduleStatus = [...filters.scheduleStatuses];
    }
    if (filters.signReq?.length) {
      pageConfig.signReq = [...filters.signReq];
    }

    const url = this.apiBaseUrl + "api/v1/encounters/list-clinic";
    return this._dataService.post(url, pageConfig).pipe(
      map((response: ListClinicFacetResponse) =>
        this.mapListClinicFacetResponseToOptions(facetField, response)
      )
    );
  }

  private mapListClinicFacetResponseToOptions(
    facetField: ScribeListClinicFacetField,
    response: ListClinicFacetResponse | null | undefined
  ): { label: string; value: string }[] {
    const rawFromFacet = response?.facetValues ?? null;

    if (Array.isArray(rawFromFacet) && rawFromFacet.length > 0) {
      return this.normalizeListClinicFacetRawValues(facetField, rawFromFacet);
    }

    // Fallback if an older environment still returns encounter rows only.
    return this.deriveScribeFacetOptionsFromRows(
      facetField,
      response?.data ?? []
    );
  }

  private normalizeListClinicFacetRawValues(
    facetField: ScribeListClinicFacetField,
    raw: Array<string | { value?: string; label?: string; name?: string }>
  ): { label: string; value: string }[] {
    const map = new Map<string, string>();
    for (const item of raw) {
      if (item == null) {
        continue;
      }
      const rawValue =
        typeof item === "string"
          ? item.trim()
          : (item.value ?? item.name ?? item.label ?? "").toString().trim();
      if (!rawValue) {
        continue;
      }

      if (facetField === "name") {
        // API returns distinct "First Last"; use display name as value (list-clinic `names`).
        const display =
          typeof item === "string"
            ? rawValue
            : (item.label ?? item.name ?? rawValue).toString().trim() ||
              rawValue;
        const name =
          display.startsWith("n:") ? display.slice(2).trim() || "—" : display;
        const key = name;
        if (!map.has(key)) {
          map.set(key, name);
        }
        continue;
      }

      if (facetField === "dob" || facetField === "dos") {
        const dayKey = this.normalizeListClinicFacetDateKey(rawValue);
        if (!dayKey) {
          continue;
        }
        if (!map.has(dayKey)) {
          map.set(dayKey, this.formatScribeFacetLabel(facetField, dayKey));
        }
        continue;
      }

      if (facetField === "service") {
        const display =
          this._appContextService
            .resolveEncounterServiceDisplayName(rawValue)
            .trim() || rawValue;
        if (!display || this.isProbableUuid(display)) {
          continue;
        }
        if (!map.has(display)) {
          map.set(display, display);
        }
        continue;
      }

      if (facetField === "resource") {
        const display = this.resolveScribeResourceFacetDisplay(rawValue);
        if (!display) {
          continue;
        }
        if (!map.has(display)) {
          map.set(display, display);
        }
        continue;
      }

      if (facetField === "scheduleStatus") {
        if (!map.has(rawValue)) {
          map.set(rawValue, rawValue);
        }
        continue;
      }

      if (!map.has(rawValue)) {
        map.set(rawValue, this.formatScribeFacetLabel(facetField, rawValue));
      }
    }
    return [...map.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
      );
  }

  /** Normalize facet date strings to `yyyy-MM-dd` when possible. */
  private normalizeListClinicFacetDateKey(raw: string): string | null {
    const s = (raw ?? "").trim();
    if (!s) {
      return null;
    }
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
      return s.slice(0, 10);
    }
    const dt = DateTime.fromISO(s, { zone: "local" });
    if (dt.isValid) {
      return dt.toFormat("yyyy-MM-dd");
    }
    const mdY = DateTime.fromFormat(s, "MM/dd/yyyy", { zone: "local" });
    if (mdY.isValid) {
      return mdY.toFormat("yyyy-MM-dd");
    }
    const mdy = DateTime.fromFormat(s, "M/d/yyyy", { zone: "local" });
    if (mdy.isValid) {
      return mdy.toFormat("yyyy-MM-dd");
    }
    return s;
  }

  private formatScribeFacetLabel(
    facetField: ScribeListClinicFacetField,
    value: string
  ): string {
    if (facetField === "dob" || facetField === "dos") {
      const dt = DateTime.fromISO(value, { zone: "local" });
      return dt.isValid ? dt.toFormat("MM/dd/yyyy") : value;
    }
    if (facetField === "name" && value.startsWith("n:")) {
      return value.slice(2).trim() || "—";
    }
    return value;
  }

  private resolveScribeResourceFacetDisplay(raw: string): string {
    const token = (raw ?? "").trim();
    if (!token) {
      return "";
    }
    const ctx = this._appContextService.contextSnapshot;
    const byId = ctx?.resources?.find(
      (r) => (r.id ?? "").trim().toLowerCase() === token.toLowerCase()
    );
    if (byId?.name?.trim()) {
      return byId.name.trim();
    }
    const byName = ctx?.resources?.find(
      (r) => (r.name ?? "").trim().toLowerCase() === token.toLowerCase()
    );
    if (byName?.name?.trim()) {
      return byName.name.trim();
    }
    if (this.isProbableUuid(token)) {
      return "";
    }
    return token;
  }

  private isProbableUuid(value: string): boolean {
    return isValidUuid((value ?? "").trim());
  }

  private deriveScribeFacetOptionsFromRows(
    facetField: ScribeListClinicFacetField,
    rows: Appointment[]
  ): { label: string; value: string }[] {
    const map = new Map<string, string>();
    for (const appt of rows) {
      let value = "";
      let label = "";
      switch (facetField) {
        case "dob": {
          const raw = appt.encountersPatient?.dob;
          value = this.normalizeListClinicFacetDateKey(String(raw ?? "")) ?? "";
          label = this.formatScribeFacetLabel("dob", value);
          break;
        }
        case "dos": {
          const enc = appt.encounterDateTime;
          if (enc) {
            const dt = DateTime.fromISO(String(enc), { zone: "local" });
            value = dt.isValid ? dt.toFormat("yyyy-MM-dd") : "";
            label = dt.isValid ? dt.toFormat("MM/dd/yyyy") : value;
          }
          break;
        }
        case "name": {
          label = (appt.encountersPatient?.name ?? "").trim() || "—";
          value = label;
          break;
        }
        case "service": {
          const raw = (appt.service ?? "").toString().trim();
          label =
            this._appContextService
              .resolveEncounterServiceDisplayName(raw)
              .trim() || raw;
          if (!label || this.isProbableUuid(label)) {
            break;
          }
          value = label;
          break;
        }
        case "resource": {
          label = resolveScribeScheduleResourceDisplay(appt, {
            resources: this._appContextService.contextSnapshot?.resources ?? [],
            users: this._appContextService.contextSnapshot?.allAvailableUsers ?? [],
          }).trim();
          value = label;
          break;
        }
        case "scheduleStatus": {
          label = resolveScribeScheduleStatusFromAppointment(appt).trim();
          value = label;
          break;
        }
        default:
          break;
      }
      if (value && !map.has(value)) {
        map.set(value, label || value);
      }
    }
    return [...map.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
      );
  }

  /** Back-compat wrapper for scribe initial load (single provider, no filters). */
  getAllAdmissionsForScribe(
    page: number = 0,
    pageSize: number = 25,
    providerId?: string
  ): Observable<PaginatedAppointmentResponse> {
    return this.getScribeAdmissions(page, pageSize, {
      providerIds: providerId ? [providerId] : [],
    });
  }

  /** Parse yyyy-MM-dd filter keys as local calendar dates (no UTC shift). */
  private scribeFilterDateKeyToLocalDate(dateKey: string): Date {
    const [year, month, day] = (dateKey ?? "").split("-").map(Number);
    return new Date(year, (month || 1) - 1, day || 1);
  }

  // Clear scribe cache when needed (no longer needed but keeping for compatibility)
  clearScribeCache(): void {
    // No cache to clear with backend pagination
  }

  /** DOS window for a procedure-list tab (unless the filters sheet committed a custom range). */
  getProcedureListTabDateRange(tabId: ProcedureListTabId = this.procedureListActiveTabId): DateRange {
    const now = DateTime.now();
    if (tabId === "today") {
      return {
        startDate: now.startOf("day").toJSDate(),
        endDate: now.endOf("day").toJSDate(),
      };
    }
    return {
      startDate: DateTime.fromObject({ year: 2020, month: 1, day: 1 })
        .startOf("day")
        .toJSDate(),
      endDate: now.endOf("day").toJSDate(),
    };
  }

  /** Encounter-status facet for a procedure-list tab (`list-clinic` `filterStatus`). */
  getProcedureListTabStatusFilter(tabId: ProcedureListTabId = this.procedureListActiveTabId): string[] {
    switch (tabId) {
      case "open":
        return ["Open"];
      case "sign_req":
        return ["Sign Req"];
      case "retro_auth":
        return ["Retro Auth"];
      case "ready_billing":
        return ["Ready for Billing"];
      default:
        return [];
    }
  }

  clearProcedureListSheetFilters(): void {
    this.procedureListSheetFiltersCommitted = false;
    this.procedureListSheetFilters = {
      ...DEFAULT_PROCEDURE_LIST_SHEET_FILTERS,
      serviceIds: [],
      referralSourceIds: [],
    };
  }

  commitProcedureListSheetFilters(filters: ProcedureListSheetFilters): void {
    this.procedureListSheetFilters = {
      ...filters,
      serviceIds: [...(filters.serviceIds ?? [])],
      referralSourceIds: [...(filters.referralSourceIds ?? [])],
    };
    this.procedureListSheetFiltersCommitted = true;
  }

  getProcedureListAppliedFilterCount(): number {
    if (!this.procedureListSheetFiltersCommitted) {
      return 0;
    }
    const f = this.procedureListSheetFilters;
    let n = 0;
    if ((f.providerId ?? "").trim()) {
      n += 1;
    }
    if (f.statusId) {
      n += 1;
    }
    if (f.datePreset && f.datePreset !== "all") {
      n += 1;
    }
    if ((f.serviceIds ?? []).length) {
      n += 1;
    }
    if ((f.referralSourceIds ?? []).length) {
      n += 1;
    }
    return n;
  }

  /** `list-clinic` `filterStatus` from the filter drawer when a status is selected; otherwise tab facet. */
  getProcedureListEncounterStatusFilter(
    tabId: ProcedureListTabId = this.procedureListActiveTabId
  ): string[] {
    if (this.procedureListSheetFiltersCommitted && this.procedureListSheetFilters.statusId) {
      return this.mapProcedureListFilterStatusToApi(
        this.procedureListSheetFilters.statusId
      );
    }
    return this.getProcedureListTabStatusFilter(tabId);
  }

  private mapProcedureListFilterStatusToApi(
    statusId: ProcedureListFilterStatusId
  ): string[] {
    switch (statusId) {
      case "open":
        return ["Open"];
      case "missing_signatures":
        return ["Sign Req"];
      case "upcoming":
        return ["Scheduled"];
      case "missing_dictations":
        return ["Unsubmitted"];
      case "ready_billing":
        return ["Ready for Billing"];
      case "complete":
        return ["Completed"];
      default:
        return [];
    }
  }

  /** DOS for procedure list: committed drawer date preset, else active tab window. */
  getProcedureListDosRange(
    tabId: ProcedureListTabId = this.procedureListActiveTabId
  ): DateRange {
    if (!this.procedureListSheetFiltersCommitted) {
      return this.getProcedureListTabDateRange(tabId);
    }
    const preset = this.procedureListSheetFilters.datePreset;
    const now = DateTime.now();
    if (preset === "all") {
      return this.getProcedureListTabDateRange(tabId);
    }
    if (preset === "today") {
      return {
        startDate: now.startOf("day").toJSDate(),
        endDate: now.endOf("day").toJSDate(),
      };
    }
    if (preset === "last_week") {
      return {
        startDate: now.minus({ days: 7 }).startOf("day").toJSDate(),
        endDate: now.endOf("day").toJSDate(),
      };
    }
    if (preset === "previous") {
      return {
        startDate: DateTime.fromObject({ year: 2020, month: 1, day: 1 })
          .startOf("day")
          .toJSDate(),
        endDate: now.minus({ days: 1 }).endOf("day").toJSDate(),
      };
    }
    if (preset === "custom") {
      const start = this.procedureListSheetFilters.customStart;
      const end = this.procedureListSheetFilters.customEnd ?? start;
      if (start) {
        const s = DateTime.fromJSDate(start).startOf("day");
        const e = DateTime.fromJSDate(end ?? start).endOf("day");
        return { startDate: s.toJSDate(), endDate: e.toJSDate() };
      }
    }
    return this.getProcedureListTabDateRange(tabId);
  }

  /**
   * Procedure listing: `POST /api/v1/procedure/list-procedure` with tab/drawer filters and search.
   */
  getProcedureEncountersList(
    page: number = 1,
    pageSize: number = 25,
    searchQuery: string = "",
    tabId: ProcedureListTabId = this.procedureListActiveTabId,
    options?: { persistListState?: boolean }
  ): Observable<PaginatedAppointmentResponse> {
    const persistListState = options?.persistListState !== false;
    const pageConfig = this.buildProcedureListProcedurePayload(
      page,
      pageSize,
      searchQuery,
      tabId
    );
    const forceClientReferralPagination = false;
    const listClinicCompatConfig = new AdmissionListPayload(
      this.getProcedureListDosRange(tabId)
    );
    listClinicCompatConfig.filterStatus = pageConfig.filterStatus;

    const url = this.apiBaseUrl + "api/v1/procedure/list-procedure";
    return this._dataService.post(url, pageConfig).pipe(
      map((response: PaginatedAppointmentResponse) =>
        this.processPaginatedClinicResponse(
          response,
          page,
          pageSize,
          listClinicCompatConfig,
          forceClientReferralPagination,
          persistListState
        )
      ),
      tap({
        error: () => {
},
      })
    );
  }

  /**
   * Procedure list tab badges: `POST /api/v1/procedure/list-procedure/status-counts`.
   * Same base filters as list-procedure; server ignores filterStatus, pagination, and sort.
   */
  getProcedureListStatusCounts(
    searchQuery: string = ""
  ): Observable<ProcedureListStatusCountsResponse> {
    const url =
      this.apiBaseUrl + "api/v1/procedure/list-procedure/status-counts";
    return this._dataService
      .post(url, this.buildProcedureListStatusCountsPayload(searchQuery))
      .pipe(
        map((response: ProcedureListStatusCountsResponse) => ({
          counts: response?.counts ?? [],
        })),
        catchError(() => of({ counts: [] }))
      );
  }

  private buildProcedureListStatusCountsPayload(
    searchQuery: string
  ): ProcedureListStatusCountsPayload {
    const dateRange = this.getProcedureListStatusCountsDateRange();
    const companyId = (
      this._appContextService.contextSnapshot?.currentlyLoggedInUser?.companyId ??
      ""
    ).trim();
    const providerId = (this.procedureListSheetFilters.providerId ?? "").trim();
    const serviceIds = this.getProcedureListCommittedServiceIds();
    const referralSources = this.getProcedureListCommittedReferralSourceIds();

    return {
      companyId: companyId || undefined,
      providers:
        this.procedureListSheetFiltersCommitted && providerId ? [providerId] : [],
      resources: [],
      filterLocation: [],
      visitType: ["procedure"],
      referralSources,
      ...(serviceIds.length ? { service: serviceIds } : {}),
      filterDate: {
        startDate: Utils.getStartOfDayISODate(dateRange.startDate),
        endDate: Utils.getEndOfDayISODate(dateRange.endDate),
      },
      searchQuery: (searchQuery || this.searchQuery || "").trim(),
    };
  }

  /**
   * Shared DOS window for status-counts: drawer date when set, otherwise full history through today
   * (per-tab buckets like Today are resolved server-side).
   */
  private getProcedureListStatusCountsDateRange(): DateRange {
    if (this.procedureListSheetFiltersCommitted) {
      const preset = this.procedureListSheetFilters.datePreset;
      if (preset !== "all") {
        return this.getProcedureListDosRange("open");
      }
    }
    const now = DateTime.now();
    return {
      startDate: DateTime.fromObject({ year: 2020, month: 1, day: 1 })
        .startOf("day")
        .toJSDate(),
      endDate: now.endOf("day").toJSDate(),
    };
  }

  private buildProcedureListProcedurePayload(
    page: number,
    pageSize: number,
    searchQuery: string,
    tabId: ProcedureListTabId
  ): ProcedureListProcedurePayload {
    const procedureListDosRange = this.getProcedureListDosRange(tabId);
    const companyId = (
      this._appContextService.contextSnapshot?.currentlyLoggedInUser?.companyId ??
      ""
    ).trim();
    const providerId = (this.procedureListSheetFilters.providerId ?? "").trim();
    const serviceIds = this.getProcedureListCommittedServiceIds();
    const referralSources = this.getProcedureListCommittedReferralSourceIds();

    return {
      companyId: companyId || undefined,
      providers:
        this.procedureListSheetFiltersCommitted && providerId ? [providerId] : [],
      resources: [],
      filterStatus: this.getProcedureListEncounterStatusFilter(tabId),
      filterLocation: [],
      visitType: ["procedure"],
      referralSources,
      ...(serviceIds.length ? { service: serviceIds } : {}),
      filterDate: {
        startDate: Utils.getStartOfDayISODate(procedureListDosRange.startDate),
        endDate: Utils.getEndOfDayISODate(procedureListDosRange.endDate),
      },
      pageStart: (page - 1) * pageSize,
      pageLength: pageSize,
      sortField: "encounterDateTime",
      sortDirection: "DESC",
      searchQuery: (searchQuery || this.searchQuery || "").trim(),
    };
  }

  private getProcedureListCommittedServiceIds(): string[] {
    if (!this.procedureListSheetFiltersCommitted) {
      return [];
    }
    return (this.procedureListSheetFilters.serviceIds ?? [])
      .map((id) => String(id ?? "").trim())
      .filter(Boolean);
  }

  private getProcedureListCommittedReferralSourceIds(): string[] {
    if (!this.procedureListSheetFiltersCommitted) {
      return [];
    }
    return (this.procedureListSheetFilters.referralSourceIds ?? [])
      .map((id) => String(id ?? "").trim())
      .filter(Boolean);
  }

  private applyConfirmationStatusFilter(pageConfig: AdmissionListPayload): void {
    if (this.confirmationStatusFilter === "confirmed") {
      pageConfig.isConfirmed = true;
    } else if (this.confirmationStatusFilter === "unconfirmed") {
      pageConfig.isConfirmed = false;
    }
  }

  /**
   * Payload for /referrals left-panel "All (N)" distinct counts: same date/search/status/providers,
   * but each count omits that dimension's filter (like referral distinct count).
   */
  private buildListClinicCountPayload(
    omit: {
      omitResources?: boolean;
      omitLocations?: boolean;
      omitVisitTypes?: boolean;
      omitReferralSources?: boolean;
    },
    searchQuery: string
  ): AdmissionListPayload {
    const pageConfig = new AdmissionListPayload(this.selectedDate);

    const selectedProviders = this.physicianFilter
      .filter((p) => p.value !== "All" && p.isSelected)
      .map((p) => p.value);
    if (selectedProviders.length > 0) {
      pageConfig.providers = selectedProviders;
    }

    if (!omit.omitResources) {
      const selectedResources = this.resourcesFilter
        .filter((r) => r.value !== "All" && r.isSelected)
        .map((r) => r.value);
      if (selectedResources.length > 0) {
        pageConfig.resources = selectedResources;
      }
    }

    pageConfig.filterStatus = this.encounerStatusList
      .filter((p) => p.value !== "All" && p.isSelected)
      .map((p) => p.value);

    const selectedAuthStatus = this.authStatusFilterList
      .filter((p) => p.isSelected)
      .map((p) => p.value);
    if (selectedAuthStatus.length > 0) {
      pageConfig.authStatus = selectedAuthStatus;
    }
    this.applyConfirmationStatusFilter(pageConfig);

    if (!omit.omitLocations) {
      pageConfig.filterLocation = this.locationFilter
        .filter((p) => p.value !== "All" && p.isSelected)
        .map((p) => p.value);
    } else {
      pageConfig.filterLocation = [];
    }

    if (!omit.omitVisitTypes) {
      const selectedVisitTypes = this.visitTypeFilter
        .filter((vt) => vt.value !== "All" && vt.isSelected)
        .map((vt) => vt.value);
      if (selectedVisitTypes.length > 0) {
        pageConfig.visitType = selectedVisitTypes;
      }
    }

    if (!omit.omitReferralSources) {
      const activeReferralSources = this.getActiveReferralSourceValues();
      if (activeReferralSources.length > 0) {
        pageConfig.referralSources = activeReferralSources;
      }
    }

    pageConfig.pageStart = 0;
    pageConfig.pageLength = 10_000;
    pageConfig.sortField = "encounterDateTime";
    pageConfig.sortDirection = "DESC";
    pageConfig.searchQuery = searchQuery || this.searchQuery || "";
    return pageConfig;
  }

  private distinctKeyResource(appt: Appointment): string | null {
    const id =
      appt.scheduleResourceId ||
      appt.resourceId ||
      appt.encounterSchedule?.scheduleResourceId;
    if (id && String(id).trim()) {
      return String(id).trim().toLowerCase();
    }
    const name = appt.resourceName?.trim();
    return name ? name.toLowerCase() : null;
  }

  private distinctKeyLocation(appt: Appointment): string | null {
    const room = appt.encounterSchedule?.procedureRoom?.trim();
    return room ? room.toLowerCase() : null;
  }

  private distinctKeyVisitType(appt: Appointment): string | null {
    const vt = appt.encounterSchedule?.visitType ?? appt.visitType;
    if (vt && String(vt).trim()) {
      return String(vt).trim().toLowerCase();
    }
    return null;
  }

  private distinctKeyReferralSource(appt: Appointment): string | null {
    const raw = (
      appt.referralSource ??
      (appt as Appointment & { referral_source?: string }).referral_source ??
      ""
    )
      .toString()
      .trim();
    return raw ? raw.toLowerCase() : null;
  }

  /**
   * Distinct counts for left-panel "All (N)" labels from the same rows as the main list (no extra list-clinic calls).
   */
  private computeUniqueFilterDimensionsFromRows(rows: Appointment[]): UniqueFilterDimensions {
    const referralSources = new Set<string>();
    const resources = new Set<string>();
    const locations = new Set<string>();
    const visitTypes = new Set<string>();
    for (const appt of rows) {
      const rs = this.distinctKeyReferralSource(appt);
      if (rs) {
        referralSources.add(rs);
      }
      const res = this.distinctKeyResource(appt);
      if (res) {
        resources.add(res);
      }
      const loc = this.distinctKeyLocation(appt);
      if (loc) {
        locations.add(loc);
      }
      const vt = this.distinctKeyVisitType(appt);
      if (vt) {
        visitTypes.add(vt);
      }
    }
    return {
      referralSources: referralSources.size,
      resources: resources.size,
      locations: locations.size,
      visitTypes: visitTypes.size,
    };
  }

  /**
   * When `filterStatus` is sent, list-clinic already applied the status facet — keep every row returned.
   * When empty (Schedule "All"), hide Rescheduled/Waitlist client-side; Cancelled rows remain visible.
   */
  private passesListClinicClientStatusFilter(
    appt: Appointment,
    filterStatus: string[] | undefined | null
  ): boolean {
    if (filterStatus != null && filterStatus.length > 0) {
      return true;
    }
    return !!resolveAdmissionsScheduleStatusFromAppointment(appt);
  }

  private buildProcessedClinicRows(
    response: PaginatedAppointmentResponse,
    pageConfig: AdmissionListPayload
  ): Appointment[] {
    return response.data
      .filter((appt) =>
        this.passesListClinicClientStatusFilter(appt, pageConfig.filterStatus)
      )
      .map((encounter: Appointment) => {
        const normalized = normalizeEncounterPatientPhi(encounter);
        this.applyCachedAuthFieldsToAppointment(normalized);
        const pickedDos = this.pickEncounterDateTimeFromRow(normalized);
        const row: Appointment =
          pickedDos && pickedDos !== (normalized.encounterDateTime ?? "").toString().trim()
            ? { ...normalized, encounterDateTime: pickedDos }
            : { ...normalized };
        const date = DateTime.fromISO((row.encounterDateTime ?? "").trim());
        const checkInTime = row.encounterSchedule?.checkIn;

        let combinedDateTime = date;
        if (date.isValid && checkInTime) {
          const [time, period] = checkInTime.split(" ");
          const [hours, minutes] = time.split(":").map(Number);

          const adjustedHours =
            period === "PM" && hours !== 12
              ? hours + 12
              : period === "AM" && hours === 12
              ? 0
              : hours;

          combinedDateTime = combinedDateTime.set({
            hour: adjustedHours,
            minute: minutes,
          });
        }

        const checklistOpen = extractWorksheetChecklistOpenCountFromEncounter(row);
        const lastDosDt = this.pickLastDosDateTimeFromRow(row);
        const lastSvc = this.pickLastServiceFromRow(row);
        const lastEncStatus = this.pickLastEncounterStatusFromRow(row);
        return {
          ...row,
          combinedDateTime: date.isValid ? combinedDateTime.toISO() : (row.encounterDateTime ?? ""),
          ...(checklistOpen !== undefined
            ? { worksheetChecklistOpenCount: checklistOpen }
            : {}),
          ...(lastDosDt ? { lastDosDateTime: lastDosDt } : {}),
          ...(lastSvc ? { lastService: lastSvc } : {}),
          ...(lastEncStatus ? { lastEncounterStatus: lastEncStatus } : {}),
        };
      });
  }

  /**
   * Engage `assigned_to` may be a string id, or nested under `assignTo` / `assign_to`.
   */
  private extractEngageReferralAssignedToId(ref: EngageReferral): string {
    const raw = ref.assigned_to ?? ref.assignTo ?? ref.assign_to;
    if (raw == null) {
      return "";
    }
    if (typeof raw === "string") {
      return raw.trim();
    }
    if (typeof raw === "object") {
      const id = (raw as { id?: unknown }).id;
      if (id != null) {
        return String(id).trim();
      }
    }
    return String(raw).trim();
  }

  private resolveCompanyUserDisplayName(userId: string): string {
    const uid = (userId || "").trim();
    if (!uid) {
      return "";
    }
    const ctx = this._appContextService.contextSnapshot;
    if (!ctx) {
      return "";
    }
    const map = ctx.companyUsersMap;
    const fromMap =
      map?.[uid] ??
      map?.[uid.toLowerCase()] ??
      Object.values(map || {}).find(
        (u) => u?.id && String(u.id).trim().toLowerCase() === uid.toLowerCase()
      );
    const u =
      fromMap ?? ctx.allAvailableUsers?.find((x) => String(x.id).trim().toLowerCase() === uid.toLowerCase());
    if (!u) {
      return "";
    }
    const n = [u.firstName, u.lastName].filter((x) => String(x || "").trim()).join(" ").trim();
    return n || "";
  }

  /** Resolve a company user id to display name for the worklist coordinator column (never returns the raw id). */
  resolveWorklistUserDisplayName(userId: string | null | undefined): string {
    const key = String(userId ?? "").trim();
    if (!key) {
      return "";
    }
    const byId = this.resolveCompanyUserDisplayName(key);
    if (byId) {
      return byId;
    }
    if (key.includes("@")) {
      return this.resolveCompanyUserDisplayNameByEmail(key);
    }
    return "";
  }

  private resolveCompanyUserDisplayNameByEmail(email: string): string {
    const target = String(email ?? "").trim().toLowerCase();
    if (!target) {
      return "";
    }
    const ctx = this._appContextService.contextSnapshot;
    const me = ctx?.currentlyLoggedInUser;
    const myEmail = String(me?.email ?? "").trim().toLowerCase();
    if (myEmail && myEmail === target) {
      const selfName = [me?.firstName, me?.lastName]
        .filter((x) => String(x ?? "").trim())
        .join(" ")
        .trim();
      if (selfName) {
        return selfName;
      }
    }
    const nameFromUser = (u: { firstName?: string; lastName?: string } | null | undefined): string =>
      [u?.firstName, u?.lastName]
        .filter((x) => String(x ?? "").trim())
        .join(" ")
        .trim();

    const users = ctx?.allAvailableUsers ?? [];
    for (const u of users) {
      const em = String(u.email ?? "").trim().toLowerCase();
      if (!em || em !== target) {
        continue;
      }
      const name = nameFromUser(u);
      if (name) {
        return name;
      }
    }
    const map = ctx?.companyUsersMap;
    if (map) {
      for (const u of Object.values(map)) {
        const em = String(u.email ?? "").trim().toLowerCase();
        if (!em || em !== target) {
          continue;
        }
        const name = nameFromUser(u);
        if (name) {
          return name;
        }
      }
    }
    return "";
  }

  registerWorksheetStageLabels(
    opts: ReadonlyArray<{ display?: string | null; value?: string | null }> | null | undefined
  ): void {
    let changed = false;
    for (const o of opts ?? []) {
      const value = String(o?.value ?? "").trim();
      const display = String(o?.display ?? "").trim();
      if (value && display) {
        const key = value.toLowerCase();
        if (this.worksheetStageLabelByStageId.get(key) !== display) {
          this.worksheetStageLabelByStageId.set(key, display);
          changed = true;
        }
      }
    }
    if (changed) {
      this.worksheetStageLabelsChanged$.next();
    }
  }

  resolveWorksheetStageLabel(stageId: string | null | undefined): string {
    const raw = String(stageId ?? "").trim();
    if (!raw) {
      return "";
    }
    return this.worksheetStageLabelByStageId.get(raw.toLowerCase()) ?? "";
  }

  /**
   * Coordinator label + assignee id for worklist rows (GET referrals: `assign_to_name`, `assigned_to`, …).
   */
  /** Coordinator / assignee display from a list-clinic encounter row (same fields as worklist grid). */
  private clinicCoordinatorDisplayFromRow(row: Appointment): string {
    const o = row as unknown as Record<string, unknown>;
    const a = row as Appointment & { coordinator?: string; assignedTo?: string; assigned_to?: string };
    const nested = [
      o["coordinator_name"],
      o["coordinatorName"],
      o["assigned_to_name"],
      o["assignToName"],
      o["case_manager_name"],
      o["caseManagerName"],
    ];
    for (const v of nested) {
      if (v != null && String(v).trim()) {
        return String(v).trim();
      }
    }
    const cand = (
      (row.coordinatorName || "").trim() ||
      (a.coordinator || "").trim() ||
      (a.assignedTo || "").trim() ||
      (a.assigned_to ?? "").toString().trim() ||
      ""
    ).trim();
    if (!cand) {
      return "";
    }
    if (isValidUuid(cand)) {
      const n = this.resolveCompanyUserDisplayName(cand);
      return n || "";
    }
    return cand;
  }

  /**
   * For the same `patientId`, list-clinic may return sparse `encountersPatient` on one encounter and full
   * demographics on another — merge the best-known clinic fields onto every merged row for that patient.
   */
  private applyClinicPatientDemographicsFallback(
    rows: Appointment[],
    clinicRows: Appointment[]
  ): Appointment[] {
    if (!clinicRows?.length) {
      return rows;
    }
    const byPatient = new Map<
      string,
      {
        dob?: string;
        name?: string;
        gender?: string;
        coordinatorName?: string;
        referralWorkflowStatus?: string;
        encounterStatus?: string;
      }
    >();
    for (const row of clinicRows) {
      const pk = (row.encountersPatient?.id ?? row.patientId ?? "").toString().trim().toLowerCase();
      if (!pk) {
        continue;
      }
      const ep = row.encountersPatient;
      const prev = byPatient.get(pk) ?? {};
      const dob = (prev.dob || (ep?.dob ?? "").toString().trim()) || undefined;
      const name =
        pickBetterEncounterPatientDisplayName(
          prev.name,
          (ep?.name ?? "").toString().trim()
        ) || undefined;
      const gender = (prev.gender || (ep?.gender ?? "").toString().trim()) || undefined;
      const coordRow = this.clinicCoordinatorDisplayFromRow(row);
      const coordinatorName =
        [prev.coordinatorName, coordRow].map((s) => (s || "").trim()).find((s) => s) || undefined;
      const rwf = (row.referralWorkflowStatus ?? "").toString().trim();
      const encs = (row.encounterStatus ?? "").toString().trim();
      const referralWorkflowStatus =
        (prev.referralWorkflowStatus || "").trim() || rwf || undefined;
      const encounterStatus = (prev.encounterStatus || "").trim() || encs || undefined;
      byPatient.set(pk, {
        dob,
        name,
        gender,
        coordinatorName,
        referralWorkflowStatus,
        encounterStatus,
      });
    }
    return rows.map((row) => {
      const pk = (row.encountersPatient?.id ?? row.patientId ?? "").toString().trim().toLowerCase();
      if (!pk) {
        return row;
      }
      const fb = byPatient.get(pk);
      if (!fb) {
        return row;
      }
      const next = { ...row } as Appointment;
      if (!next.encountersPatient && (fb.name || fb.dob || fb.gender)) {
        next.encountersPatient = {
          id: pk,
          name: (fb.name ?? "").trim() || "",
          dob: (fb.dob ?? "").trim() || "",
          gender: (fb.gender ?? "").trim() || "",
          age: 0,
        };
      } else if (next.encountersPatient) {
        const ep = next.encountersPatient;
        const hasDob = !!(ep.dob || "").toString().trim();
        const hasName = isUsableEncounterPatientDisplayName((ep.name || "").toString());
        const hasGender = !!(ep.gender || "").toString().trim();
        next.encountersPatient = {
          ...ep,
          dob: hasDob ? ep.dob : (fb.dob ?? ep.dob ?? ""),
          name: hasName ? ep.name : (fb.name ?? ep.name ?? ""),
          gender: hasGender ? ep.gender : (fb.gender ?? ep.gender ?? ""),
        };
      }
      if (!(next.coordinatorName || "").trim() && (fb.coordinatorName || "").trim()) {
        next.coordinatorName = fb.coordinatorName;
      }
      if (!(next.referralWorkflowStatus || "").trim() && (fb.referralWorkflowStatus || "").trim()) {
        next.referralWorkflowStatus = fb.referralWorkflowStatus;
      }
      if (!(next.encounterStatus || "").trim() && (fb.encounterStatus || "").trim()) {
        next.encounterStatus = fb.encounterStatus;
      }
      return next;
    });
  }

  /** Display name from nested Engage `assign_to` / `assignTo` object when flat strings are absent. */
  private engageAssigneeDisplayNameFromRef(ref: EngageReferral): string {
    const r = ref as Record<string, unknown>;
    const node = ref.assignTo ?? ref.assign_to ?? r["assign_to"] ?? r["assignTo"] ?? r["assigned_to"];
    if (node == null) {
      return "";
    }
    if (typeof node === "string") {
      return node.trim();
    }
    if (typeof node !== "object") {
      return "";
    }
    const o = node as Record<string, unknown>;
    const direct = String(o["name"] ?? o["displayName"] ?? o["fullName"] ?? "").trim();
    if (direct) {
      return direct;
    }
    const fn = String(o["firstName"] ?? o["first_name"] ?? "").trim();
    const ln = String(o["lastName"] ?? o["last_name"] ?? "").trim();
    const joined = [fn, ln].filter(Boolean).join(" ").trim();
    return joined;
  }

  private engageReferralCoordinatorFields(ref: EngageReferral): { label: string; userId: string } {
    const userId = this.extractEngageReferralAssignedToId(ref);
    const r = ref as Record<string, unknown>;
    const explicitName = (
      ref.assign_to_name ??
      r["assigned_to_name"] ??
      r["assignToName"] ??
      r["coordinatorName"] ??
      r["coordinator_name"] ??
      r["primary_coordinator_name"] ??
      r["owner_name"] ??
      r["case_manager_name"] ??
      ""
    )
      .toString()
      .trim();
    if (explicitName) {
      return { label: explicitName, userId };
    }
    const nestedName = this.engageAssigneeDisplayNameFromRef(ref);
    if (nestedName) {
      return { label: nestedName, userId };
    }
    if (!userId) {
      return { label: "", userId: "" };
    }
    const resolved = this.resolveCompanyUserDisplayName(userId);
    return { label: resolved, userId };
  }

  private engageReferralWorkFlowFields(ref: EngageReferral): { id: string; name: string } {
    const r = ref as Record<string, unknown>;
    const nestedRaw = r["workFlow"] ?? r["workflow"] ?? r["WorkFlow"] ?? r["pipeline"];
    const nested =
      nestedRaw != null && typeof nestedRaw === "object" && !Array.isArray(nestedRaw)
        ? (nestedRaw as Record<string, unknown>)
        : null;
    const id =
      this.directoryStringField(r, [
        "workFlowId",
        "work_flow_id",
        "workflowId",
        "workflow_id",
      ]) ||
      (nested
        ? this.directoryStringField(nested, ["id", "workFlowId", "work_flow_id"])
        : "");
    const name =
      this.directoryStringField(r, [
        "workFlowName",
        "work_flow_name",
        "workflowName",
        "workflow_name",
      ]) ||
      (nested
        ? this.directoryStringField(nested, [
            "workFlowName",
            "work_flow_name",
            "name",
          ])
        : "");
    return { id, name };
  }

  private enrichWorksheetRowsWithEngageReferralCounts(
    rows: Appointment[],
    refs: EngageReferral[]
  ): Appointment[] {
    if (!refs.length) {
      return rows;
    }
    const byId = new Map<string, EngageReferral>();
    const byPatientId = new Map<string, EngageReferral>();
    for (const ref of refs) {
      const id = (ref.id ?? "").toString().trim().toLowerCase();
      if (id) {
        byId.set(id, ref);
      }
      const pid = this.extractPatientIdFromEngageReferral(ref).toLowerCase();
      if (pid) {
        byPatientId.set(pid, ref);
      }
    }
    return rows.map((row) => {
      const rid = getAppointmentReferralId(row).trim().toLowerCase();
      const patientKey = (row.encountersPatient?.id ?? row.patientId ?? "").toString().trim().toLowerCase();
      /** Prefer referral id; else fall back to patient id (last referral wins in map). */
      const ref = (rid ? byId.get(rid) : undefined) ?? (patientKey ? byPatientId.get(patientKey) : undefined);
      if (!ref) {
        return row;
      }
      const ls = (ref.lead_status ?? "").toString().trim();
      const qid = this.engageReferralQueueIdFromRef(ref);
      const qname = this.engageReferralQueueNameFromRef(ref);
      const stage = (ref.stage ?? "").toString().trim();
      const rAny = ref as Record<string, unknown>;
      const patientStatus = String(rAny["patient_status"] ?? rAny["patientStatus"] ?? "").trim();
      const { label: coordLabel, userId: coordUserId } = this.engageReferralCoordinatorFields(ref);
      const engageRefId = (ref.id ?? "").toString().trim();
      const mergedWorkflow =
        ls ||
        (row.referralWorkflowStatus ?? "").toString().trim() ||
        patientStatus ||
        stage ||
        "";
      const next = {
        ...row,
        ...(ref.last_activity != null ? { last_activity: ref.last_activity } : {}),
        worksheetEngageIconCount: worksheetRowEngageIconCountFromReferral(ref),
        worksheetUnreadMessageCount: worksheetRowUnreadMessageCountFromReferral(ref),
        worksheetNotificationsChannelCount:
          worksheetRowNotificationsChannelCountFromReferral(ref),
        worksheetReminderIconCount: worksheetRowReminderIconCountFromReferral(ref),
        referralWorkflowStatus: mergedWorkflow ? mergedWorkflow : row.referralWorkflowStatus,
      } as Appointment & { assigned_to?: string; assignedTo?: string };
      if (coordLabel) {
        next.coordinatorName = coordLabel;
      }
      /** list-clinic often omits `referralId` while Engage has it on the same patient — link so queue UI and PATCH work. */
      if (engageRefId && !getAppointmentReferralId(next).trim()) {
        next.referralId = engageRefId;
        if (next.encountersPatient) {
          next.encountersPatient = {
            ...next.encountersPatient,
            referralId: engageRefId,
          };
        }
      }
      if (qid) {
        next.referralQueueId = qid;
      }
      if (qname) {
        next.referralQueueLabel = qname;
      } else if (!qid && stage) {
        next.referralQueueLabel = stage;
      }
      if (coordUserId) {
        next.assigned_to = coordUserId;
        next.assignedTo = coordUserId;
      }
      const wf = this.engageReferralWorkFlowFields(ref);
      if (wf.id) {
        next.workFlowId = wf.id;
      }
      if (wf.name) {
        next.workFlowName = wf.name;
      }
      const serviceLabel = this.directoryStringField(rAny, [
        "service",
        "formProduct",
        "form_product",
      ]);
      if (serviceLabel && !(next.service || "").toString().trim()) {
        next.service = serviceLabel;
      }
      const lastServiceLabel = this.pickLastServiceFromRow(rAny);
      if (lastServiceLabel && !(next.lastService || "").toString().trim()) {
        next.lastService = lastServiceLabel;
      }
      const reasonLabel = this.directoryStringField(rAny, [
        "reasonOfVisit",
        "reason_of_visit",
        "reasonForVisit",
        "reason_for_visit",
      ]);
      if (
        reasonLabel &&
        !(next.reasonOfVisit || "").toString().trim() &&
        !(next.reasonForVisit || "").trim()
      ) {
        next.reasonOfVisit = reasonLabel;
      }
      const refDob = (ref.dob ?? "").toString().trim();
      const refGender = (ref.gender ?? "").toString().trim();
      const refFirst = (ref.firstName ?? "").toString().trim();
      const refLast = (ref.lastName ?? "").toString().trim();
      const refFullName = [refFirst, refLast].filter(Boolean).join(" ").trim();
      if (next.encountersPatient) {
        const ep = next.encountersPatient;
        const mergedDob = pickEngageDisplayDob(
          refDob,
          (ep.dob ?? "").toString().trim()
        );
        const needGender = !(ep.gender || "").toString().trim() && !!refGender;
        const needName = !(ep.name || "").toString().trim() && !!refFullName;
        if (mergedDob || needGender || needName) {
          next.encountersPatient = {
            ...ep,
            dob: mergedDob || ep.dob,
            gender: needGender ? refGender : ep.gender,
            name: needName ? refFullName : ep.name,
          };
        }
      } else if (patientKey && (refDob || refGender || refFullName)) {
        next.encountersPatient = {
          id: patientKey,
          name: refFullName || "Prospect",
          dob: pickEngageDisplayDob(refDob) || refDob || "",
          gender: refGender || "",
          age: 0,
          referralId: engageRefId || undefined,
        };
      }
      const lastDosDt = this.engageReferralLastDosDateTime(ref);
      if (lastDosDt && !(next.lastDosDateTime ?? "").toString().trim()) {
        next.lastDosDateTime = lastDosDt;
      }
      const lastEncStatus = this.pickLastEncounterStatusFromRow(rAny);
      if (lastEncStatus && !(next.lastEncounterStatus ?? "").toString().trim()) {
        next.lastEncounterStatus = lastEncStatus;
      }
      return next;
    });
  }

  private applyReferralSourceClientFilterRows(
    rows: Appointment[],
    activeReferralSources: string[]
  ): Appointment[] {
    if (!activeReferralSources.length) {
      return rows;
    }
    return rows.filter((appt) =>
      rowMatchesReferralSourceFilter(appt, activeReferralSources)
    );
  }

  /**
   * Worklist (/worklist): merged clinic + Engage rows order by most recent activity (Engage sidebar parity).
   */
  private sortWorklistRowsByLatestActivityDesc(rows: Appointment[]): Appointment[] {
    return this.applyWorklistPatientActivityBoost(
      sortAppointmentsByLatestActivityDesc(rows)
    );
  }

  private applyWorklistPatientActivityBoost(rows: Appointment[]): Appointment[] {
    const pid = (this.worklistBoostPatientId || "").trim();
    if (!pid) {
      return rows;
    }
    const iso = (this.worklistBoostActivityIso || new Date().toISOString()).trim();
    this.worklistBoostPatientId = null;
    this.worklistBoostActivityIso = null;

    const rowPatientId = (r: Appointment): string =>
      (r.patientId ?? r.encountersPatient?.id ?? "").toString().trim();

    const withFreshActivity = (row: Appointment): Appointment => {
      const la: EngageReferralLastActivity = {
        type: "form_filled",
        createdDateTime: iso,
        comment: "",
      };
      const next = {
        ...row,
        updatedAt: iso,
        last_activity: la,
      } as Appointment & { last_activity?: EngageReferralLastActivity };
      next.last_activity = la;
      return next;
    };

    const idx = rows.findIndex((r) => rowPatientId(r) === pid);
    if (idx >= 0) {
      const copy = [...rows];
      const [row] = copy.splice(idx, 1);
      return [withFreshActivity(row), ...copy];
    }

    const patient = this._applicationCacheService.applicationCache?.patient;
    if (patient && (patient.id || "").trim() === pid) {
      const stub = this.buildEngageReferralStubFromCachedPatient(patient, iso);
      const row = withFreshActivity(this.mapEngageReferralToWorklistAppointment(stub));
      return [row, ...rows];
    }
    return rows;
  }

  private buildEngageReferralStubFromCachedPatient(
    patient: Patient,
    activityIso: string
  ): EngageReferral {
    const rid = (
      (patient.referralId || "").trim() ||
      (this._applicationCacheService.applicationCache?.appointment?.referralId || "").trim()
    );
    return {
      id: rid || undefined,
      patientId: (patient.id || "").trim() || undefined,
      firstName: (patient.firstName || "").trim() || undefined,
      lastName: (patient.lastName || "").trim() || undefined,
      dob: patient.dob,
      gender: patient.gender,
      referralSource: patient.referralSource,
      createdAt: activityIso,
      last_activity: {
        type: "form_filled",
        createdDateTime: activityIso,
        comment: "",
      },
    } as EngageReferral;
  }

  /**
   * True when worklist `list-clinic` is narrowed by facets or name search (same dimensions as merge POST).
   * Any single facet or any combination counts as “active”. Used to avoid inflating totals with unscoped
   * plain `GET /referrals` `total` when list-clinic is filtered.
   *
   * @param effectiveSearchQuery When provided, must match the `searchQuery` sent on that list-clinic request
   * (e.g. `getAdmissionsList` argument merged with `this.searchQuery`) so pager logic stays in sync.
   */
  private worklistHasActiveListClinicMergeFilters(effectiveSearchQuery?: string): boolean {
    const facet = (items: SelectableEnumerationItem[] | undefined): boolean =>
      (items ?? []).some((p) => p.isSelected && String(p.value ?? "") !== "All");
    if (facet(this.physicianFilter)) {
      return true;
    }
    if (facet(this.resourcesFilter)) {
      return true;
    }
    if (facet(this.locationFilter)) {
      return true;
    }
    if (facet(this.encounerStatusList)) {
      return true;
    }
    if (facet(this.visitTypeFilter)) {
      return true;
    }
    if (facet(this.serviceFilter)) {
      return true;
    }
    if (this.getActiveReferralSourceValues().length > 0) {
      return true;
    }
    const q = (effectiveSearchQuery ?? this.searchQuery ?? "").trim();
    if (q !== "") {
      return true;
    }
    return false;
  }

  private finalizePaginatedList(
    processedData: Appointment[],
    page: number,
    pageSize: number,
    forceClientReferralPagination: boolean,
    response: PaginatedAppointmentResponse,
    useClientRowCountForTotal: boolean,
    /** When set (worklist GET `/referrals` `total`), paginator uses this count (e.g. 4120). */
    referralsTotalFromApi?: number | null,
    /** Same `searchQuery` as the list-clinic POST for this response (single vs multi-filter + search). */
    effectiveListClinicSearchQuery?: string,
    /** Procedure-list tab counts use `persistListState: false` so parallel list-clinic calls do not overwrite the grid. */
    persistListState = true,
    listOrigin: "schedule" | "procedure" = "schedule"
  ): PaginatedAppointmentResponse {
    let totalForList = useClientRowCountForTotal
      ? processedData.length
      : (response.totalRowsCount ?? 0);
    if (
      referralsTotalFromApi != null &&
      !Number.isNaN(Number(referralsTotalFromApi)) &&
      referralsTotalFromApi >= 0
    ) {
      const refT = Number(referralsTotalFromApi);
      /**
       * Filtered GET /referrals returns a small `total` (e.g. 6) while `totalCount` / list-clinic can be much
       * larger. Prefer Engage filtered total when it is lower so the pager matches the referral query; else
       * use the higher ceiling for unfiltered referral totals + clinic.
       */
      if (refT < totalForList) {
        totalForList = refT;
      } else {
        totalForList = Math.max(totalForList, refT);
      }
    }
    const uniqueFilterDimensions = this.computeUniqueFilterDimensionsFromRows(processedData);
    const pageData = forceClientReferralPagination
      ? processedData.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize)
      : processedData;
    const pageLenForTotalPages = forceClientReferralPagination
      ? pageSize
      : response.pageLength || pageSize;
    let totalPages = forceClientReferralPagination
      ? Math.max(1, Math.ceil(totalForList / pageSize))
      : response.totalPages;
    if (
      !forceClientReferralPagination &&
      pageLenForTotalPages > 0 &&
      totalForList !== (response.totalRowsCount ?? 0)
    ) {
      totalPages = Math.max(1, Math.ceil(totalForList / pageLenForTotalPages));
    }

    if (persistListState) {
      this.admissionList = pageData;
      this.admissionListOrigin = listOrigin;
      this.currentPage = forceClientReferralPagination ? page : response.currentPage;
      this.totalRecords = totalForList;
      this.totalPages = totalPages;
      this.pageSize = forceClientReferralPagination ? pageSize : response.pageLength;
    }

    return {
      ...response,
      data: pageData,
      uniqueFilterDimensions,
      pageStart: forceClientReferralPagination ? (page - 1) * pageSize : response.pageStart,
      pageLength: forceClientReferralPagination ? pageSize : response.pageLength,
      currentPage: forceClientReferralPagination ? page : response.currentPage,
      totalPages,
      totalRowsCount: totalForList,
    };
  }

  private processPaginatedClinicResponse(
    response: PaginatedAppointmentResponse,
    page: number,
    pageSize: number,
    pageConfig: AdmissionListPayload,
    forceClientReferralPagination: boolean,
    persistListState = true
  ): PaginatedAppointmentResponse {
    let processedData = filterProcedureListDisplayRows(
      this.buildProcessedClinicRows(response, pageConfig)
    );
    const activeReferralSources = this.getActiveReferralSourceValues();
    const applyReferralClientFilter = activeReferralSources.length > 0;
    if (applyReferralClientFilter) {
      processedData = this.applyReferralSourceClientFilterRows(processedData, activeReferralSources);
    }
    return this.finalizePaginatedList(
      processedData,
      page,
      pageSize,
      forceClientReferralPagination,
      response,
      applyReferralClientFilter,
      undefined,
      pageConfig.searchQuery,
      persistListState,
      "procedure"
    );
  }

  /**
   * Maps Engage referral search hits to the shape expected by {@link mergeEngageProspectsNotOnClinic} /
   * {@link enrichWorksheetRowsWithEngageReferralCounts}.
   */
  private referralSearchHitsToEngageReferrals(
    hits: ReferralSearchResult[]
  ): EngageReferral[] {
    return (hits ?? []).map((h) => ({
      ...(h as unknown as Record<string, unknown>),
      id: h.id,
      firstName: h.firstName,
      lastName: h.lastName,
      createdAt: h.createdAt,
      phone: h.phone,
      email: h.email,
    })) as EngageReferral[];
  }

  /**
   * Engage referrals for worklist merge: toolbar selections plus optional term resolved via
   * PHI `GET …/patients/directory` with `engage_search` (deduped by referral id).
   */
  private fetchEngageBootstrapForWorklistMerge(): Observable<EngageReferral[]> {
    const q = (this.searchQuery || "").trim();
    const mergeDedupe = (apiHits: EngageReferral[]): EngageReferral[] => {
      const byId = new Map<string, EngageReferral>();
      for (const r of this.worklistToolbarEngageSelections) {
        const id = String(r?.id ?? "").trim();
        if (id) {
          byId.set(id, r);
        }
      }
      for (const r of apiHits) {
        const id = String(r?.id ?? "").trim();
        if (id && !byId.has(id)) {
          byId.set(id, r);
        }
      }
      return [...byId.values()];
    };
    if (!q) {
      return of(mergeDedupe([]));
    }
    return this._engagePatientService.searchReferrals(q).pipe(
      map((hits: ReferralSearchResult[]) =>
        mergeDedupe(this.referralSearchHitsToEngageReferrals(hits ?? []))
      ),
      catchError(() => of(mergeDedupe([])))
    );
  }

  private parseReferralsPayloadFromUnknown(res: unknown): {
    referrals: EngageReferral[];
    total: number | null;
  } {
        const r = res as Record<string, unknown> | unknown[] | null;
    let referrals: EngageReferral[] = [];
        if (Array.isArray(r)) {
      referrals = r as EngageReferral[];
    } else if (r && typeof r === "object") {
      const o = r as Record<string, unknown>;
      const itemsArr = o["items"];
      const data = o["data"];
      const refArr = o["referrals"];
      const body = o["body"];
      if (Array.isArray(itemsArr)) {
        referrals = itemsArr as EngageReferral[];
      } else if (Array.isArray(data)) {
        referrals = data as EngageReferral[];
      } else if (Array.isArray(refArr)) {
        referrals = refArr as EngageReferral[];
      } else if (Array.isArray(body)) {
        referrals = body as EngageReferral[];
      }
      const totalFiltered = o["total"];
      const totalCountAll = o["totalCount"];
      const paginationTotal = (o["pagination"] as Record<string, unknown>)?.["total"];
      const meta = o["meta"] as Record<string, unknown> | undefined;
      /** Prefer `total` (filter-scoped) over `totalCount` when both exist — Engage returns both for filtered lists. */
      const totalRaw =
        totalFiltered != null && String(totalFiltered).trim() !== ""
          ? totalFiltered
          : totalCountAll ?? paginationTotal;
      const t =
        totalRaw != null
          ? Number(totalRaw)
          : meta?.["total"] != null
            ? Number(meta["total"])
            : NaN;
      return { referrals, total: !Number.isNaN(t) ? t : null };
    }
    return { referrals, total: null };
  }

  /** Pipeline board: per-stage totals (`include_stage=true`) matching list directory filters. */
  fetchEngagePipelineGroupedStageTotals(): Observable<EngagePipelineGroupedStageTotals> {
    const pageLimit = 1;
    const base = phiPatientDirectoryListGetUrl(1, pageLimit);
    const filters = this.buildWorklistPlainPatientDirectoryFilters({
      pageLimit,
      includeStageGrouped: true,
    });
    const url = appendPatientDirectoryFiltersToUrl(base, filters);
    return this._dataService.get(url).pipe(
      map((res: unknown) => {
        const parsed = parsePatientDirectoryGroupedByStagePayload(res);
        if (parsed) {
          return parsed;
        }
        return { grandTotal: 0, stages: [] };
      }),
      catchError(() => of({ grandTotal: 0, stages: [] }))
    );
  }

  /** Pipeline column page: full directory rows for one workflow stage id (`que_id`). */
  fetchEngagePipelineStageLeadsPage(
    stageId: string,
    page: number,
    pageLimit: number
  ): Observable<{ referrals: EngageReferral[]; total: number | null }> {
    const sid = (stageId ?? "").trim();
    if (!sid) {
      return of({ referrals: [], total: 0 });
    }
    return this.fetchWorklistReferralsPlainPage(page, {
      pageLimit,
      overrideQueId: sid,
      includeFullName: true,
      includeDos: true,
      includeDob: true,
    }).pipe(
      map(({ referrals, total }) => ({
        referrals: referrals ?? [],
        total: total ?? null,
      }))
    );
  }

  /** One plain PHI `GET …/patients/directory` page with worksheet referral_source / lead_status / que_id when set. */
  private fetchWorklistReferralsPlainPage(
    page: number,
    directoryOpts?: WorklistDirectoryPageFetchOpts
  ): Observable<{ page: number; referrals: EngageReferral[]; total: number | null }> {
    const cacheKey = this.worklistPlainReferralsCacheKey(page, directoryOpts);
    const cached = this.worklistPlainReferralPageCache.get(cacheKey);
    if (cached) {
      return of({ page, referrals: cached.referrals, total: cached.total });
    }
    const pageLimit = resolveWorklistDirectoryPageLimit(directoryOpts);
    const base = phiPatientDirectoryListGetUrl(page, pageLimit);
    const filters = this.buildWorklistPlainPatientDirectoryFilters(directoryOpts);
    const url = appendPatientDirectoryFiltersToUrl(base, filters);
    return this._dataService.get(url).pipe(
      map((res: unknown) => {
        const parsed = parsePatientDirectoryOrLegacyReferralsPayload(
          res,
          page,
          pageLimit
        );
        return {
          page,
          referrals: parsed.referrals,
          total: parsed.total ?? null,
        };
      }),
      tap(({ referrals, total }) => {
        this.worklistPlainReferralPageCache.set(cacheKey, { referrals, total });
      }),
      catchError(() => of({ page, referrals: [] as EngageReferral[], total: null as number | null }))
    );
  }

  /** Parallel referral API pages (plain or Engage-query), merged into a map + shared `total`. */
  private fetchWorklistReferralPagesBatch(
    pages: number[],
    directoryOpts?: WorklistDirectoryPageFetchOpts
  ): Observable<{ byPage: Map<number, EngageReferral[]>; total: number | null }> {
    const uniq = [...new Set(pages)].filter((p) => p >= 1).sort((a, b) => a - b);
    if (!uniq.length) {
      return of({ byPage: new Map(), total: null });
    }
    if (this.worklistMergeEngageFromReferralsApi) {
      const pageLimit = resolveWorklistDirectoryPageLimit(directoryOpts);
      const cacheEngageReferralPages =
        pageLimit === WORKLIST_REFERRALS_API_PAGE_LIMIT;
      return forkJoin(
        uniq.map((p) => {
          const hitEng = cacheEngageReferralPages
            ? this.worklistEngageReferralPageCache.get(p)
            : undefined;
          if (hitEng) {
            return this.fetchWorklistReferralsPlainPage(p, directoryOpts).pipe(
              map((dir) => ({
                page: p,
                referrals: mergeEngageReferralPageWithPatientDirectoryOverlay(
                  hitEng.referrals,
                  dir.referrals
                ),
                total: hitEng.total ?? dir.total ?? null,
              }))
            );
          }
          const createdDr = this.getWorksheetCreatedDateRangeFilterForEngageQuery();
          return this._engageReferralsQuery
            .getReferralsPage(
              p,
              pageLimit,
              createdDr ? { referralDateRangeOverride: createdDr } : undefined
            )
            .pipe(
              tap((res) => {
                const referrals = (Array.isArray(res.referrals)
                  ? res.referrals
                  : []) as EngageReferral[];
                if (cacheEngageReferralPages) {
                  this.worklistEngageReferralPageCache.set(p, {
                    referrals,
                    total: res.total ?? null,
                  });
                }
              }),
              switchMap((res) => {
                const raw = (Array.isArray(res.referrals)
                  ? res.referrals
                  : []) as EngageReferral[];
                return this.fetchWorklistReferralsPlainPage(p, directoryOpts).pipe(
                  map((dir) => ({
                    page: p,
                    referrals: mergeEngageReferralPageWithPatientDirectoryOverlay(
                      raw,
                      dir.referrals
                    ),
                    total: res.total ?? dir.total ?? null,
                  }))
                );
              }),
              catchError(() =>
                of({
                  page: p,
                  referrals: [] as EngageReferral[],
                  total: null as number | null,
                })
              )
            );
        })
      ).pipe(
        map((results) => {
          const byPage = new Map<number, EngageReferral[]>();
          let total: number | null = null;
          for (const r of results) {
            byPage.set(r.page, r.referrals);
            if (r.total != null) {
              total = r.total;
            }
          }
          return { byPage, total };
        })
      );
    }
    return forkJoin(uniq.map((p) => this.fetchWorklistReferralsPlainPage(p, directoryOpts))).pipe(
      map((results) => {
        const byPage = new Map<number, EngageReferral[]>();
        let total: number | null = null;
        for (const r of results) {
          byPage.set(r.page, r.referrals);
          if (r.total != null) {
            total = r.total;
          }
        }
        return { byPage, total };
      })
    );
  }

  /** Bootstrap response rows first, then paginated pages (dedupe by referral id). */
  private flattenWorklistReferralRows(
    bootstrapReferrals: EngageReferral[],
    byPage: Map<number, EngageReferral[]>
  ): EngageReferral[] {
    const seen = new Set<string>();
    const out: EngageReferral[] = [];
    const push = (r: EngageReferral): void => {
      const id = (extractEngageReferralRowId(r) || (r.id ?? "").toString()).trim().toLowerCase();
      if (!id || seen.has(id)) {
        return;
      }
      seen.add(id);
      out.push(r);
    };
    for (const r of bootstrapReferrals ?? []) {
      push(r);
    }
    for (const r of this.flattenReferralsByPage(byPage)) {
      push(r);
    }
    return out;
  }

  private flattenReferralsByPage(byPage: Map<number, EngageReferral[]>): EngageReferral[] {
    const pages = [...byPage.keys()].sort((a, b) => a - b);
    const seen = new Set<string>();
    const out: EngageReferral[] = [];
    for (const p of pages) {
      for (const r of byPage.get(p) ?? []) {
        const id = (extractEngageReferralRowId(r) || (r.id ?? "").toString()).trim().toLowerCase();
        if (!id || seen.has(id)) {
          continue;
        }
        seen.add(id);
        out.push(r);
      }
    }
    return out;
  }

  private computeReferralsHasMore(
    apiTotal: number | null,
    bootstrapReferrals: EngageReferral[],
    byPage: Map<number, EngageReferral[]>
  ): boolean {
    const loaded = this.flattenWorklistReferralRows(bootstrapReferrals, byPage).length;
    if (apiTotal != null && !Number.isNaN(Number(apiTotal))) {
      return loaded < Number(apiTotal);
    }
    if (!byPage.size) {
      return false;
    }
    const highest = Math.max(...byPage.keys());
    const last = byPage.get(highest);
    return (last?.length ?? 0) >= WORKLIST_REFERRALS_API_PAGE_LIMIT;
  }

  /**
   * When the worklist calls GET /referrals with `referral_source` / `lead_status` / `que_id`, the response `data` is the
   * source of truth — list-clinic can still return other encounters on the same page; keep only rows tied to
   * those referrals (by referral id or patient id).
   */
  private restrictWorklistCombinedToReferralPage(
    combined: Appointment[],
    refsFlat: EngageReferral[]
  ): Appointment[] {
    if (!refsFlat.length) {
      return combined;
    }
    const allowedRefIds = new Set(
      refsFlat
        .map((r) => (extractEngageReferralRowId(r) || (r.id ?? "").toString()).trim().toLowerCase())
        .filter(Boolean)
    );
    const allowedPatientIds = new Set(
      refsFlat
        .map((r) => this.extractPatientIdFromEngageReferral(r))
        .map((id) => id.toLowerCase())
        .filter(Boolean)
    );
    return combined.filter((a) => {
      const rid = (getAppointmentReferralId(a) ?? "").toString().trim().toLowerCase();
      if (rid && allowedRefIds.has(rid)) {
        return true;
      }
      const pid = (a.encountersPatient?.id ?? a.patientId ?? "").toString().trim().toLowerCase();
      if (pid && allowedPatientIds.has(pid)) {
        return true;
      }
      return false;
    });
  }

  /**
   * List-clinic + Engage merge is narrowed by GET /referrals `lead_status` / `que_id` (not referral source — when
   * referral sources are selected, the grid is Engage-only; see {@link getAdmissionsList}).
   */
  private worklistUsesEngageReferralQueryFilters(): boolean {
    return (
      this.getWorklistReferralsLeadStatusQueryParams().length > 0 ||
      !this.isAllWorkFlowId(this.getWorklistWorkFlowIdQueryParam()) ||
      this.getWorklistReferralsQueIdQueryParams().length > 0 ||
      this.getWorklistReferralNotificationQueryParams().length > 0
    );
  }

  private buildWorklistMergedCombinedFromParts(
    clinicAll: Appointment[],
    processedData: Appointment[],
    bootstrapReferrals: EngageReferral[],
    byPage: Map<number, EngageReferral[]>,
    applyReferralClientFilter: boolean,
    activeRs: string[]
  ): Appointment[] {
    const refsFlat = this.flattenWorklistReferralRows(bootstrapReferrals, byPage);
    const extras = this.mergeEngageProspectsNotOnClinic(clinicAll, refsFlat);
    let combined = processedData.concat(extras);
    combined = this.enrichWorksheetRowsWithEngageReferralCounts(combined, refsFlat);
    combined = this.applyClinicPatientDemographicsFallback(combined, clinicAll);
    if (
      applyReferralClientFilter &&
      !(this.worklistServerPagedRows && activeRs.length > 0)
    ) {
      combined = this.applyReferralSourceClientFilterRows(combined, activeRs);
    }
    if (this.worklistUsesEngageReferralQueryFilters()) {
      combined = refsFlat.length
        ? this.restrictWorklistCombinedToReferralPage(combined, refsFlat)
        : [];
    }
    return this.sortWorklistRowsByLatestActivityDesc(combined);
  }

  /**
   * Engage-only prospects: omit if list-clinic already has that referral id, or any row for the same patient
   * (clinic payloads often omit `referralId` while Engage has it — avoid a duplicate no-DOS Engage row).
   */
  private mergeEngageProspectsNotOnClinic(
    clinicRowsUnfiltered: Appointment[],
    engageRefs: EngageReferral[]
  ): Appointment[] {
    const seen = new Set<string>();
    const clinicPatientKeys = new Set<string>();
    for (const a of clinicRowsUnfiltered) {
      const rid = getAppointmentReferralId(a);
      if (rid) {
        seen.add(rid.toLowerCase());
      }
      const pk = (a.encountersPatient?.id ?? a.patientId ?? "").toString().trim().toLowerCase();
      if (pk) {
        clinicPatientKeys.add(pk);
      }
    }
    for (const ref of engageRefs) {
      const pid = this.extractPatientIdFromEngageReferral(ref).toLowerCase();
      if (pid && clinicPatientKeys.has(pid)) {
        const erefId = (ref.id ?? "").toString().trim().toLowerCase();
        if (erefId) {
          seen.add(erefId);
        }
      }
    }
    const extras: Appointment[] = [];
    for (const ref of engageRefs) {
      const rid = (extractEngageReferralRowId(ref) || (ref.id ?? "").toString()).trim();
      if (!rid || seen.has(rid.toLowerCase())) {
        continue;
      }
      const apt = this.mapEngageReferralToWorklistAppointment(ref);
      extras.push(apt);
      seen.add(rid.toLowerCase());
    }
    return extras;
  }

  private engageReferralSourceRaw(ref: EngageReferral): string {
    const r = ref as Record<string, unknown>;
    const named = this.directoryStringField(r, [
      "referral_source_name",
      "referralSourceName",
      "referral_source_display_name",
      "referralSourceDisplayName",
      "referral_source_label",
      "referralSourceLabel",
    ]);
    if (named) {
      return named;
    }
    const s = ref.source ?? ref.referralSource ?? ref.referral_source;
    if (typeof s === "string") {
      return s.trim();
    }
    if (s && typeof s === "object") {
      const o = s as Record<string, unknown>;
      const named =
        o["source_name"] ?? o["sourceName"] ?? o["name"] ?? o["label"] ?? o["display"];
      if (named != null && String(named).trim()) {
        return String(named).trim();
      }
      const v = o["source"] ?? o["id"];
      if (v != null) {
        return String(v).trim();
      }
    }
    return "";
  }

  /** Referral source **id** for worksheet facets (UUID/slug); {@link engageReferralSourceRaw} is display-first. */
  private engageReferralSourceIdFromRef(ref: EngageReferral): string {
    const r = ref as Record<string, unknown>;
    const fromFlat = this.directoryStringField(r, [
      "referral_source_id",
      "referralSourceId",
      "source_id",
      "sourceId",
    ]);
    if (fromFlat) {
      return fromFlat;
    }
    const s = ref.source ?? ref.referralSource ?? ref.referral_source;
    if (typeof s === "string") {
      return s.trim();
    }
    if (s && typeof s === "object") {
      const o = s as Record<string, unknown>;
      const vid =
        o["id"] ??
        o["source_id"] ??
        o["sourceId"] ??
        o["source"];
      if (vid != null && String(vid).trim()) {
        return String(vid).trim();
      }
    }
    const slug = this.directoryStringField(r, ["referral_source", "referralSource"]);
    return slug ? slug.trim() : "";
  }

  /** Engage referral stage row id (`stage_id` / `que_id`), when present on the referral payload. */
  private engageReferralQueueIdFromRef(ref: EngageReferral): string {
    const r = ref as Record<string, unknown>;
    const top = String(
      r["stage_id"] ??
        r["stageId"] ??
        r["que_id"] ??
        r["queId"] ??
        r["queue_id"] ??
        r["queueId"] ??
        ""
    ).trim();
    if (top) {
      return top;
    }
    const nestedStage = r["stage"];
    if (nestedStage && typeof nestedStage === "object") {
      const s = nestedStage as Record<string, unknown>;
      const fromStage = String(s["id"] ?? s["stageId"] ?? s["stage_id"] ?? "").trim();
      if (fromStage) {
        return fromStage;
      }
    }
    const nested = r["queue"];
    if (nested && typeof nested === "object") {
      const q = nested as Record<string, unknown>;
      return String(q["id"] ?? q["que_id"] ?? q["queueId"] ?? "").trim();
    }
    return "";
  }

  /** Human-readable stage name from Engage referral (not lead / patient pipeline). */
  private engageReferralQueueNameFromRef(ref: EngageReferral): string {
    const r = ref as Record<string, unknown>;
    const top = String(
      r["stage_name"] ??
        r["stageName"] ??
        r["queue_name"] ??
        r["que_name"] ??
        r["queueName"] ??
        r["queue_label"] ??
        r["queueLabel"] ??
        ""
    ).trim();
    if (top) {
      return top;
    }
    const nestedStage = r["stage"];
    if (nestedStage && typeof nestedStage === "object") {
      const s = nestedStage as Record<string, unknown>;
      const fromStage = String(
        s["stageName"] ?? s["stage_name"] ?? s["name"] ?? s["label"] ?? s["title"] ?? ""
      ).trim();
      if (fromStage) {
        return fromStage;
      }
    }
    const nested = r["queue"];
    if (nested && typeof nested === "object") {
      const q = nested as Record<string, unknown>;
      return String(q["name"] ?? q["label"] ?? q["title"] ?? q["display"] ?? "").trim();
    }
    return "";
  }

  private directoryStringField(
    r: Record<string, unknown>,
    keys: readonly string[]
  ): string {
    for (const k of keys) {
      const v = r[k];
      if (v == null || v === "") {
        continue;
      }
      if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
        const s = String(v).trim();
        if (s) {
          return s;
        }
      }
    }
    return "";
  }

  /** Recent DOS for worklist (API `last_dos`). */
  private engageReferralLastDosDateTime(ref: EngageReferral): string | undefined {
    const r = ref as Record<string, unknown>;
    const direct = this.directoryStringField(r, [
      "last_dos",
      "lastDos",
      "lastDosDateTime",
      "last_dos_date_time",
      "lastDosDate",
      "last_dos_date",
      "lastEncounterDateTime",
      "last_encounter_date_time",
      "lastEncounterDate",
      "last_encounter_date",
      "lastEncounterDos",
      "last_encounter_dos",
      "previousEncounterDateTime",
      "previous_encounter_date_time",
      "previousDos",
      "previous_dos",
    ]);
    if (direct) {
      return this.normalizeDirectoryDateTimeToEncounterIso(direct);
    }
    for (const key of [
      "lastEncounter",
      "last_encounter",
      "previousEncounter",
      "previous_encounter",
    ]) {
      const nest = r[key];
      if (nest == null || typeof nest !== "object" || Array.isArray(nest)) {
        continue;
      }
      const iso = this.followUpScheduleSummaryToEncounterIso(nest as Record<string, unknown>);
      if (iso) {
        return iso;
      }
    }
    return undefined;
  }

  /** Follow-up DOS/time for worklist Fol.Dos column (PHI directory / nested follow-up encounter). */
  private engageReferralFollowUpEncounterDateTime(ref: EngageReferral): string | undefined {
    const r = ref as Record<string, unknown>;
    const direct = this.directoryStringField(r, [
      "followUpDate",
      "follow_up_date",
      "followUpEncounterDateTime",
      "follow_up_encounter_date_time",
      "followUpDateTime",
      "follow_up_date_time",
      "followUpDos",
      "follow_up_dos",
      "nextFollowUpDate",
      "next_follow_up_date",
    ]);
    if (direct) {
      return this.normalizeDirectoryDateTimeToEncounterIso(direct);
    }
    for (const key of [
      "followUpEncounter",
      "follow_up_encounter",
      "followUpSchedule",
      "follow_up_schedule",
      "scheduledFollowUp",
      "scheduled_follow_up",
      "followUp",
      "follow_up",
    ]) {
      const nest = r[key];
      if (nest == null || typeof nest !== "object" || Array.isArray(nest)) {
        continue;
      }
      const iso = this.followUpScheduleSummaryToEncounterIso(nest as Record<string, unknown>);
      if (iso) {
        return iso;
      }
    }
    const schedInfo = r["scheduleInfo"] ?? r["schedule_info"];
    if (schedInfo != null && typeof schedInfo === "object" && !Array.isArray(schedInfo)) {
      const si = schedInfo as Record<string, unknown>;
      const fu = si["followUp"] ?? si["follow_up"];
      if (fu != null && typeof fu === "object" && !Array.isArray(fu)) {
        return this.followUpScheduleSummaryToEncounterIso(fu as Record<string, unknown>);
      }
    }
    return undefined;
  }

  private followUpScheduleSummaryToEncounterIso(
    o: Record<string, unknown>
  ): string | undefined {
    const dos = String(o["dos"] ?? o["DOS"] ?? "").trim();
    const dt = this.directoryStringField(o, [
      "encounterDateTime",
      "encounter_date_time",
      "dateOfService",
      "date_of_service",
      "scheduledDatetime",
      "scheduled_datetime",
      "followUpDate",
      "follow_up_date",
    ]);
    const raw = dos || dt;
    if (!raw) {
      return undefined;
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const time = String(
        o["procedureTime"] ??
          o["procedure_time"] ??
          o["checkIn"] ??
          o["check_in"] ??
          o["actualCheckIn"] ??
          o["actual_check_in"] ??
          ""
      ).trim();
      if (time && /\d/.test(time)) {
        for (const fmt of ["yyyy-MM-dd h:mm a", "yyyy-MM-dd HH:mm", "yyyy-MM-dd HH:mm:ss"]) {
          const parsed = DateTime.fromFormat(`${raw} ${time}`, fmt);
          if (parsed.isValid) {
            const iso = parsed.toISO({ includeOffset: false });
            if (iso) {
              return iso.slice(0, 19);
            }
          }
        }
      }
      return `${raw}T12:00:00`;
    }
    return this.normalizeDirectoryDateTimeToEncounterIso(raw);
  }

  private normalizeDirectoryDateTimeToEncounterIso(raw: string): string | undefined {
    const s = (raw ?? "").toString().trim();
    if (!s) {
      return undefined;
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      return `${s}T12:00:00`;
    }
    if (s.includes("T")) {
      return s;
    }
    const d = new Date(s);
    if (!isNaN(d.getTime())) {
      return d.toISOString().slice(0, 19);
    }
    return undefined;
  }

  private directoryResourceDisplay(r: Record<string, unknown>): string {
    const keyPrefer =
      this.directoryStringField(r, [
        "resource",
        "resourceName",
        "resource_name",
        "scheduleResourceName",
        "schedule_resource_name",
        "assignedResourceName",
        "assigned_resource_name",
        "providerDisplayName",
        "provider_display_name",
      ]) ||
      ((): string => {
        const raw = r["resource"] ?? r["resourceName"];
        if (raw && typeof raw === "object" && !Array.isArray(raw)) {
          const o = raw as Record<string, unknown>;
          return String(o["name"] ?? o["display"] ?? o["label"] ?? o["title"] ?? "").trim();
        }
        return "";
      })();
    const fallbackProv = this.directoryStringField(r, ["providerName", "provider_name"]);
    return (keyPrefer || fallbackProv).trim();
  }

  private mapEngageReferralToWorklistAppointment(ref: EngageReferral): Appointment {
    const patientId = this.extractPatientIdFromEngageReferral(ref);
    const referralId = (extractEngageReferralRowId(ref) || patientId || "").trim();
    const first = (ref.firstName ?? "").toString().trim();
    const last = (ref.lastName ?? "").toString().trim();
    const name = [first, last].filter(Boolean).join(" ").trim() || "Prospect";
    const r = ref as Record<string, unknown>;
    const dosRaw = this.directoryStringField(r, [
      "dos",
      "DOS",
      "dateOfService",
      "date_of_service",
      "serviceDate",
      "service_date",
      "scheduledDatetime",
      "scheduled_datetime",
      "encounterDateTime",
      "encounter_date_time",
      "scheduledDate",
      "scheduled_date",
      "appointmentDate",
      "appointment_date",
      "visitDate",
      "visit_date",
      "encounter_date",
    ]);
    const encounterDateTime = dosRaw
      ? this.normalizeDirectoryDateTimeToEncounterIso(dosRaw)
      : undefined;
    const latestEncounterIdFromDirectory =
      this.directoryStringField(r, ["latestEncounterId", "latest_encounter_id"]) ||
      null;
    const encId = this.directoryStringField(r, [
      "encounterId",
      "encounter_id",
      "latestEncounterId",
      "latest_encounter_id",
    ]);
    const scheduleStat = this.directoryStringField(r, [
      "scheduleStatus",
      "schedule_status",
      "encounterScheduleStatus",
      "encounter_schedule_status",
    ]);
    const encounterStat = this.directoryStringField(r, [
      "encounterStatus",
      "encounter_status",
      "status",
      "visit_status",
      "visitStatus",
    ]);
    const visitType = this.directoryStringField(r, [
      "visitType",
      "visit_type",
      "visitTypeName",
      "visit_type_name",
      "encounterVisitType",
      "encounter_visit_type",
    ]);
    const resourceId = this.directoryStringField(r, [
      "schedule_resource",
      "scheduleResource",
      "scheduleResourceId",
      "schedule_resource_id",
      "resourceId",
      "resource_id",
      "assignedResourceId",
      "assigned_resource_id",
    ]);
    const resourceName = this.directoryResourceDisplay(r);
    const procedureRoom = this.directoryStringField(r, [
      "procedureRoom",
      "procedure_room",
      "locationName",
      "location_name",
      "location",
      "clinicLocation",
      "clinic_location",
    ]);
    const neoRaw = this.directoryStringField(r, [
      "newOrEstablishedStatus",
      "new_or_established_status",
      "NewOrEstablishedStatus",
    ]);
    let newOrEstablishedStatus: string | undefined;
    if (neoRaw) {
      const compact = neoRaw.toLowerCase().replace(/[\s_-]+/g, "");
      if (compact === "newpatient" || compact === "established") {
        newOrEstablishedStatus = neoRaw;
      }
    }
    let payerName = this.directoryStringField(r, [
      "payerName",
      "payer_name",
      "primaryPayerName",
      "primary_payer_name",
      "payer",
      "primaryPayer",
      "primary_payer",
    ]);
    let eligibility = normalizeWorksheetDirectoryEligibility(
      r["eligibility"] ??
        r["eligibilityStatus"] ??
        r["eligibility_status"] ??
        r["payer_eligibility_status"]
    );
    const authorizationStatus = this.directoryStringField(r, [
      "authorizationStatus",
      "authorization_status",
      "authStatus",
      "auth_status",
    ]);
    const claimStatus = this.directoryStringField(r, [
      "claimStatus",
      "claim_status",
    ]);
    const billerLookup = r["biller_lookup"] ?? r["billerLookup"];
    if (billerLookup && typeof billerLookup === "object" && !Array.isArray(billerLookup)) {
      const bl = billerLookup as Record<string, unknown>;
      if (!payerName) {
        payerName =
          this.directoryStringField(bl, ["primary_payer_name", "primaryPayerName"]) ||
          payerName;
      }
      if (!eligibility) {
        const eligFromLookup = normalizeWorksheetDirectoryEligibility(
          bl["eligibility"] ?? bl["eligibilityStatus"] ?? bl["eligibility_status"]
        );
        if (eligFromLookup) {
          eligibility = eligFromLookup;
        }
      }
    }
    const payerStatus = this.directoryStringField(r, [
      "payerStatus",
      "payer_status",
      "payerEligibilityStatus",
      "payer_eligibility_status",
      "eligibilityStatus",
      "eligibility_status",
    ]);
    const paymentSourceFlat = this.directoryStringField(r, ["paymentSource", "payment_source"]);
    const paymentSource =
      [payerName, payerStatus].filter(Boolean).join(" · ").trim() ||
      paymentSourceFlat ||
      "";

    const procKey = (encId || referralId || "engage").trim() || "engage";
    const proc = new Procedure(procKey);
    proc.id = (encId || referralId || "engage").trim() || "engage";
    if (encId) {
      proc.encounterId = encId;
    }

    const lastAct = ref.last_activity;
    const updated =
      (lastAct && typeof lastAct === "object" && (lastAct as { createdDateTime?: string }).createdDateTime) ||
      undefined;
    const lead =
      (
        this.directoryStringField(r, ["lead_status", "leadStatus"]) ||
        (ref.lead_status ?? "").toString().trim()
      ).trim() || "";
    const pipelineStatus = this.directoryStringField(r, [
      "referralWorkflowStatus",
      "referral_workflow_status",
      "referralStatus",
      "referral_status",
      "patientStatus",
      "patient_status",
      "pipelineStatus",
      "pipeline_status",
    ]);
    const qid = this.engageReferralQueueIdFromRef(ref);
    const qname = this.engageReferralQueueNameFromRef(ref);
    const stage = (ref.stage ?? "").toString().trim();
    const { label: coordLabel, userId: coordUserId } = this.engageReferralCoordinatorFields(ref);
    const { id: workFlowId, name: workFlowName } = this.engageReferralWorkFlowFields(ref);
    const statusForEncFilter = (scheduleStat || encounterStat || "").trim();
    /** Enc. status column: clinical schedule/encounter only — Ref. status column uses {@link referralWorkflowStatus}. */
    const encounterStatusDisplay = statusForEncFilter;

    const referralSourceLabel = this.engageReferralSourceRaw(ref) || undefined;
    const referralSourceFacetId = this.engageReferralSourceIdFromRef(ref) || undefined;
    const serviceLabel = this.directoryStringField(r, [
      "service",
      "formProduct",
      "form_product",
    ]);
    const lastServiceLabel = this.pickLastServiceFromRow(r);
    const lastEncounterStatusLabel = this.pickLastEncounterStatusFromRow(r);
    const reasonOfVisitLabel = this.directoryStringField(r, [
      "reasonOfVisit",
      "reason_of_visit",
      "reasonForVisit",
      "reason_for_visit",
    ]);

    const row: Appointment & { assigned_to?: string; assignedTo?: string } = {
      referralId,
      patientId: patientId || undefined,
      encounterType: "Outpatient",
      providerId: "",
      providerName: resourceName || "",
      encounterDateTime,
      id: encId || undefined,
      referralSource: referralSourceLabel,
      referralSourceId: referralSourceFacetId,
      referralWorkflowStatus: lead || pipelineStatus || undefined,
      encounterStatus: encounterStatusDisplay || undefined,
      referralQueueId: qid || undefined,
      referralQueueLabel:
        (qname || (!qid && stage ? stage : "")).trim() || undefined,
      scheduleStatus: scheduleStat || encounterStat || undefined,
      visitType: visitType || undefined,
      scheduleResourceId: resourceId || undefined,
      resourceId: resourceId || undefined,
      resourceName: resourceName || undefined,
      paymentSource: paymentSource || undefined,
      newOrEstablishedStatus,
      coordinatorName: coordLabel || undefined,
      workFlowId: workFlowId || undefined,
      workFlowName: workFlowName || undefined,
      createdAt:
        (ref.createdAt ?? "").toString().trim() ||
        this.directoryStringField(r, ["created_at", "createdAt", "CreatedAt"]) ||
        undefined,
      updatedAt: typeof updated === "string" ? updated : undefined,
      worksheetEngageIconCount: worksheetRowEngageIconCountFromReferral(ref),
      worksheetUnreadMessageCount: worksheetRowUnreadMessageCountFromReferral(ref),
      worksheetNotificationsChannelCount:
        worksheetRowNotificationsChannelCountFromReferral(ref),
      worksheetReminderIconCount: worksheetRowReminderIconCountFromReferral(ref),
      encountersPatient: {
        id: patientId || "",
        name,
        dob: (ref.dob ?? "").toString(),
        gender: (ref.gender ?? "").toString(),
        age: 0,
        referralId,
      },
      encounterProcedure: proc,
      isConfirmed: false,
      ...(serviceLabel ? { service: serviceLabel } : {}),
      ...(lastServiceLabel ? { lastService: lastServiceLabel } : {}),
      ...(lastEncounterStatusLabel
        ? { lastEncounterStatus: lastEncounterStatusLabel }
        : {}),
      ...(reasonOfVisitLabel ? { reasonOfVisit: reasonOfVisitLabel } : {}),
      ...(payerName ? { primaryPayerName: payerName } : {}),
      ...(eligibility ? { eligibility } : {}),
      ...(authorizationStatus ? { authorizationStatus } : {}),
      ...(claimStatus ? { claimStatus } : {}),
      latestEncounterId: latestEncounterIdFromDirectory,
    };

    const dosDateOnly =
      dosRaw && /^\d{4}-\d{2}-\d{2}$/.test(dosRaw)
        ? dosRaw
        : encounterDateTime && /^\d{4}-\d{2}-\d{2}/.test(encounterDateTime)
          ? encounterDateTime.slice(0, 10)
          : "";
    /** Always attach schedule so worksheet columns read DOS/time, enc status, visit type, resource, location consistently. */
    const schedKey = (encId || referralId || "engage").trim() || "engage";
    const sched = new EncounterSchedule(schedKey, dosDateOnly || undefined);
    sched.scheduleStatus = scheduleStat || encounterStat || "";
    sched.visitType = visitType;
    sched.scheduleResourceId = resourceId;
    if (procedureRoom) {
      sched.procedureRoom = procedureRoom;
    }
    row.encounterSchedule = sched;

    if (coordUserId) {
      row.assigned_to = coordUserId;
      row.assignedTo = coordUserId;
    }
    if (ref.last_activity != null) {
      (row as Appointment & { last_activity?: EngageReferral["last_activity"] }).last_activity =
        ref.last_activity;
    }
    const followUpDt = this.engageReferralFollowUpEncounterDateTime(ref);
    if (followUpDt) {
      row.followUpEncounterDateTime = followUpDt;
    }
    const lastDosDt = this.engageReferralLastDosDateTime(ref);
    if (lastDosDt) {
      row.lastDosDateTime = lastDosDt;
    }
    return row;
  }

  /** Patient id from directory / referral row (camelCase + snake_case). */
  private extractPatientIdFromEngageReferral(ref: EngageReferral): string {
    const r = ref as Record<string, unknown>;
    const top = (ref.patientId ?? r["patient_id"] ?? r["PatientId"] ?? r["patientID"] ?? "")
      .toString()
      .trim();
    if (top) {
      return top;
    }
    const arr = r["patientIds"] ?? r["patient_ids"];
    if (Array.isArray(arr)) {
      for (const x of arr) {
        const s = String(x ?? "").trim();
        if (s) {
          return s;
        }
      }
    }
    return "";
  }

  /** PHI `POST …/encounters/latest-by-patient-ids` — used only to enrich **payer** (+ encounter id for coverage fetch). */
  private extractPayerSummaryFromLatestEncounterRow(row: Record<string, unknown>): string {
    const uniqParts = (vals: string[]): string =>
      [...new Set(vals.map((x) => x.trim()).filter(Boolean))].join(" · ").trim();
    const flat = [
      row["payerStatus"],
      row["payer_status"],
      row["eligibilityStatus"],
      row["eligibility_status"],
      row["paymentSource"],
      row["payment_source"],
      row["payerName"],
      row["payer_name"],
      row["primaryPayerName"],
      row["primary_payer_name"],
      row["insuranceStatus"],
      row["insurance_status"],
    ]
      .map((x) => String(x ?? "").trim())
      .filter(Boolean);
    const nested =
      row["primaryInsurance"] ??
      row["primary_insurance"] ??
      row["coverage"] ??
      row["insurance"];
    if (nested && typeof nested === "object" && !Array.isArray(nested)) {
      const o = nested as Record<string, unknown>;
      flat.push(
        ...[
          o["payerName"],
          o["payer_name"],
          o["name"],
          o["status"],
          o["eligibility"],
          o["eligibilityStatus"],
        ]
          .map((x) => String(x ?? "").trim())
          .filter(Boolean)
      );
    }
    return uniqParts(flat);
  }

  private worklistLatestEncounterPatientCacheKey(patientId: string): string {
    return String(patientId ?? "").trim().toLowerCase();
  }

  private getCachedLatestEncountersForPatientIds(
    patientIds: string[]
  ): { patientId: string; encounterId: string; payerSummary: string }[] {
    const out: { patientId: string; encounterId: string; payerSummary: string }[] = [];
    for (const id of patientIds) {
      const hit = this.worklistLatestEncounterByPatientId.get(
        this.worklistLatestEncounterPatientCacheKey(id)
      );
      if (hit) {
        out.push(hit);
      }
    }
    return out;
  }

  private storeLatestEncountersInWorklistCache(
    rows: { patientId: string; encounterId: string; payerSummary: string }[]
  ): void {
    for (const row of rows) {
      const key = this.worklistLatestEncounterPatientCacheKey(row.patientId);
      if (key) {
        this.worklistLatestEncounterByPatientId.set(key, row);
      }
    }
  }

  private fetchLatestEncountersByPatientIds(
    patientIds: string[]
  ): Observable<{ patientId: string; encounterId: string; payerSummary: string }[]> {
    const unique = [...new Set(patientIds.map((x) => String(x ?? "").trim()).filter(Boolean))];
    if (!unique.length) {
      return of([]);
    }
    const cached = this.getCachedLatestEncountersForPatientIds(unique);
    const missing = unique.filter(
      (id) =>
        !this.worklistLatestEncounterByPatientId.has(
          this.worklistLatestEncounterPatientCacheKey(id)
        )
    );
    if (!missing.length) {
      return of(cached);
    }
    const url = this.apiBaseUrl + "api/v1/encounters/latest-by-patient-ids";
    return this._dataService.post(url, { patientIds: missing }).pipe(
      map((res: unknown) => {
        if (!Array.isArray(res)) {
          return [];
        }
        return (res as Record<string, unknown>[]).map((row) => {
          const pid = String(
            row["patientId"] ?? row["patient_id"] ?? row["PatientId"] ?? ""
          ).trim();
          const encounterId = String(
            row["encounterId"] ?? row["encounter_id"] ?? row["id"] ?? ""
          ).trim();
          const payerSummary = this.extractPayerSummaryFromLatestEncounterRow(row);
          return { patientId: pid, encounterId, payerSummary };
        });
      }),
      tap((fresh) => this.storeLatestEncountersInWorklistCache(fresh)),
      map((fresh) => {
        const merged = [...cached];
        const seen = new Set(
          cached.map((r) => this.worklistLatestEncounterPatientCacheKey(r.patientId))
        );
        for (const row of fresh) {
          const key = this.worklistLatestEncounterPatientCacheKey(row.patientId);
          if (key && !seen.has(key)) {
            merged.push(row);
            seen.add(key);
          }
        }
        return merged;
      }),
      catchError(() => of(cached))
    );
  }

  private applyLatestPhiEncountersToAppointments(
    rows: Appointment[],
    latest: { patientId: string; encounterId: string; payerSummary: string }[]
  ): Appointment[] {
    const byPid = new Map<string, { patientId: string; encounterId: string; payerSummary: string }>();
    for (const row of latest) {
      const k = (row.patientId ?? "").toString().trim().toLowerCase();
      if (k) {
        byPid.set(k, row);
      }
    }
    return rows.map((appt) => {
      const pk = (appt.encountersPatient?.id ?? appt.patientId ?? "").toString().trim().toLowerCase();
      if (!pk) {
        return appt;
      }
      const hit = byPid.get(pk);
      if (!hit) {
        return appt;
      }
      const next = { ...appt } as Appointment;
      const encId = (hit.encounterId ?? "").trim();
      const hadStableEncounterId = !!(next.id ?? "").toString().trim();
      if (encId && !hadStableEncounterId) {
        next.id = encId;
        if (next.encounterProcedure) {
          next.encounterProcedure.encounterId = encId;
          const pid = (next.referralId ?? "").toString().trim();
          if (
            !(next.encounterProcedure.id ?? "").toString().trim() ||
            (pid && next.encounterProcedure.id === pid)
          ) {
            next.encounterProcedure.id = encId;
          }
        }
      }
      const payer = (hit.payerSummary ?? "").trim();
      const existingPs = (next.paymentSource ?? "").toString().trim();
      if (payer && !existingPs) {
        next.paymentSource = payer;
      }
      return next;
    });
  }

  private enrichReferralMappedRowsWithLatestPhi$(
    rows: Appointment[],
    refsFlat: EngageReferral[],
    /** Worklist directory page: page 1 may POST latest-by-patient-ids; scroll pages 2+ use cache only. */
    worklistDirectoryPage?: number
  ): Observable<Appointment[]> {
    if (this.shouldSkipWorklistFilteredDirectoryFollowUps()) {
      return of(rows);
    }
    const patientIds = refsFlat
      .map((r) => this.extractPatientIdFromEngageReferral(r))
      .filter((id) => !!id);
    if (!patientIds.length) {
      return of(rows);
    }
    const cachedLatest = this.getCachedLatestEncountersForPatientIds(patientIds);
    let enriched = this.applyLatestPhiEncountersToAppointments(rows, cachedLatest);
    if (this.worklistMergeEngageProspects && (worklistDirectoryPage ?? 1) > 1) {
      return of(enriched);
    }
    if (this.worklistNonBlockingLatestPhiEnrichment) {
      this.scheduleWorklistLatestPhiBackgroundEnrichment(patientIds);
      return of(enriched);
    }
    return this.fetchLatestEncountersByPatientIds(patientIds).pipe(
      map((latest) => this.applyLatestPhiEncountersToAppointments(rows, latest))
    );
  }

  private scheduleWorklistLatestPhiBackgroundEnrichment(patientIds: string[]): void {
    const missing = patientIds.filter(
      (id) =>
        !this.worklistLatestEncounterByPatientId.has(
          this.worklistLatestEncounterPatientCacheKey(id)
        )
    );
    if (!missing.length) {
      return;
    }
    const seq = ++this.worklistLatestPhiBackgroundSeq;
    this.fetchLatestEncountersByPatientIds(missing)
      .pipe(take(1), catchError(() => of([])))
      .subscribe(() => {
        if (seq !== this.worklistLatestPhiBackgroundSeq) {
          return;
        }
        this.applyWorklistLatestPhiEnrichmentToLiveLists(patientIds);
        this.worklistLatestPhiEnrichmentApplied$.next();
      });
  }

  private applyWorklistLatestPhiEnrichmentToLiveLists(patientIds: string[]): void {
    const latest = this.getCachedLatestEncountersForPatientIds(patientIds);
    if (!latest.length) {
      return;
    }
    if (this.admissionList?.length) {
      this.admissionList = this.applyLatestPhiEncountersToAppointments(
        this.admissionList,
        latest
      );
    }
    const hit = this.worklistMergedRowsCache;
    if (hit?.combined?.length) {
      hit.combined = this.applyLatestPhiEncountersToAppointments(hit.combined, latest);
    }
  }

  /** When referral sources are chosen, the worklist uses PHI patient directory only — no list-clinic merge. */
  private syntheticEmptyListClinicResponse(
    page: number,
    pageSize: number
  ): PaginatedAppointmentResponse {
    return {
      data: [],
      totalRowsCount: 0,
      pageStart: Math.max(0, (page - 1) * pageSize),
      pageLength: pageSize,
      totalPages: 1,
      currentPage: page,
    };
  }

  getAdmissionsList(
    page: number = 1,
    pageSize: number = 25,
    searchQuery: string = "",
    opts?: GetAdmissionsListOptions
  ): Observable<PaginatedAppointmentResponse> {
    if (!this.worklistMergeEngageProspects) {
      this.worklistServerPagedRows = false;
    }
    const pageConfig = new AdmissionListPayload(this.selectedDate);
    /** Worklist merge: list-clinic always uses a broad DOS window (2020 → today) for full clinic coverage. */
    if (this.worklistMergeEngageProspects) {
      const { startIso, endIso } = this.getWorklistListClinicDosDateRangeIso();
    pageConfig.filterDate = {
        startDate: startIso,
        endDate: endIso,
    };
  }

    // Get selected provider filter values
    const selectedProviders = this.physicianFilter
      .filter((p) => p.value !== "All" && p.isSelected)
      .map((p) => p.value);

    // Get selected resource filter values
    const selectedResources = this.resourcesFilter
      .filter((r) => r.value !== "All" && r.isSelected)
      .map((r) => r.value);

    // Apply provider filters if selected
    if (selectedProviders.length > 0) {
      pageConfig.providers = selectedProviders;
    }

    // Apply resource filters if selected
    if (selectedResources.length > 0) {
      pageConfig.resources = selectedResources;
    }

    pageConfig.filterStatus = this.encounerStatusList
      .filter((p) => p.value !== "All" && p.isSelected)
      .map((p) => p.value);

    const selectedAuthStatuses = this.authStatusFilterList
      .filter((p) => p.isSelected)
      .map((p) => p.value);
    if (selectedAuthStatuses.length > 0) {
      pageConfig.authStatus = selectedAuthStatuses;
    }
    this.applyConfirmationStatusFilter(pageConfig);

    pageConfig.filterLocation = this.locationFilter
      .filter((p) => p.value !== "All" && p.isSelected)
      .map((p) => p.value);

    // Get selected visit type filter values
    const selectedVisitTypes = this.visitTypeFilter
      .filter((vt) => vt.value !== "All" && vt.isSelected)
      .map((vt) => vt.value);

    // Apply visit type filters if selected
    if (selectedVisitTypes.length > 0) {
      pageConfig.visitType = selectedVisitTypes;
    }

    const activeReferralSources = this.getActiveReferralSourceValues();
    if (activeReferralSources.length > 0) {
      pageConfig.referralSources = activeReferralSources;
    }
    // Fallback when backend ignores referralSources: fetch a broad page and paginate client-side.
    const referralSourceClientPag =
      activeReferralSources.length > 0 && pageSize < 1000;

    let forceClientReferralPagination: boolean;
    if (this.worklistMergeEngageProspects) {
      this.worklistServerPagedRows = true;
      forceClientReferralPagination = false;
      pageConfig.pageStart = Math.max(0, (page - 1) * pageSize);
      pageConfig.pageLength = pageSize;
    } else {
      forceClientReferralPagination = referralSourceClientPag;
      pageConfig.pageStart = forceClientReferralPagination ? 0 : (page - 1) * pageSize;
      pageConfig.pageLength = forceClientReferralPagination ? 10_000 : pageSize;
    }
    pageConfig.sortField = "encounterDateTime";
    pageConfig.sortDirection = "DESC";
    pageConfig.searchQuery = searchQuery || this.searchQuery || "";
    const listClinicSearchForPager = pageConfig.searchQuery ?? "";

    const directoryPageLimit =
      opts?.directoryPageLimit != null && Number(opts.directoryPageLimit) > 0
        ? Math.floor(Number(opts.directoryPageLimit))
        : undefined;
    const pipelineDirectoryOpts: WorklistDirectoryPageFetchOpts | undefined =
      directoryPageLimit != null ? { pageLimit: directoryPageLimit } : undefined;

    const capWorklistMergedRows = (rows: Appointment[]): Appointment[] =>
      rows.length > pageSize ? rows.slice(0, pageSize) : rows;
    const listClinicPagerResponse = (
      clinicRes: PaginatedAppointmentResponse
    ): PaginatedAppointmentResponse => ({
      ...clinicRes,
      currentPage: page,
      pageLength: pageSize,
      pageStart: Math.max(0, (page - 1) * pageSize),
      totalPages:
        clinicRes.totalPages && clinicRes.totalPages > 0
          ? clinicRes.totalPages
          : Math.max(1, Math.ceil((clinicRes.totalRowsCount ?? 0) / pageSize) || 1),
    });

    if (this.worklistMergeEngageProspects) {
      const sig = this.worklistPageCacheSignature(pageSize, searchQuery, page);
      const hit = this.worklistMergedRowsCache;
      if (hit?.sig === sig && !opts?.bypassMergedRowsCache) {
        return of(
          this.finalizePaginatedList(
            capWorklistMergedRows(hit.combined),
            page,
            pageSize,
            false,
            listClinicPagerResponse(hit.clinicResponse),
            false,
            hit.referralsApiTotal,
            listClinicSearchForPager
          )
        );
      }

      const qEarly = (searchQuery || this.searchQuery || "").trim();
      const engageReferralSourceOnly =
        activeReferralSources.length > 0 &&
        !qEarly &&
        !this.worklistToolbarEngageSelections.length &&
        !this.worklistMergeEngageFromReferralsApi;

      if (engageReferralSourceOnly) {
        return this.fetchWorklistReferralPagesBatch([page], pipelineDirectoryOpts).pipe(
          switchMap(({ byPage, total }) => {
            const refsFlat = this.flattenWorklistReferralRows([], byPage);
            let combined = refsFlat.map((r) => this.mapEngageReferralToWorklistAppointment(r));
            combined = this.enrichWorksheetRowsWithEngageReferralCounts(combined, refsFlat);
            combined = this.sortWorklistRowsByLatestActivityDesc(combined);
            return this.enrichReferralMappedRowsWithLatestPhi$(combined, refsFlat, page).pipe(
              map((enriched) => {
                const capped = capWorklistMergedRows(enriched);
                const clinicSynth = this.syntheticEmptyListClinicResponse(page, pageSize);
                const referralsHasMore = this.computeReferralsHasMore(total, [], byPage);
                this.worklistMergedRowsCache = {
                  sig,
                  clinicAll: [],
                  processedData: [],
                  applyReferralClientFilter: true,
                  activeRs: [...activeReferralSources],
                  clinicResponse: clinicSynth,
                  bootstrapReferrals: [],
                  referralsByPage: byPage,
                  referralsApiTotal: total,
                  referralsHasMore,
                  combined: capped,
                };
                return this.finalizePaginatedList(
                  capped,
                  page,
                  pageSize,
                  false,
                  listClinicPagerResponse(clinicSynth),
                  false,
                  total,
                  listClinicSearchForPager
                );
              })
            );
          }),
          catchError(() => {
            const clinicSynth = this.syntheticEmptyListClinicResponse(page, pageSize);
            const combined: Appointment[] = [];
            this.worklistMergedRowsCache = {
              sig,
              clinicAll: [],
              processedData: [],
              applyReferralClientFilter: true,
              activeRs: [...activeReferralSources],
              clinicResponse: clinicSynth,
              bootstrapReferrals: [],
              referralsByPage: new Map(),
              referralsApiTotal: null,
              referralsHasMore: false,
              combined,
            };
            return of(
              this.finalizePaginatedList(
                combined,
                page,
                pageSize,
                false,
                listClinicPagerResponse(clinicSynth),
                false,
                null,
                listClinicSearchForPager
              )
            );
          })
        );
      }

      const textQ = (searchQuery || this.searchQuery || "").trim();
      const qTrimWorklist = !!textQ || this.worklistToolbarEngageSelections.length > 0;
      const applyRsClient = activeReferralSources.length > 0;
      const activeRsLocal = [...activeReferralSources];
      const clinicSynthMerged = this.syntheticEmptyListClinicResponse(page, pageSize);
      const pagerSynthMerged = listClinicPagerResponse(clinicSynthMerged);

      const cacheMergedReferralRows = (
        rowsIn: Appointment[],
        byPage: Map<number, EngageReferral[]>,
        total: number | null,
        bootstrapRefs: EngageReferral[],
        refsForPhi: EngageReferral[]
      ): Observable<PaginatedAppointmentResponse> =>
        this.enrichReferralMappedRowsWithLatestPhi$(rowsIn, refsForPhi, page).pipe(
          map((enriched) => {
            const rows = capWorklistMergedRows(enriched);
            const referralsHasMore = this.computeReferralsHasMore(total, bootstrapRefs, byPage);
            this.worklistMergedRowsCache = {
              sig,
              clinicAll: [],
              processedData: [],
              applyReferralClientFilter: applyRsClient,
              activeRs: activeRsLocal,
              clinicResponse: clinicSynthMerged,
              bootstrapReferrals: bootstrapRefs,
              referralsByPage: byPage,
              referralsApiTotal: total,
              referralsHasMore,
              combined: rows,
            };
            return this.finalizePaginatedList(
              rows,
              page,
              pageSize,
              false,
              pagerSynthMerged,
              false,
              total,
              listClinicSearchForPager
            );
          })
        );

      if (qTrimWorklist) {
        const selectionsOnly =
          this.worklistToolbarEngageSelections.length > 0 && !textQ;
        const batch$ = selectionsOnly
          ? of({
              byPage: new Map<number, EngageReferral[]>(),
              total: null as number | null,
            })
          : this.fetchWorklistReferralPagesBatch([page], pipelineDirectoryOpts);
        return forkJoin({
          batch: batch$,
          search: this.fetchEngageBootstrapForWorklistMerge(),
        }).pipe(
          switchMap(({ batch, search }) => {
            const bootstrap = search ?? [];
            let combined = this.buildWorklistMergedCombinedFromParts(
              [],
              [],
              bootstrap,
              batch.byPage,
              applyRsClient,
              activeRsLocal
            );
            const refsForPhi = this.flattenWorklistReferralRows(bootstrap, batch.byPage);
            const effectiveTotal =
              selectionsOnly && bootstrap.length > 0 ? bootstrap.length : batch.total;
            return cacheMergedReferralRows(
              combined,
              batch.byPage,
              effectiveTotal,
              bootstrap,
              refsForPhi
            );
          }),
          catchError(() => {
            this.worklistMergedRowsCache = {
              sig,
              clinicAll: [],
              processedData: [],
              applyReferralClientFilter: applyRsClient,
              activeRs: activeRsLocal,
              clinicResponse: clinicSynthMerged,
              bootstrapReferrals: [],
              referralsByPage: new Map(),
              referralsApiTotal: null,
              referralsHasMore: false,
              combined: [],
            };
            return of(
              this.finalizePaginatedList(
                [],
                page,
                pageSize,
                false,
                pagerSynthMerged,
                false,
                null,
                listClinicSearchForPager
              )
            );
          })
        );
      }

      return this.fetchWorklistReferralPagesBatch([page], pipelineDirectoryOpts).pipe(
        switchMap(({ byPage, total }) => {
          let combined = this.buildWorklistMergedCombinedFromParts(
            [],
            [],
            [],
            byPage,
            applyRsClient,
            activeRsLocal
          );
          const refsFlat = this.flattenWorklistReferralRows([], byPage);
          return cacheMergedReferralRows(combined, byPage, total, [], refsFlat);
        }),
        catchError(() => {
          this.worklistMergedRowsCache = {
            sig,
            clinicAll: [],
            processedData: [],
            applyReferralClientFilter: applyRsClient,
            activeRs: activeRsLocal,
            clinicResponse: clinicSynthMerged,
            bootstrapReferrals: [],
            referralsByPage: new Map(),
            referralsApiTotal: null,
            referralsHasMore: false,
            combined: [],
          };
          return of(
            this.finalizePaginatedList(
              [],
              page,
              pageSize,
              false,
              pagerSynthMerged,
              false,
              null,
              listClinicSearchForPager
            )
          );
        })
      );
    }

    const url = this.apiBaseUrl + "api/v1/encounters/list-clinic";
    return this.getWorklistListClinicPost$(url, pageConfig).pipe(
      switchMap((response: PaginatedAppointmentResponse) => {
        const clinicAll = this.buildProcessedClinicRows(response, pageConfig);
        const activeRs = this.getActiveReferralSourceValues();
        const applyReferralClientFilter = activeRs.length > 0;
        let processedData = clinicAll;
        if (applyReferralClientFilter) {
          processedData = this.applyReferralSourceClientFilterRows(clinicAll, activeRs);
        }

        if (!this.worklistMergeEngageProspects) {
          return of(
            this.finalizePaginatedList(
              processedData,
              page,
              pageSize,
              forceClientReferralPagination,
              response,
              applyReferralClientFilter,
              undefined,
              listClinicSearchForPager
            )
          );
        }

        const sig = this.worklistPageCacheSignature(pageSize, searchQuery, page);
        const qTrim = (searchQuery || this.searchQuery || "").trim();
        const pagerRes = listClinicPagerResponse(response);

        if (qTrim || this.worklistToolbarEngageSelections.length > 0) {
          return this.fetchEngageBootstrapForWorklistMerge().pipe(
          map((refs) => {
            const extras = this.mergeEngageProspectsNotOnClinic(clinicAll, refs);
            let combined = processedData.concat(extras);
            combined = this.enrichWorksheetRowsWithEngageReferralCounts(combined, refs);
              combined = this.applyClinicPatientDemographicsFallback(combined, clinicAll);
            if (applyReferralClientFilter) {
              combined = this.applyReferralSourceClientFilterRows(combined, activeRs);
            }
            combined = this.sortWorklistRowsByLatestActivityDesc(combined);
              combined = capWorklistMergedRows(combined);
            this.worklistMergedRowsCache = {
              sig,
                clinicAll,
                processedData,
                applyReferralClientFilter,
                activeRs,
              clinicResponse: response,
                bootstrapReferrals: [],
                referralsByPage: new Map(),
                referralsApiTotal: null,
                referralsHasMore: false,
                combined,
            };
            return this.finalizePaginatedList(
              combined,
              page,
              pageSize,
                false,
                pagerRes,
                false,
                null,
                listClinicSearchForPager
            );
          }),
          catchError(() => {
              const sortedErr = capWorklistMergedRows(
                this.sortWorklistRowsByLatestActivityDesc(processedData)
              );
            this.worklistMergedRowsCache = {
              sig,
                clinicAll,
                processedData,
                applyReferralClientFilter,
                activeRs,
                clinicResponse: response,
                bootstrapReferrals: [],
                referralsByPage: new Map(),
                referralsApiTotal: null,
                referralsHasMore: false,
              combined: sortedErr,
              };
              return of(
                this.finalizePaginatedList(
                  sortedErr,
                  page,
                  pageSize,
                  false,
                  pagerRes,
                  false,
                  null,
                  listClinicSearchForPager
                )
              );
            })
          );
        }

        if (this.worklistMergeEngageFromReferralsApi) {
          return this.fetchWorklistReferralPagesBatch([page], pipelineDirectoryOpts).pipe(
            switchMap(({ byPage, total }) => {
              const referralsHasMore = this.computeReferralsHasMore(total, [], byPage);
              let combined = this.buildWorklistMergedCombinedFromParts(
                clinicAll,
                processedData,
                [],
                byPage,
                applyReferralClientFilter,
                activeRs
              );
              combined = capWorklistMergedRows(combined);
              const hit: WorklistMergedRowsCache = {
                sig,
                clinicAll,
                processedData,
                applyReferralClientFilter,
                activeRs,
              clinicResponse: response,
                bootstrapReferrals: [],
                referralsByPage: byPage,
                referralsApiTotal: total,
                referralsHasMore,
                combined,
              };
              this.worklistMergedRowsCache = hit;
              return of(
                this.finalizePaginatedList(
                  combined,
                  page,
                  pageSize,
                  false,
                  pagerRes,
                  false,
                  total,
                  listClinicSearchForPager
                )
              );
            }),
            catchError(() => {
              const sortedErr = capWorklistMergedRows(
                this.sortWorklistRowsByLatestActivityDesc(processedData)
              );
              this.worklistMergedRowsCache = {
                sig,
                clinicAll,
                processedData,
                applyReferralClientFilter,
                activeRs,
                clinicResponse: response,
                bootstrapReferrals: [],
                referralsByPage: new Map(),
                referralsApiTotal: null,
                referralsHasMore: false,
                combined: sortedErr,
            };
            return of(
              this.finalizePaginatedList(
                sortedErr,
                page,
                pageSize,
                  false,
                  pagerRes,
                  false,
                  null,
                  listClinicSearchForPager
                )
              );
            })
          );
        }

        return this.fetchWorklistReferralPagesBatch([page], pipelineDirectoryOpts).pipe(
          switchMap(({ byPage, total }) => {
            const referralsHasMore = this.computeReferralsHasMore(total, [], byPage);
            let combined = this.buildWorklistMergedCombinedFromParts(
              clinicAll,
              processedData,
              [],
              byPage,
              applyReferralClientFilter,
              activeRs
            );
            combined = capWorklistMergedRows(combined);
            const hit: WorklistMergedRowsCache = {
              sig,
              clinicAll,
              processedData,
              applyReferralClientFilter,
              activeRs,
            clinicResponse: response,
              bootstrapReferrals: [],
              referralsByPage: byPage,
              referralsApiTotal: total,
              referralsHasMore,
              combined,
            };
            this.worklistMergedRowsCache = hit;
            return of(
              this.finalizePaginatedList(
                combined,
                page,
                pageSize,
                false,
                pagerRes,
                false,
                total,
                listClinicSearchForPager
              )
            );
          }),
          catchError(() => {
            const sortedErr = capWorklistMergedRows(
              this.sortWorklistRowsByLatestActivityDesc(processedData)
            );
            this.worklistMergedRowsCache = {
              sig,
              clinicAll,
              processedData,
              applyReferralClientFilter,
              activeRs,
              clinicResponse: response,
              bootstrapReferrals: [],
              referralsByPage: new Map(),
              referralsApiTotal: null,
              referralsHasMore: false,
              combined: sortedErr,
            };
            return of(
              this.finalizePaginatedList(
                sortedErr,
                page,
                pageSize,
                false,
                pagerRes,
                false,
                null,
                listClinicSearchForPager
              )
            );
          })
        );
      }),
      tap({
        error: (err) => {
},
      })
    );
  }

  // Get all encounters for a specific date to check for duplicates
  getEncountersForDate(scheduleDate: string): Observable<Appointment[]> {
    const dateObj = DateTime.fromISO(scheduleDate);
    const payload = {
      providers: [],
      resources: [],
      filterStatus: [],
      filterLocation: [],
      visitType: [],
      filterDate: {
        startDate: dateObj.startOf('day').toFormat("yyyy-MM-dd'T'HH:mm:ss"),
        endDate: dateObj.endOf('day').toFormat("yyyy-MM-dd'T'HH:mm:ss"),
      },
      pageStart: 0,
      pageLength: 1000, // Get all records for the date
      sortField: "encounterDateTime",
      sortDirection: "DESC" as "DESC",
      searchQuery: ""
    };
    const url = this.apiBaseUrl + "api/v1/encounters/list-clinic";
    return this._dataService.post(url, payload).pipe(
      map((response: PaginatedAppointmentResponse) => response.data)
    );
  }

  getPatientsByDob(dob: string) {
    const dobArray = dob?.split("-");
    if (dobArray?.length !== 3) {
      return;
    }

    const term = `${dobArray[1]}${dobArray[2]}${dobArray[0]}`;
    // Cache-bust via query param — custom Cache-Control/Pragma headers break CORS preflight on PHI.
    const url =
      this.apiBaseUrl +
      `api/v1/patients/search/term?term=${term}&_=${Date.now()}`;
    return this._dataService.get(url);
  }

  /** Cached encounter_status auth fields when list-clinic rows omit them. */
  private readonly encounterAuthFieldsById = new Map<
    string,
    Pick<Appointment, "authRequirementStatus" | "authStatus">
  >();

  mergeAuthFieldsIntoAdmissionList(
    updated: Appointment | null | undefined
  ): void {
    const id = (updated?.id ?? "").trim();
    if (!id) {
      return;
    }
    const authRequirementStatus = (updated.authRequirementStatus ?? "").trim();
    const authStatus = (updated.authStatus ?? "").trim();
    if (authRequirementStatus || authStatus) {
      this.encounterAuthFieldsById.set(id, {
        authRequirementStatus: authRequirementStatus || null,
        authStatus: authStatus || null,
      });
    }
    for (const appt of this.admissionList ?? []) {
      if ((appt.id ?? "").trim() !== id) {
        continue;
      }
      if (authRequirementStatus) {
        appt.authRequirementStatus = authRequirementStatus;
      }
      if (authStatus) {
        appt.authStatus = authStatus;
      }
      break;
    }
  }

  private applyCachedAuthFieldsToAppointment(appt: Appointment): void {
    const id = (appt.id ?? "").trim();
    if (!id) {
      return;
    }
    const cached = this.encounterAuthFieldsById.get(id);
    if (!cached) {
      return;
    }
    if (!(appt.authRequirementStatus ?? "").trim() && cached.authRequirementStatus) {
      appt.authRequirementStatus = cached.authRequirementStatus;
    }
    if (!(appt.authStatus ?? "").trim() && cached.authStatus) {
      appt.authStatus = cached.authStatus;
    }
  }

  /**
   * list-clinic often omits encounter_status auth columns on deployed PHI; details includes them.
   */
  hydrateAppointmentAuthFieldsFromDetails(
    appointments: Appointment[]
  ): Observable<void> {
    const rows = appointments ?? [];
    for (const appt of rows) {
      this.applyCachedAuthFieldsToAppointment(appt);
    }

    const ids = [
      ...new Set(
        rows
          .filter((appt) => {
            const id = (appt.id ?? "").trim();
            if (!id || !isValidUuid(id)) {
              return false;
            }
            this.applyCachedAuthFieldsToAppointment(appt);
            return resolveEncounterRequiresAuthForListView(appt) === undefined;
          })
          .map((appt) => (appt.id ?? "").trim())
      ),
    ];

    if (!ids.length) {
      return of(undefined);
    }

    return forkJoin(
      ids.map((id) =>
        this.getAppointmentById(id).pipe(
          catchError(() => of(null)),
          map((detail) => ({ id, detail }))
        )
      )
    ).pipe(
      tap((pairs) => {
        for (const { id, detail } of pairs) {
          if (!detail) {
            continue;
          }
          const authRequirementStatus = (
            detail.authRequirementStatus ?? ""
          ).trim();
          const authStatus = (detail.authStatus ?? "").trim();
          if (authRequirementStatus || authStatus) {
            this.encounterAuthFieldsById.set(id, {
              authRequirementStatus: authRequirementStatus || null,
              authStatus: authStatus || null,
            });
          }
          for (const appt of rows) {
            if ((appt.id ?? "").trim() !== id) {
              continue;
            }
            if (authRequirementStatus) {
              appt.authRequirementStatus = authRequirementStatus;
            }
            if (authStatus) {
              appt.authStatus = authStatus;
            }
          }
          this.mergeAuthFieldsIntoAdmissionList(detail);
        }
      }),
      map(() => undefined)
    );
  }

  updateAppointmentCache(updatedAppointment: Appointment) {
    const list = this.admissionList;
    if (!list?.length || !updatedAppointment?.id) {
      return;
    }
    const matchingIndex = list.findIndex(
      (appointment) => appointment.id === updatedAppointment.id
    );
    if (matchingIndex > -1) {
      list[matchingIndex] = updatedAppointment;
    }
  }

  /**
   * Worklist Engage icon: decrement badge after mark-active (messages) or activity dismiss
   * (new request, missed call, voicemail).
   */
  applyWorksheetEngageBadgeDismiss(params: {
    referralId?: string;
    encounterId?: string;
    channel: WorksheetEngageBadgeDismissChannel;
    decrement?: number;
  }): void {
    const rid = (params.referralId || "").trim().toLowerCase();
    const encId = (params.encounterId || "").trim();
    if (!rid && !encId) {
      return;
    }
    const list = this.admissionList;
    if (!list?.length) {
      return;
    }
    let changed = false;
    for (let i = 0; i < list.length; i++) {
      const row = list[i];
      const rowRid = getAppointmentReferralId(row).trim().toLowerCase();
      const rowEnc = (row.id ?? "").toString().trim();
      const match =
        (rid && rowRid && rowRid === rid) ||
        (encId && rowEnc && rowEnc === encId);
      if (!match) {
        continue;
      }
      list[i] = patchWorksheetEngageBadgeDismiss(
        row,
        params.channel,
        params.decrement
      );
      changed = true;
    }
    if (changed) {
      this.admissionList = [...list];
    }
  }

  insertAppointment(
    appointment: Partial<Appointment>
  ): Observable<Appointment> {
    const url = this.apiBaseUrl + `api/v1/encounters`;
    return this._dataService.post(url, appointment);
  }

  updateAppointment(appointment: Partial<Appointment>): Observable<Appointment> {
    const url = this.apiBaseUrl + `api/v1/encounters/${appointment.id}`;
    return this._dataService.patch(url, appointment).pipe(
      switchMap((updated) =>
        this.syncClaimStatusDraftAfterEncounterPatch(
          appointment.id ?? (updated as Appointment | undefined)?.id,
          appointment,
          updated as Appointment | undefined
        ).pipe(map(() => updated as Appointment))
      ),
      tap((updated) => {
        if (typeof appointment.isConfirmed === "boolean") {
          this._confirmationActivityNoteService.log(
            updated,
            appointment.isConfirmed
          );
        }
      })
    );
  }

  /**
   * Persist Requires Auth Yes/No for the current encounter DOS only
   * (`encounter_status.authRequirementStatus` + derived `authStatus`).
   */
  patchEncounterRequiresAuth(
    requiresAuth: boolean | undefined,
    encounterId?: string
  ): Observable<Appointment | null> {
    const id = (
      encounterId ??
      this._applicationCacheService.applicationCache.appointment?.id ??
      ""
    ).trim();
    if (!id) {
      return of(null);
    }
    const cache = this._applicationCacheService.applicationCache;
    const priorAuthStatus = getOverallPriorAuthStatus(
      cache.priorAuths,
      cache.priorAuth
    );
    const authRequirementStatus = requiresAuthToAuthRequirementStatus(requiresAuth);
    const authStatus = deriveAuthWorkflowStatusLabel(
      requiresAuth,
      priorAuthStatus
    );
    const patch: Partial<Appointment> = { id };
    if (authRequirementStatus != null) {
      patch.authRequirementStatus = authRequirementStatus;
    } else {
      patch.authRequirementStatus = null;
    }
    if (authStatus != null) {
      patch.authStatus = authStatus;
    } else {
      patch.authStatus = null;
    }
    return this.updateAppointment(patch).pipe(
      tap((updated) => {
        this._applicationCacheService.updateAppointmentCache(updated);
        this.mergeAuthFieldsIntoAdmissionList(updated);
      })
    );
  }

  /**
   * PATCH PHI `encounter_status.authStatus` / `authRequirementStatus` from application cache
   * (encounter requires-auth + prior auth), without changing clinical encounterStatus.
   */
  syncEncounterAuthStatusFromCache(
    encounterId?: string
  ): Observable<Appointment | null> {
    const id = (
      encounterId ??
      this._applicationCacheService.applicationCache.appointment?.id ??
      ""
    ).trim();
    if (!id) {
      return of(null);
    }
    const authStatus =
      this._applicationCacheService.getExpectedAuthStatusLabel();
    const authRequirementStatus =
      this._applicationCacheService.getExpectedAuthRequirementStatusLabel();
    const appointment =
      this._applicationCacheService.applicationCache.appointment;
    const patch: Partial<Appointment> = { id };
    let hasChange = false;
    if (authStatus != null) {
      const current = (appointment?.authStatus ?? "").trim();
      if (authStatus !== current) {
        patch.authStatus = authStatus;
        hasChange = true;
      }
    }
    if (authRequirementStatus != null) {
      const currentReq = (appointment?.authRequirementStatus ?? "").trim();
      if (authRequirementStatus !== currentReq) {
        patch.authRequirementStatus = authRequirementStatus;
        hasChange = true;
      }
    }
    if (!hasChange) {
      return of(appointment ?? null);
    }
    return this.updateAppointment(patch).pipe(
      tap((updated) =>
        this._applicationCacheService.updateAppointmentCache(updated)
      )
    );
  }

  /**
   * Mark an encounter submitted to billing. Sets scheduleStatus to Submitted
   * when it is not already Submitted (Open, Sign Req, etc.).
   * Also posts journey_log eventType=achieved for GAE Procedure + Procedure visit type.
   */
  submitEncounterToBilling(
    appointment: EncounterScheduleStatusSource &
      Partial<
        Pick<
          Appointment,
          | "id"
          | "patientId"
          | "service"
          | "visitType"
          | "referralId"
          | "encountersPatient"
        >
      > & {
        encounterSchedule?: { scheduleStatus?: string; visitType?: string } | null;
      }
  ): Observable<Appointment> {
    const id = (appointment.id ?? "").trim();
    if (!id) {
      return throwError(() => new Error("Encounter id is required"));
    }
    const previousScheduleStatus = resolveEncounterScheduleRowStatus(appointment);
    const patch = buildSubmitEncounterToBillingPatch(id, appointment);
    return this.updateAppointment(patch).pipe(
      switchMap((updated) =>
        this.getAppointmentById(id).pipe(
          catchError(() =>
            of({
              ...(appointment as Appointment),
              ...(updated as Appointment),
            } as Appointment)
          ),
          tap((fresh) => {
            this._applicationCacheService.updateAppointmentCache({
              ...(fresh as Appointment),
              encounterStatus: EncounterStatus.Complete,
              clinicalStatus: EncounterStatus.Complete,
              claimStatus: "draft",
              scheduleStatus:
                resolveScheduleStatusAfterSubmit(fresh, patch.scheduleStatus) ||
                resolveEncounterScheduleRowStatus(fresh),
            });
          }),
          switchMap((fresh) => {
            const nextScheduleStatus = resolveScheduleStatusAfterSubmit(
              fresh,
              patch.scheduleStatus
            );
            return this.maybePostAchievedJourneyLogOnScheduleSubmitted(
              id,
              previousScheduleStatus,
              nextScheduleStatus,
              fresh
            );
          }),
          map(() => updated as Appointment)
        )
      )
    );
  }

  /**
   * When `encounter_schedule.scheduleStatus` transitions to Submitted, POST achieved
   * journey_log for GAE Procedure + Procedure visit type.
   */
  maybePostAchievedJourneyLogOnScheduleSubmitted(
    encounterId: string,
    previousScheduleStatus: string | undefined,
    nextScheduleStatus: string | undefined,
    appointmentHint?: Partial<Appointment> | null
  ): Observable<unknown> {
    const eid = String(encounterId ?? "").trim();
    const next = String(nextScheduleStatus ?? "").trim();
    const previous = String(previousScheduleStatus ?? "").trim();
    if (
      !eid ||
      !isEncounterScheduleSubmitted(next) ||
      isEncounterScheduleSubmitted(previous)
    ) {
      return of(null);
    }

    const hint = appointmentHint as Appointment | null | undefined;
    const hintHasEligibilityFields =
      !!String(hint?.patientId ?? hint?.encountersPatient?.id ?? "").trim() &&
      !!String(
        hint?.encounterSchedule?.visitType ?? hint?.visitType ?? ""
      ).trim() &&
      !!String(hint?.service ?? "").trim();

    const appointment$ = hintHasEligibilityFields
      ? of(hint as Appointment)
      : this.getAppointmentById(eid).pipe(catchError(() => of(hint ?? null)));

    return appointment$.pipe(
      switchMap((appt) => {
        if (!appt) {
          return of(null);
        }
        const patientId = String(
          appt.patientId ?? appt.encountersPatient?.id ?? ""
        ).trim();
        const visitTypeRaw = String(
          appt.encounterSchedule?.visitType ?? appt.visitType ?? ""
        );
        const serviceRaw = String(appt.service ?? "");
        return this.postGaeProcedureJourneyLogIfEligible({
          eventType: "achieved",
          encounterId: eid,
          patientId,
          referralId: getAppointmentReferralId(appt),
          visitTypeRaw,
          serviceRaw,
        });
      })
    );
  }

  /**
   * After PATCH encounters, set claimStatus to draft (biller Sent) when schedule status is Submitted
   * (from request payload and/or API response).
   */
  syncClaimStatusDraftAfterEncounterPatch(
    encounterId: string | undefined,
    request: EncounterScheduleStatusSource | undefined,
    response: EncounterScheduleStatusSource | undefined
  ): Observable<void> {
    const scheduleStatus =
      resolveEncounterScheduleStatus(response) ||
      resolveEncounterScheduleStatus(request);
    return this.ensureClaimStatusDraftWhenSubmitted(
      encounterId,
      scheduleStatus,
      [response, request]
    );
  }

  updateEncounterClaimStatus(
    encounterId: string,
    claimStatus: string
  ): Observable<void> {
    const url =
      this.apiBaseUrl + `api/v1/encounters/${encounterId}/claim-status`;
    return this._dataService.patch(url, { claimStatus });
  }

  /** When schedule status is Submitted, set claimStatus to draft (biller Sent). */
  ensureClaimStatusDraftWhenSubmitted(
    encounterId: string | undefined,
    scheduleStatus: string | undefined,
    sources: unknown[] = []
  ): Observable<void> {
    return this._encounterClaimStatusSyncService.ensureDraftWhenSubmittedIfClaimStatusEmpty(
      encounterId,
      scheduleStatus,
      sources
    );
  }

  updateEncounterRetroAuth(
    encounterId: string,
    isRetroAuth: boolean
  ): Observable<Appointment> {
    return this.updateAppointment({
      id: encounterId,
      retroAuthStatus: isRetroAuth,
    }).pipe(
      tap((updated) => {
        const merged = {
          ...updated,
          isRetroAuth: updated.retroAuthStatus === true || updated.isRetroAuth === true,
          retroAuthStatus:
            updated.retroAuthStatus === true || updated.isRetroAuth === true,
        };
        this._applicationCacheService.updateAppointmentCache(merged);
      }),
      map((updated) => ({
        ...updated,
        isRetroAuth: updated.retroAuthStatus === true || updated.isRetroAuth === true,
        retroAuthStatus:
          updated.retroAuthStatus === true || updated.isRetroAuth === true,
      }))
    );
  }

  deleteAppointment(appointmentId: string) {
    const url = this.apiBaseUrl + `api/v1/encounters/${appointmentId}`;
    return this._dataService.delete(url);
  }

  /**
   * Engage PATCH needs referral id; list row may omit it or appointment may not be on `admissionList`.
   */
  private resolveReferralIdForCancel(
    appointmentId: string,
    listAppointment: Appointment | undefined,
    resolvedPatientId: string
  ): string {
    const fromList = getAppointmentReferralId(listAppointment);
    if (fromList) {
      return fromList;
    }
    const cachedAppt = this._applicationCacheService.applicationCache?.appointment;
    if (cachedAppt?.id === appointmentId) {
      const fromCacheAppt = getAppointmentReferralId(cachedAppt);
      if (fromCacheAppt) {
        return fromCacheAppt;
      }
    }
    const cachePatient = this._applicationCacheService.applicationCache?.patient;
    const cachePid = (cachePatient?.id || "").trim();
    const cacheRid = (cachePatient?.referralId || "").trim();
    if (resolvedPatientId && cachePid && resolvedPatientId === cachePid && cacheRid) {
      return cacheRid;
    }
    return "";
  }

  /**
   * Map cancel-modal `reason` (CancelAppointmentModalComponent `reasonMap`) to Engage PATCH event type.
   * Known API values: `cancel` → canceled, `no_show` → no_show, `waitlist` → waitlist,
   * `reschedule` → clinic_rescheduled; display strings → canceled.
   */
  private mapCancellationReasonToClinicEventType(
    cancellationReason: string
  ): EngageClinicEventType | null {
    const r = (cancellationReason || "").trim().toLowerCase();
    if (!r) {
      return null;
    }
    const direct: Record<string, EngageClinicEventType> = {
      no_show: EngageClinicEventType.NoShow,
      reschedule: EngageClinicEventType.ClinicRescheduled,
      waitlist: EngageClinicEventType.Waitlist,
      cancel: EngageClinicEventType.Canceled,
    };
    if (direct[r] != null) {
      return direct[r];
    }
    const canceledLike = [
      "provider schedule conflict",
      "insurance issue",
      "npo status",
      "other",
    ];
    if (canceledLike.includes(r)) {
      return EngageClinicEventType.Canceled;
    }
    return null;
  }

  cancelAppointment(appointmentId: string, cancellationReason: string, cancellationComment: string, tentativeDate?: string, patientId?: string, loggedInUserName?: string) {
    const url = this.apiBaseUrl + `api/v1/encounters/${appointmentId}/cancel`;
    const engageUrl = this.engageApiBaseUrl + `api/v1/activity`;
    const payload: any = {
      cancellationReason,
      cancellationComment
    };

    // Get appointment to find resourceId and patient id (list-clinic often nests patient under encountersPatient)
    const appointment = this.admissionList?.find(a => a.id === appointmentId);
    const resolvedPatientId = (
      (patientId || '').trim() ||
      appointment?.patientId ||
      appointment?.encountersPatient?.id ||
      ''
    ).trim();
    const resolvedReferralId = this.resolveReferralIdForCancel(
      appointmentId,
      appointment,
      resolvedPatientId
    );
    const resourceId = appointment?.encounterSchedule?.scheduleResourceId || appointment?.scheduleResourceId || appointment?.resourceId;

    // Get context to check resource roleId
    return this._appContextService.context.pipe(
      take(1),
      switchMap((context) => {
        // Check if resource has roleId === 1 (provider)
        let isProvider = false;
        if (resourceId && context?.resources) {
          const resource = context.resources.find(r => r.id === resourceId || r.name === resourceId);
          isProvider = resource?.roleId === 1;
        }

        const engagePayload: any = {
          referralId: resolvedReferralId,
          patientId: resolvedPatientId,
          type: cancellationReason,
          name: loggedInUserName || '',
          stage: '',
          isProvider: isProvider
        };

        // Add tentative date if provided and reason is reschedule request
        if (tentativeDate && cancellationReason === 'reschedule') {
          // Format date to MM/DD/YYYY (e.g., 10/12/2025)
          const formattedDate = new Date(tentativeDate).toLocaleDateString('en-US');
          payload.tentativeDate = tentativeDate;
          engagePayload.comment = cancellationComment;
        } else {
          engagePayload.comment = cancellationComment;
        }

        const triggeredBy = context?.currentlyLoggedInUser?.id;
        const providerIdForEvent = appointment?.providerId;
        const dosForEvent =
          appointment?.encounterSchedule?.scheduleDate ||
          (appointment?.encounterDateTime
            ? String(appointment.encounterDateTime).split('T')[0]
            : undefined);

        // First cancel the appointment, then try to log engage activity (silently ignore if referral not found)
        return this._dataService.post(url, payload).pipe(
          tap(() => {
            // Log engage activity as a side effect - don't block on failure
            this._dataService.post(engageUrl, engagePayload).subscribe({
              next: () => console.log('Engage activity logged:', cancellationReason),
              error: (err) => {
                // Silently ignore "referral not found" errors
                if (err?.message?.toLowerCase().includes('referral not found') ||
                    err?.error?.message?.toLowerCase().includes('referral not found')) {
                  console.log('Engage activity skipped: No referral found for patient');
                } else {
                  console.error('Error logging Engage activity:', err);
                }
              }
            });

            const clinicEventType =
              this.mapCancellationReasonToClinicEventType(cancellationReason);

            if (resolvedReferralId && clinicEventType) {
              this._engageEventsService
                .registerClinicEvent(resolvedReferralId, {
                  eventType: clinicEventType,
                  encounterId: appointmentId,
                  ...(resolvedPatientId && { patientId: resolvedPatientId }),
                  ...(triggeredBy && { triggeredBy }),
                  ...(providerIdForEvent != null && { providerId: String(providerIdForEvent) }),
                  ...(dosForEvent != null && { dos: dosForEvent }),
                  ...(providerIdForEvent != null && {
                    chartsScheduledPhysician: String(providerIdForEvent),
                  }),
                  metadata: {
                    cancellationReason,
                    comment: cancellationComment || '',
                    ...(tentativeDate && cancellationReason === 'reschedule'
                      ? { tentativeDate }
                      : {}),
                  },
                })
                .subscribe({
                  error: (err) =>
                    console.warn('[AdmissionsService] Engage registerClinicEvent failed:', err),
                });
            }
          })
        );
      })
    );
  }

    logEngageActivity(patientId: string, type: string, name: string, comment: string, isProvider?: boolean, ScheduleVisitType?: string, referralId?: string) {
    const engageUrl = this.engageApiBaseUrl + 'api/v1/activity';
    const payload: any = {
      referralId: referralId || '',
      patientId: patientId || '',
      type: type,
      name: name,
      stage: '',
      comment: comment
    };

    // Add isProvider to payload if provided
    if (isProvider !== undefined) {
      payload.isProvider = isProvider;
    }

    // Add scheduleVisitType to payload if provided
    if (ScheduleVisitType) {
      payload.ScheduleVisitType = ScheduleVisitType;
    }

    return this._dataService.post(engageUrl, payload).subscribe({
      next: () => console.log('Engage activity logged:', type),
      error: (err) => {
        // Silently ignore "referral not found" errors
        if (err?.message?.toLowerCase().includes('referral not found') ||
            err?.error?.message?.toLowerCase().includes('referral not found')) {
          console.log('Engage activity skipped: No referral found for patient');
        } else {
          console.error('Error logging Engage activity:', err);
        }
      }
    });
  }

  /**
   * Log phone call to Engage API
   * POST /api/v1/phone-calls-log
   * Returns the phone-calls-log id from the response for use in PATCH.
   */
  logPhoneCall(patientId: string, referralId?: string): Observable<string | null> {
    const url = this.engageApiBaseUrl + 'api/v1/phone-calls-log';
    const payload = {
      callActivated: false,
      callType: 'outbound',
      referralId: referralId || '',
      patientId: patientId || ''
    };
    return this._dataService.post(url, payload).pipe(
      map((res: any) => {
        const body = res?.body ?? res;
        if (body == null) return null;
        if (typeof body === 'string') return body || null;
        const first = Array.isArray(body) ? body[0] : body;
        const id = first?.id ?? first?._id ?? first?.phoneCallLogId ?? first?.phone_call_log_id
          ?? body.id ?? body._id ?? body.phoneCallLogId ?? body.data?.id ?? body.data
          ?? body.record?.id ?? body.record ?? body.result?.id ?? body.result
          ?? body.phoneCallLog?.id ?? body.value?.id;
        return id != null && id !== '' ? String(id) : null;
      })
    );
  }

  /**
   * Update phone call log (e.g. when user saves after Yes/No answer).
   * PATCH /api/v1/phone-calls-log/{id}  (id = phone-calls-log id from POST response)
   */
  patchPhoneCallLog(phoneCallLogId: string, patientAnswered: boolean): Observable<any> {
    const url = this.engageApiBaseUrl + 'api/v1/phone-calls-log/' + phoneCallLogId;
    const payload = {
      callActivated: true,
      patientAnswered
    };
    return this._dataService.patch(url, payload);
  }

  logEngageStageActivity(patientId: string, stage: string, name: string, comment: string): Observable<any> {
    const engageUrl = this.engageApiBaseUrl + 'api/v1/activity';
    const payload = {
      referralId: '',
      patientId,
      type: 'stage',
      name,
      stage,
      comment
    };
    return this._dataService.post(engageUrl, payload);
  }

  /**
   * After a new encounter exists, sync Engage referral id onto the journey log row for that encounter.
   * PATCH `{apiBaseUrl}api/v1/journey-log?encounterId={id}&eventType=scheduled` with body `{ referralId }`.
   */
  patchJourneyLogReferralId(
    encounterId: string,
    referralId: string
  ): Observable<unknown> {
    const eid = String(encounterId ?? "").trim();
    const rid = String(referralId ?? "").trim();
    if (!eid || !rid) {
      return of(null);
    }
    const q = new URLSearchParams({
      encounterId: eid,
      eventType: "scheduled",
    });
    const url = `${this.apiBaseUrl}api/v1/journey-log?${q.toString()}`;
    return this._dataService.patch(url, { referralId: rid }).pipe(
      catchError((err) => {
        console.warn(
          "[AdmissionsService] PATCH journey-log referralId failed",
          err
        );
        return of(null);
      })
    );
  }

  /**
   * PHI: POST journey log row (e.g. established, booked, achieved).
   * POST `{apiBaseUrl}api/v1/journey-log`
   * When referralId is missing, falls back to patientId (API requires a UUID).
   */
  postJourneyLog(payload: {
    eventType: string;
    patientId: string;
    referralId?: string;
    encounterId: string;
    procedureId: number;
    createdById: string;
    createdByName: string;
    occuredAt?: string;
  }): Observable<unknown> {
    const url = `${this.apiBaseUrl}api/v1/journey-log`;
    const patientId = String(payload.patientId ?? "").trim();
    // Clinic-only patients may have no Engage referral; API requires a UUID referralId.
    const referralId =
      String(payload.referralId ?? "").trim() || patientId;
    const journeyLogPayload = {
      ...payload,
      patientId,
      referralId,
      occuredAt: payload.occuredAt || new Date().toISOString(),
    };
    return this._dataService.post(url, journeyLogPayload).pipe(
      catchError((err) => {
        console.warn(
          `[AdmissionsService] POST journey-log (${payload.eventType}) failed`,
          err
        );
        return of(null);
      })
    );
  }

  /** @deprecated Use {@link postJourneyLog}. */
  postJourneyLogEstablished(payload: {
    eventType: string;
    patientId: string;
    referralId: string;
    encounterId: string;
    procedureId: number;
    createdById: string;
    createdByName: string;
    occuredAt?: string;
  }): Observable<unknown> {
    return this.postJourneyLog(payload);
  }

  /**
   * When visit type is Procedure and service is GAE Procedure, POST booked / achieved journey_log.
   */
  postGaeProcedureJourneyLogIfEligible(options: {
    eventType: "booked" | "achieved";
    encounterId: string;
    patientId: string;
    referralId?: string;
    visitTypeRaw: string;
    serviceRaw: string;
    serviceDisplayName?: string;
    createdById?: string;
    createdByName?: string;
    occuredAt?: string;
  }): Observable<unknown> {
    const serviceDisplayName =
      String(options.serviceDisplayName ?? "").trim() ||
      this._appContextService.resolveEncounterServiceDisplayName(
        options.serviceRaw
      ) ||
      options.serviceRaw;
    if (
      !isGaeProcedureJourneyLogEligible(
        options.visitTypeRaw,
        serviceDisplayName
      )
    ) {
      return of(null);
    }

    const encounterId = String(options.encounterId ?? "").trim();
    const patientId = String(options.patientId ?? "").trim();
    if (!encounterId || !patientId) {
      return of(null);
    }
    const referralId = String(options.referralId ?? "").trim();

    return this._appContextService.context.pipe(
      take(1),
      switchMap((context) => {
        const user = context?.currentlyLoggedInUser;
        const createdById = (
          options.createdById ||
          user?.id ||
          ""
        ).trim();
        const createdByName = (
          options.createdByName ||
          (user
            ? `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim()
            : "")
        ).trim();
        if (!createdById) {
          return of(null);
        }

        return this.postJourneyLog({
          eventType: options.eventType,
          patientId,
          referralId,
          encounterId,
          procedureId: 0,
          createdById,
          createdByName,
          occuredAt: options.occuredAt || Utils.utcNowIsoString(),
        });
      })
    );
  }

  updateReferralPatientInfo(patientId: string, fullName: string): Observable<any> {
    const engageUrl = this.engageApiBaseUrl + `api/v1/referrals/patient/${patientId}/info`;
    const payload = {
      fullName: fullName
    };
    return this._dataService.patch(engageUrl, payload);
  }

  createFollowUpAppointment(
    originalAppointmentId: string,
    followUpDate: string,
    patientId: string,
    providerId: string,
    providerName: string,
    visitType?: string,
    carryForward?: boolean,
    locationId?: string,
    locationName?: string,
    scheduleResourceId?: string,
    selectedSlot?: string,
    procedureMin?: number,
    checkOut?: string,
    reasonForVisit?: string,
    serviceId?: string,
    referralSource?: string,
    workFlowId?: string
  ) {
    const url = this.apiBaseUrl + `api/v1/encounters/${originalAppointmentId}/follow-up`;
    const trimmedFollowUpDate = (followUpDate || "").trim();
    const trimmedSlot = (selectedSlot || "").trim();
    const normalizedFollowUpDate =
      trimmedFollowUpDate && trimmedSlot
        ? Utils.buildScheduleDateTime(trimmedFollowUpDate, trimmedSlot)
        : trimmedFollowUpDate
          ? Utils.formatISODateWithoutTime(
              Utils.parseLocalDate(trimmedFollowUpDate.split("T")[0])
            )
          : followUpDate;
    const payload: any = {
      followUpDate: normalizedFollowUpDate,
      patientId,
      providerId,
      providerName,
      originalAppointmentId
    };

    // Only include visitType if it exists
    if (visitType) {
      payload.visitType = visitType;
    }

    payload.carryForward = !!carryForward;
    if (locationId) {
      payload.locationId = locationId;
    }
    if (locationName) {
      payload.location = locationName;
      payload.lineLocation = locationName;
    }
    if (scheduleResourceId) {
      payload.scheduleResourceId = scheduleResourceId;
    }
    if (selectedSlot) {
      payload.selectedSlot = selectedSlot;
      payload.checkIn = selectedSlot;
      payload.procedureTime = selectedSlot;
    }
    if (procedureMin != null && procedureMin > 0) {
      payload.procedureMin = procedureMin;
    }
    if (checkOut) {
      payload.checkOut = checkOut;
    }
    payload.reasonForVisit = (reasonForVisit ?? "").trim();
    if (serviceId?.trim()) {
      payload.service = serviceId.trim();
    }
    if (referralSource?.trim()) {
      payload.referralSource = referralSource.trim();
    }
    const wf = (workFlowId ?? "").trim();
    if (wf) {
      payload.workFlowId = wf;
    }

    console.log('Creating follow-up with payload:', payload);

    return this._dataService.post(url, payload);
  }

  /** List all follow-ups for an encounter (preferred). */
  getEncounterFollowUps(encounterId: string): Observable<EncounterFollowUpsResponse> {
    const url = this.apiBaseUrl + `api/v1/encounters/${encounterId}/follow-ups`;
    return this._dataService.get(url);
  }

  /** @deprecated Prefer {@link getEncounterFollowUps}. */
  getFollowUpInfo(encounterId: string): Observable<FollowUpInfoResponse> {
    const url = this.apiBaseUrl + `api/v1/encounters/${encounterId}/follow-up-info`;
    return this._dataService.get(url);
  }

  getAppointmentById(appointmentId: string) {
    if (!appointmentId || !isValidUuid(appointmentId)) {
      return throwError(() => ({
        message: "Missing or invalid ID. Please refresh the page and try again.",
      }));
    }
    const url = this.apiBaseUrl + `api/v1/encounters/details/${appointmentId}`;
    return this._dataService.get(url).pipe(
      map((response) => normalizeEncounterPatientPhi(response as Appointment))
    );
  }

  // Get upcoming appointments for a specific patient (from tomorrow onwards)
  getPatientUpcomingAppointments(patientId: string): Observable<Appointment[]> {
    const tomorrow = DateTime.now().plus({ days: 1 }).startOf('day');
    const futureDate = DateTime.now().plus({ years: 1 }).endOf('day');

    const payload = {
      providers: [],
      resources: [],
      filterStatus: [],
      filterLocation: [],
      visitType: [],
      filterDate: {
        startDate: tomorrow.toFormat("yyyy-MM-dd'T'HH:mm:ss"),
        endDate: futureDate.toFormat("yyyy-MM-dd'T'HH:mm:ss"),
      },
      pageStart: 0,
      pageLength: 100, // Get upcoming appointments
      sortField: "encounterDateTime",
      sortDirection: "ASC" as "ASC", // Ascending for upcoming
      searchQuery: ""
    };

    const url = this.apiBaseUrl + "api/v1/encounters/list-clinic";
    return this._dataService.post(url, payload).pipe(
      map((response: PaginatedAppointmentResponse) => response.data),
      tap({
        error: (err) => {
          console.error('Error fetching patient upcoming appointments:', err);
        }
      })
    );
  }

  /**
   * Warm `POST …/encounters/list-clinic` cache for one patient so Engage merges clinic schedule rows
   * as soon as the HTTP completes (typically started when the user selects a patient with a PHI id).
   */
  prefetchEngageListClinicForPatient(patientId: string): void {
    const pid = (patientId || "").trim();
    if (!pid) {
      return;
    }
    if (this.engageListClinicByPatientId.has(pid) || this.engageListClinicInflight.has(pid)) {
      return;
    }
    this.getListClinicEncountersForPatient(patientId).pipe(take(1)).subscribe({ error: () => {} });
  }

  /** Drop cached list-clinic rows for one patient (after schedule changes, deletes, or explicit encounter reload). */
  invalidateEngageListClinicCacheForPatient(patientId: string | null | undefined): void {
    const pid = (patientId || "").trim();
    if (!pid) {
      return;
    }
    this.engageListClinicByPatientId.delete(pid);
    this.engageListClinicInflight.delete(pid);
  }

  /**
   * Same `POST .../encounters/list-clinic` payload shape as Schedule list-view, filtered client-side
   * to one patient. Rows include `encounterSchedule.procedureRoom` (location column) that
   * `GET .../encounters/patient/{id}` often omits — merge into patient-encounters for Engage.
   *
   * Results are cached per patient id for the session. Pass `{ bypassCache: true }` after mutations.
   */
  getListClinicEncountersForPatient(
    patientId: string,
    opts?: { bypassCache?: boolean }
  ): Observable<Appointment[]> {
    const pid = (patientId || "").trim();
    if (!pid) {
      return of([]);
    }
    if (opts?.bypassCache) {
      this.invalidateEngageListClinicCacheForPatient(pid);
    }
    if (!opts?.bypassCache && this.engageListClinicByPatientId.has(pid)) {
      const rows = this.engageListClinicByPatientId.get(pid) ?? [];
      return of(rows.map((a) => ({ ...a })));
    }
    if (!opts?.bypassCache) {
      const inflight = this.engageListClinicInflight.get(pid);
      if (inflight) {
        return inflight;
      }
    }
    const req$ = this.fetchListClinicEncountersForPatientPost(pid).pipe(
      tap((rows) => {
        this.engageListClinicByPatientId.set(pid, rows);
      }),
      finalize(() => {
        this.engageListClinicInflight.delete(pid);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    this.engageListClinicInflight.set(pid, req$);
    return req$;
  }

  private fetchListClinicEncountersForPatientPost(pid: string): Observable<Appointment[]> {
    const start = DateTime.now().minus({ years: 2 }).startOf("day");
    const end = DateTime.now().plus({ years: 1 }).endOf("day");
    const payload = {
      providers: [] as string[],
      resources: [] as string[],
      filterStatus: [] as string[],
      filterLocation: [] as string[],
      visitType: [] as string[],
      filterDate: {
        startDate: start.toFormat("yyyy-MM-dd'T'HH:mm:ss"),
        endDate: end.toFormat("yyyy-MM-dd'T'HH:mm:ss"),
      },
      pageStart: 0,
      // Smaller than Schedule list-view: Engage only needs recent rows merged by patient (DESC sort).
      pageLength: 2_500,
      sortField: "encounterDateTime",
      sortDirection: "DESC" as const,
      searchQuery: "",
    };
    const url = this.apiBaseUrl + "api/v1/encounters/list-clinic";
    return this._dataService.post(url, payload).pipe(
      map((response: PaginatedAppointmentResponse) => {
        const rows = response?.data ?? [];
        return rows.filter((a) => {
          const rowPid = String(
            a.patientId ??
              (a as Appointment & { patient_id?: string }).patient_id ??
              ""
          ).trim();
          return rowPid === pid;
        });
      }),
      catchError(() => of([]))
    );
  }

  private getTodayYYYYMMDD(): string {
    return DateTime.now().toFormat("yyyy-MM-dd");
  }

  /**
   * GET /api/v1/encounters/upcoming-encounters
   * @param date - Date in YYYY-MM-DD (week containing this date is returned)
   * @param selectedDate - Date in YYYY-MM-DD for the selected day (defaults to today when not provided)
   * @param providers - Optional provider UUIDs
   * @param locations - Optional location names
   * @param referralSources - Optional referral source ids (comma-separated in query when provided)
   */
  getUpcomingEncounters(
    date: string,
    selectedDate?: string,
    providers?: string[],
    locations?: string[],
    referralSources?: string[]
  ): Observable<UpcomingEncountersResponse> {
    const selected = selectedDate ?? this.getTodayYYYYMMDD();
    let url = this.apiBaseUrl + `api/v1/encounters/upcoming-encounters?date=${encodeURIComponent(date)}&selectedDate=${encodeURIComponent(selected)}`;
    if (providers?.length) {
      url += `&providers=${encodeURIComponent(providers.join(','))}`;
    }
    if (locations?.length) {
      url += `&locations=${encodeURIComponent(locations.join(','))}`;
    }
    if (referralSources?.length) {
      url += `&referralSources=${encodeURIComponent(referralSources.join(","))}`;
    }
    return this._dataService.get(url).pipe(
      tap({
        error: (err) => {
},
      })
    );
  }

  /**
   * GET /api/v1/encounters/status-counts
   * @param providers - Optional provider UUIDs
   * @param locations - Optional location names
   * @param startDate - Optional ISO date string (filter encounters from this date)
   * @param endDate - Optional ISO date string (filter encounters to this date)
   */
  getStatusCounts(
    providers?: string[],
    locations?: string[],
    startDate?: string,
    endDate?: string
  ): Observable<EncounterStatusCountsResponse> {
    const params = new URLSearchParams();
    if (providers?.length) {
      params.set("providers", providers.join(","));
    }
    if (locations?.length) {
      params.set("locations", locations.join(","));
    }
    if (startDate) {
      params.set("startDate", startDate);
    }
    if (endDate) {
      params.set("endDate", endDate);
    }
    const query = params.toString();
    const url =
      this.apiBaseUrl +
      `api/v1/encounters/status-counts${query ? `?${query}` : ""}`;
    return this._dataService.get(url).pipe(
      tap({
        error: (err) => {
},
      })
    );
  }

  get apiBaseUrl(): string {
    return environment.apiBaseUrl;
  }

  get engageApiBaseUrl(): string {
    return environment.engageApiBaseUrl;
  }

  /**
   * Log activity to Engage API
   * Silently ignores "referral not found" errors
   */


  // Methods to handle resource filter clearing and restoration
  clearResourceFilterForListView(): void {
    // Clear all resource filter selections when switching to list view
    this.resourcesFilter.forEach(resource => {
      resource.isSelected = false;
    });
  }

  saveResourceFilterToLocalStorage(): void {
    try {
      const selectedResources = this.resourcesFilter
        .filter(resource => resource.isSelected && resource.value !== 'All')
        .map(resource => resource.value);

      localStorage.setItem('calendar_resource_filter', JSON.stringify(selectedResources));
    } catch (error) {
      console.error('Error saving resource filter to localStorage:', error);
    }
  }

  restoreResourceFilterFromLocalStorage(): void {
    try {
      const savedResources = localStorage.getItem('calendar_resource_filter');
      if (savedResources) {
        const resourceIds = JSON.parse(savedResources);

        // Clear all selections first
        this.resourcesFilter.forEach(resource => {
          resource.isSelected = false;
        });

        // Restore saved selections
        resourceIds.forEach((resourceId: string) => {
          const resource = this.resourcesFilter.find(r => r.value === resourceId);
          if (resource) {
            resource.isSelected = true;
          }
        });
      }
    } catch (error) {
      console.error('Error restoring resource filter from localStorage:', error);
    }
  }

  clearResourceFilterFromLocalStorage(): void {
    try {
      localStorage.removeItem('calendar_resource_filter');
    } catch (error) {
      console.error('Error clearing resource filter from localStorage:', error);
    }
  }
}
